import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { PageHeader } from "@/components/EntityManager";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BRANCHES, branchPillClass } from "@/lib/branches";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, today } from "@/lib/format";
import { HEAD_OFFICE } from "@/lib/company";
import { printBranchSummary, printBranchActivity, printDailyCollection, printMonthlyCollection, printOverdueSheet, printAgingReport, printStaffBirthdayCard, printStaffAnniversaryCard, printCashBook, printDriverCertificate } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Package,
  IndianRupee,
  ShieldAlert,
  FileText,
  FileDown,
  Trophy,
  Building2,
  AlertTriangle,
  CalendarDays,
  Cake,
  Gift,
  Award,
  Printer,
  MessageCircle,
  Sparkles,
  Landmark,
  Truck,
  Wrench,
  ClipboardCheck,
  Settings2,
  ArrowUp,
  ArrowDown,
  FilePlus2,
  BookOpen,
  Users,
  Handshake,
  Search,
  BellRing,
  RotateCcw,
  FileSearch,
  BarChart3,
  IdCard,
  Pencil,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  ReferenceLine,
} from "recharts";

const PIE_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#64748B"];
const BRANCH_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#8B5CF6"];
const OFFICES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
const METRICS = [
  { key: "bookings", label: "Bookings", money: false },
  { key: "freight", label: "Freight ₹", money: true },
  { key: "collections", label: "Collections ₹", money: true },
  { key: "outstanding", label: "Outstanding ₹", money: true },
];

function OfficeSwitcher({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[170px]" data-testid="dashboard-office-filter">
        <Building2 className="mr-1 h-4 w-4 text-muted-foreground" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="All">All Branches</SelectItem>
        {OFFICES.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="border-border transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {label}
            </div>
            <div className="mt-2 font-display text-2xl font-bold tabular-nums text-foreground" data-testid={testid}>
              {value}
            </div>
          </div>
          <div className={`grid h-10 w-10 place-items-center rounded-md ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const wishLink = (phone, text) => {
  const d = String(phone || "").replace(/\D/g, "");
  return `https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(text)}`;
};

const TARGET_BRANCHES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];
const BRANCH_BAR_COLORS = { Chennai: "#1d4ed8", Tada: "#10b981", Hyderabad: "#f59e0b", Jaipur: "#8b5cf6" };

const ALL_QUICK_LINKS = [
  { key: "consignment-entry", label: "Consignment Entry", icon: Package, cls: "bg-blue-50 text-blue-600" },
  { key: "consignment-enquiry", label: "Consignment Enquiry", icon: Search, cls: "bg-indigo-50 text-indigo-600" },
  { key: "coa-register", label: "COA Register", icon: ShieldAlert, cls: "bg-red-50 text-red-600", path: "/consignment-entry?coa=1" },
  { key: "bill-create", label: "Bill Create", icon: FilePlus2, cls: "bg-fuchsia-50 text-fuchsia-600", chennaiOnly: true },
  { key: "bills", label: "Bills", icon: FileText, cls: "bg-violet-50 text-violet-600", chennaiOnly: true },
  { key: "receipts", label: "Money Receipts", icon: Wallet, cls: "bg-emerald-50 text-emerald-600" },
  { key: "bank-statement", label: "Bank Statement", icon: Landmark, cls: "bg-amber-50 text-amber-600", adminOnly: true },
  { key: "vehicles-tracking", label: "Vehicles Tracking", icon: Truck, cls: "bg-sky-50 text-sky-600" },
  { key: "attendance", label: "Attendance", icon: ClipboardCheck, cls: "bg-rose-50 text-rose-600", adminOnly: true },
  { key: "customer-ledger", label: "Customer Ledger", icon: BookOpen, cls: "bg-teal-50 text-teal-600" },
  { key: "vendor-ledger", label: "Vendor Ledger", icon: Handshake, cls: "bg-orange-50 text-orange-600" },
  { key: "expenses", label: "NTC Expenses", icon: IndianRupee, cls: "bg-lime-50 text-lime-600" },
  { key: "outstanding", label: "Outstanding", icon: AlertTriangle, cls: "bg-red-50 text-red-600" },
  { key: "staff-master", label: "Staff Master", icon: Users, cls: "bg-cyan-50 text-cyan-600", adminOnly: true },
  { key: "eway-bills", label: "E-Way Bills", icon: FileDown, cls: "bg-slate-100 text-slate-600" },
];
const DEFAULT_QUICK_KEYS = ["consignment-entry", "coa-register", "bills", "receipts", "bank-statement", "vehicles-tracking", "attendance"];

function QuickLinks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const allowed = (a) => (!a.adminOnly || isAdmin) && (!a.chennaiOnly || isAdmin || (user?.office || "Chennai") === "Chennai");
  const catalog = ALL_QUICK_LINKS.filter(allowed);
  const [keys, setKeys] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem("ntc_quick_links"));
      if (Array.isArray(s) && s.length) return s.filter((k) => ALL_QUICK_LINKS.some((a) => a.key === k));
    } catch {}
    return DEFAULT_QUICK_KEYS;
  });
  const [custOpen, setCustOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const links = keys.map((k) => catalog.find((a) => a.key === k)).filter(Boolean);
  const openCustomize = () => { setDraft([...keys]); setCustOpen(true); };
  const saveCustomize = () => {
    localStorage.setItem("ntc_quick_links", JSON.stringify(draft));
    setKeys(draft);
    setCustOpen(false);
    toast.success("Quick links updated");
  };
  const toggle = (k) => setDraft((d) => (d.includes(k) ? d.filter((x) => x !== k) : [...d, k]));
  const move = (k, dir) =>
    setDraft((d) => {
      const i = d.indexOf(k);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= d.length) return d;
      const n = [...d];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  const ordered = [...draft.map((k) => catalog.find((a) => a.key === k)).filter(Boolean), ...catalog.filter((a) => !draft.includes(a.key))];
  return (
    <div className="mb-4 flex items-start gap-2" data-testid="dashboard-quick-links">
      <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {links.map((q) => (
          <button
            key={q.key}
            type="button"
            onClick={() => navigate(q.path || "/" + q.key)}
            data-testid={`quick-link-${q.key}`}
            className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm"
          >
            <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${q.cls}`}>
              <q.icon className="h-4 w-4" />
            </span>
            <span className="text-xs font-semibold leading-tight">{q.label}</span>
          </button>
        ))}
      </div>
      <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-slate-400 hover:text-slate-600" title="Customize quick links" onClick={openCustomize} data-testid="quick-links-customize-btn">
        <Settings2 className="h-4 w-4" />
      </Button>
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[440px]" data-testid="quick-links-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Customize Quick Links</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Tick the pages you want and use the arrows to set their order. Saved on this device.</p>
          <div className="grid gap-1">
            {ordered.map((a) => {
              const on = draft.includes(a.key);
              return (
                <div key={a.key} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${on ? "border-primary/30 bg-primary/5" : "border-border"}`} data-testid={`quick-link-option-${a.key}`}>
                  <input type="checkbox" checked={on} onChange={() => toggle(a.key)} className="h-4 w-4 accent-blue-600" data-testid={`quick-link-check-${a.key}`} />
                  <span className={`grid h-6 w-6 place-items-center rounded ${a.cls}`}><a.icon className="h-3.5 w-3.5" /></span>
                  <span className="flex-1 text-sm font-medium">{a.label}</span>
                  {on && (
                    <>
                      <Button variant="ghost" size="icon" className="h-6 w-6" disabled={draft.indexOf(a.key) === 0} onClick={() => move(a.key, -1)} data-testid={`quick-link-up-${a.key}`}>
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6" disabled={draft.indexOf(a.key) === draft.length - 1} onClick={() => move(a.key, 1)} data-testid={`quick-link-down-${a.key}`}>
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setCustOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={saveCustomize} disabled={draft.length === 0} data-testid="save-quick-links-btn">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BranchSnapshot() {
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [budget, setBudget] = useState(0);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [branchLimits, setBranchLimits] = useState({});
  const [carryFwd, setCarryFwd] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [limitsDraft, setLimitsDraft] = useState({});
  useEffect(() => {
    api.get(`/reports/branch-activity?month=${month}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [month]);
  useEffect(() => {
    api.get("/settings/hire-budget").then((r) => {
      setBudget(Number(r.data?.limit) || 0);
      setBudgetDraft(String(Number(r.data?.limit) || ""));
      setBranchLimits(r.data?.branches || {});
      setCarryFwd(!!r.data?.carry_forward);
    }).catch(() => {});
  }, []);
  const effLimit = (branch) => Number(branchLimits?.[branch]) || budget || 0;
  const carryOf = (r) => Number(r?.budget_carry) || 0;
  const capOf = (r) => effLimit(r.branch) + carryOf(r);
  const toggleCarry = async (v) => {
    setCarryFwd(v);
    try {
      await api.post("/settings/hire-budget", { limit: budget, carry_forward: v });
      const r = await api.get(`/reports/branch-activity?month=${month}`);
      setData(r.data);
      toast.success(v ? "Unused hire budget now carries into the next month" : "Budget carry forward turned off");
    } catch (e) {
      setCarryFwd(!v);
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };
  const saveBudget = async () => {
    const v = parseFloat(String(budgetDraft).replace(/[^0-9.]/g, "")) || 0;
    try {
      await api.post("/settings/hire-budget", { limit: v });
      setBudget(v);
      toast.success(v ? `Common hire budget set to ₹${v.toLocaleString("en-IN")} per branch per month` : "Common hire budget turned off");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save limit");
    }
  };
  const saveBranchLimits = async () => {
    const branches = {};
    Object.entries(limitsDraft).forEach(([k, v]) => {
      const n = parseFloat(String(v).replace(/[^0-9.]/g, "")) || 0;
      if (n > 0) branches[k] = n;
    });
    try {
      await api.post("/settings/hire-budget", { limit: budget, branches });
      setBranchLimits(branches);
      setLimitsOpen(false);
      toast.success("Branch-wise hire budgets saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save limits");
    }
  };
  const overBudget = data ? (data.rows || []).filter((r) => effLimit(r.branch) > 0 && Number(r.challan_hire) > capOf(r)) : [];
  const snapshotWa = () => {
    const t = data.totals || {};
    const msg = [
      `*NTC — Branch Activity Snapshot (${monthLabel})*`,
      "",
      ...(data.rows || []).map((r) => `• ${r.branch}: ${r.bookings} bookings · Billing ₹${Number(r.billing_total || 0).toLocaleString("en-IN")} · Receipts ₹${Number(r.receipts_total || 0).toLocaleString("en-IN")} · ${r.challan_trips || 0} challans · Hire ₹${Number(r.challan_hire || 0).toLocaleString("en-IN")}${effLimit(r.branch) > 0 && Number(r.challan_hire) > capOf(r) ? ` ⚠ OVER BUDGET (₹${capOf(r).toLocaleString("en-IN")}${carryOf(r) > 0 ? " incl c/f" : ""})` : ""}`),
      "",
      `*TOTAL:* ${t.bookings || 0} bookings · Billing ₹${Number(t.billing_total || 0).toLocaleString("en-IN")} · Receipts ₹${Number(t.receipts_total || 0).toLocaleString("en-IN")} · Hire ₹${Number(t.challan_hire || 0).toLocaleString("en-IN")}`,
      ...(budget > 0 || Object.keys(branchLimits).length ? [`Hire budgets: common ₹${budget.toLocaleString("en-IN")}${Object.keys(branchLimits).length ? ` · branch-wise: ${Object.entries(branchLimits).map(([b, l]) => `${b} ₹${Number(l).toLocaleString("en-IN")}`).join(", ")}` : ""}${overBudget.length ? ` — ${overBudget.length} branch(es) OVER` : " — all within budget"}`] : []),
      "— Month-end review",
    ].join("\n");
    const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const monthLabel = (() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, (m || 1) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  })();
  return (
    <div className="mt-6">
      <Card className="border-border" data-testid="branch-activity-snapshot">
        <CardContent className="p-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div>
              <h3 className="font-display text-lg font-bold">Branch Activity Snapshot</h3>
              <p className="text-xs text-muted-foreground">Bookings, billing and receipts per branch — {monthLabel}</p>
            </div>
            <input
              type="month"
              className="ml-auto h-9 rounded-md border border-border bg-card px-3 text-sm"
              value={month}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
              data-testid="branch-snapshot-month"
            />
            <Button size="sm" variant="outline" disabled={!data} onClick={() => printBranchActivity(data, monthLabel)} data-testid="branch-snapshot-print-btn">
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
            <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" disabled={!data} onClick={snapshotWa} data-testid="branch-snapshot-wa-btn">
              <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!data}
              data-testid="branch-snapshot-excel-btn"
              onClick={() => {
                const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                const lines = [
                  [`Branch Activity Snapshot — ${monthLabel}`],
                  ["Branch", "Bookings", "Bills", "Billing (Rs.)", "MRs", "Receipts (Rs.)", "Mkt Trips", "Mkt Hire (Rs.)", "Challans", "Vehicle Hire (Rs.)"],
                  ...data.rows.map((r) => [r.branch, r.bookings, r.billing_count, r.billing_total, r.receipts_count, r.receipts_total, r.market_trips || 0, r.market_hire || 0, r.challan_trips || 0, r.challan_hire || 0]),
                  ["TOTAL", data.totals.bookings, data.totals.billing_count, data.totals.billing_total, data.totals.receipts_count, data.totals.receipts_total, data.totals.market_trips || 0, data.totals.market_hire || 0, data.totals.challan_trips || 0, data.totals.challan_hire || 0],
                ];
                const blob = new Blob(["\ufeff" + lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `branch-snapshot-${month}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
              }}
            >
              <FileDown className="mr-1.5 h-3.5 w-3.5" /> Excel
            </Button>
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {(data?.rows || []).filter((r) => r.bookings || r.challan_trips || r.billing_count || r.receipts_count).map((r) => (
              <button
                type="button"
                key={r.branch}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-primary hover:text-primary-foreground"
                title={`Tap to open ${r.branch}'s challan register — ${r.challan_trips || 0} challan(s) this month`}
                onClick={() => navigate(`/hire-challans?branch=${encodeURIComponent(r.branch)}`)}
                data-testid={`dash-branch-pill-${r.branch.toLowerCase()}`}
              >
                {r.branch} <b className="text-primary">{r.challan_trips || 0}</b>
              </button>
            ))}
          </div>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Hire budget / branch / month:</span>
            <span className="inline-flex items-center gap-1">
              <span className="text-xs">₹</span>
              <input
                type="number"
                className="h-8 w-28 rounded-md border border-border bg-card px-2 text-xs"
                placeholder="No limit"
                value={budgetDraft}
                onChange={(e) => setBudgetDraft(e.target.value)}
                data-testid="hire-budget-input"
              />
            </span>
            <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={saveBudget} data-testid="hire-budget-save-btn">Set Limit</Button>
            <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => { setLimitsDraft(Object.fromEntries((data?.rows || []).map((r) => [r.branch, branchLimits[r.branch] || ""]))); setLimitsOpen(true); }} data-testid="branch-limits-btn">
              Branch Limits{Object.keys(branchLimits).length ? ` (${Object.keys(branchLimits).length})` : ""}
            </Button>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium" data-testid="carry-forward-toggle">
              <input type="checkbox" checked={carryFwd} onChange={(e) => toggleCarry(e.target.checked)} className="h-3.5 w-3.5 accent-emerald-600" data-testid="carry-forward-checkbox" />
              Carry forward unused
            </label>
            {overBudget.length ? (
              <span className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-700" data-testid="hire-budget-alert">
                <AlertTriangle className="h-4 w-4" /> {overBudget.map((r) => `${r.branch} ₹${Number(r.challan_hire).toLocaleString("en-IN")} / ₹${capOf(r).toLocaleString("en-IN")}${carryOf(r) > 0 ? " (incl c/f)" : ""}`).join(", ")} over hire budget
              </span>
            ) : (budget > 0 || Object.keys(branchLimits).length > 0) && data ? (
              <span className="rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700">All branches within their hire budgets</span>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="branch-snapshot-table">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                  <th className="border border-border px-3 py-2 text-left">Branch</th>
                  <th className="border border-border px-3 py-2 text-right">Bookings</th>
                  <th className="border border-border px-3 py-2 text-right">Bills</th>
                  <th className="border border-border px-3 py-2 text-right">Billing (₹)</th>
                  <th className="border border-border px-3 py-2 text-right">MRs</th>
                  <th className="border border-border px-3 py-2 text-right">Receipts (₹)</th>
                  <th className="border border-border px-3 py-2 text-right">Mkt Trips</th>
                  <th className="border border-border px-3 py-2 text-right">Mkt Hire (₹)</th>
                  <th className="border border-border px-3 py-2 text-right">Challans</th>
                  <th className="border border-border px-3 py-2 text-right">Vehicle Hire (₹)</th>
                  {(budget > 0 || Object.keys(branchLimits).length > 0) && <th className="border border-border px-3 py-2 text-left">Budget Used</th>}
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r) => (
                  <tr
                    key={r.branch}
                    className="cursor-pointer transition-colors hover:bg-slate-50"
                    title={`Tap to open ${r.branch}'s challan register`}
                    onClick={() => navigate(`/hire-challans?branch=${encodeURIComponent(r.branch)}`)}
                    data-testid="branch-snapshot-row"
                  >
                    <td className="border border-border px-3 py-2 font-medium">{r.branch}{(r.budget_overrides || 0) > 0 && (
                      <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700" data-testid="override-count-pill" title="Over-budget save approvals this month (see Correction Log Book)">
                        {r.budget_overrides} override{r.budget_overrides > 1 ? "s" : ""}
                      </span>
                    )}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{r.bookings}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{r.billing_count}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{inr(r.billing_total)}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{r.receipts_count}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums text-emerald-700">{inr(r.receipts_total)}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums" data-testid="snapshot-mkt-trips">{r.market_trips || 0}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums text-amber-700">{inr(r.market_hire)}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums" data-testid="snapshot-challan-trips">{r.challan_trips || 0}</td>
                    <td className={`border border-border px-3 py-2 text-right tabular-nums font-semibold ${effLimit(r.branch) > 0 && Number(r.challan_hire) > capOf(r) ? "bg-red-50 text-red-700" : "text-orange-700"}`} data-testid="snapshot-challan-hire">{inr(r.challan_hire)}{effLimit(r.branch) > 0 && Number(r.challan_hire) > capOf(r) ? " ⚠" : ""}</td>
                    {(budget > 0 || Object.keys(branchLimits).length > 0) && (
                      <td className="border border-border px-3 py-2 min-w-[150px]" data-testid="snapshot-budget-bar">
                        {effLimit(r.branch) > 0 ? (() => {
                          const cap = capOf(r);
                          const pct = (Number(r.challan_hire) || 0) / cap * 100;
                          const w = Math.min(pct, 100);
                          const color = pct > 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
                          return (
                            <div>
                              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                <div className={`h-full rounded-full ${color}`} style={{ width: `${w}%` }} />
                              </div>
                              <div className={`mt-0.5 text-[10px] tabular-nums ${pct > 100 ? "font-semibold text-red-600" : "text-muted-foreground"}`}>
                                {Math.round(pct)}% of ₹{cap.toLocaleString("en-IN")}{carryOf(r) > 0 ? ` (incl ₹${carryOf(r).toLocaleString("en-IN")} c/f)` : ""}
                              </div>
                            </div>
                          );
                        })() : <span className="text-[10px] text-muted-foreground">no limit</span>}
                      </td>
                    )}
                  </tr>
                ))}
                {data && (
                  <tr className="bg-slate-100 font-bold" data-testid="branch-snapshot-total">
                    <td className="border border-border px-3 py-2">TOTAL</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{data.totals.bookings}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{data.totals.billing_count}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{inr(data.totals.billing_total)}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{data.totals.receipts_count}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums text-primary">{inr(data.totals.receipts_total)}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{data.totals.market_trips || 0}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums text-amber-700">{inr(data.totals.market_hire)}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums">{data.totals.challan_trips || 0}</td>
                    <td className="border border-border px-3 py-2 text-right tabular-nums text-orange-700">{inr(data.totals.challan_hire)}</td>
                    {(budget > 0 || Object.keys(branchLimits).length > 0) && <td className="border border-border px-3 py-2" />}
                  </tr>
                )}
              </tbody>
            </table>
            {!data && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
          </div>
        </CardContent>
      </Card>
      <Dialog open={limitsOpen} onOpenChange={setLimitsOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="branch-limits-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Branch-wise Hire Budgets</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Leave blank to use the common limit{budget ? ` (₹${budget.toLocaleString("en-IN")})` : ""} for that branch.</p>
          <div className="grid gap-2">
            {Object.keys(limitsDraft).map((b) => (
              <label key={b} className="flex items-center justify-between gap-3 text-sm">
                <span className="font-semibold">{b}</span>
                <span className="inline-flex items-center gap-1">
                  ₹
                  <input
                    type="number"
                    className="h-8 w-32 rounded-md border border-border bg-card px-2 text-xs"
                    placeholder="Common"
                    value={limitsDraft[b]}
                    onChange={(e) => setLimitsDraft((d) => ({ ...d, [b]: e.target.value }))}
                    data-testid={`branch-limit-input-${b.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                </span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setLimitsOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={saveBranchLimits} data-testid="branch-limits-save-btn">Save Limits</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BranchRankCard() {
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const prevMonth = (m) => {
    let [y, mm] = m.split("-").map(Number);
    mm -= 1;
    if (mm === 0) { y -= 1; mm = 12; }
    return `${y}-${String(mm).padStart(2, "0")}`;
  };
  useEffect(() => {
    api.get(`/reports/branch-activity?month=${month}`).then((r) => setData(r.data)).catch(() => {});
    api.get(`/reports/branch-activity?month=${prevMonth(month)}`).then((r) => setPrev(r.data)).catch(() => setPrev(null));
  }, [month]);
  const ranked = [...(data?.rows || [])].sort((a, b) => (b.freight_total || 0) - (a.freight_total || 0));
  const prevRanked = [...(prev?.rows || [])].sort((a, b) => (b.freight_total || 0) - (a.freight_total || 0));
  const prevPos = Object.fromEntries(prevRanked.map((r, i) => [r.branch, i]));
  const trend = (branch, i) => {
    const p = prevPos[branch];
    if (p === undefined || p === i) return <span className="w-8 shrink-0 text-center text-xs text-slate-400" data-testid="rank-trend">—</span>;
    return p > i
      ? <span className="w-8 shrink-0 text-center text-xs font-bold text-emerald-600" title={`Up from #${p + 1} last month`} data-testid="rank-trend">▲{p - i}</span>
      : <span className="w-8 shrink-0 text-center text-xs font-bold text-red-500" title={`Down from #${p + 1} last month`} data-testid="rank-trend">▼{i - p}</span>;
  };
  const max = Math.max(1, ...ranked.map((r) => r.freight_total || 0));
  const monthLabel = new Date(`${month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const medal = ["bg-amber-400 text-amber-950", "bg-slate-300 text-slate-800", "bg-orange-300 text-orange-900"];
  return (
    <div className="mt-6">
      <Card className="border-border" data-testid="branch-rank-card">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 font-display text-lg">
                <Trophy className="h-5 w-5 text-amber-500" /> Branch Performance Rank
              </CardTitle>
              <div className="mt-1 text-xs text-muted-foreground">Ranked by consignment freight — {monthLabel}</div>
            </div>
            <input
              type="month"
              className="h-9 rounded-md border border-border bg-card px-3 text-sm"
              value={month}
              max={new Date().toISOString().slice(0, 7)}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
              data-testid="branch-rank-month"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
          {ranked.map((r, i) => (
            <div key={r.branch} className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${i === 0 && (r.freight_total || 0) > 0 ? "border-amber-300 bg-amber-50/70" : "border-border"}`} data-testid="branch-rank-row">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${medal[i] || "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
              {trend(r.branch, i)}
              <span className="w-28 shrink-0 text-sm font-semibold">{r.branch}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${i === 0 ? "bg-amber-400" : "bg-primary/60"}`} style={{ width: `${Math.round(((r.freight_total || 0) / max) * 100)}%` }} />
              </div>
              <span className="w-28 shrink-0 text-right text-sm font-bold tabular-nums">{inr(r.freight_total || 0)}</span>
              <span className="w-20 shrink-0 text-right text-xs text-muted-foreground">{r.bookings} LRs</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PayrollSummaryCard() {
  const [salaries, setSalaries] = useState([]);
  const [month, setMonth] = useState("");
  useEffect(() => {
    api.get("/salaries").then((r) => {
      const list = r.data || [];
      setSalaries(list);
      const months = Array.from(new Set(list.map((s) => s.month).filter(Boolean))).sort().reverse();
      setMonth(months[0] || new Date().toISOString().slice(0, 7));
    }).catch(() => {});
  }, []);
  const months = Array.from(new Set(salaries.map((s) => s.month).filter(Boolean))).sort().reverse();
  const rows = salaries.filter((s) => s.month === month);
  const net = (s) => Number(s.net_pay ?? (Number(s.basic) || 0) + (Number(s.allowances) || 0) - (Number(s.deductions) || 0) - (Number(s.pf) || 0) - (Number(s.esi) || 0));
  const paid = rows.filter((s) => s.status === "Paid");
  const pending = rows.filter((s) => s.status !== "Paid");
  const total = rows.reduce((a, s) => a + net(s), 0);
  const byBranch = {};
  rows.forEach((s) => { const b = s.office || "—"; byBranch[b] = (byBranch[b] || 0) + net(s); });
  if (!salaries.length) return null;
  return (
    <div className="mt-6">
      <Card className="border-border" data-testid="payroll-summary-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <div>
            <CardTitle className="font-display text-lg">Payroll — Month Summary</CardTitle>
            <div className="text-xs text-muted-foreground">Total, paid, pending and per-branch wage cost</div>
          </div>
          <select className="h-8 rounded-md border bg-background px-2 text-sm" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month-select">
            {(months.length ? months : [month]).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="payroll-empty">No salaries recorded for {month}.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border bg-slate-50 p-3" data-testid="payroll-total">
                  <div className="text-xs text-muted-foreground">Total Payroll ({rows.length})</div>
                  <div className="mt-1 text-xl font-bold tabular-nums">{inr(total)}</div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="payroll-paid">
                  <div className="text-xs text-emerald-700">Paid ({paid.length})</div>
                  <div className="mt-1 text-xl font-bold tabular-nums text-emerald-700">{inr(paid.reduce((a, s) => a + net(s), 0))}</div>
                </div>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="payroll-pending">
                  <div className="text-xs text-amber-700">Pending ({pending.length})</div>
                  <div className="mt-1 text-xl font-bold tabular-nums text-amber-700">{inr(pending.reduce((a, s) => a + net(s), 0))}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(byBranch).sort((a, b) => b[1] - a[1]).map(([b, amt]) => (
                  <span key={b} className={`rounded-md border px-2.5 py-1 text-xs ${branchPillClass(b)}`} data-testid="payroll-branch-chip">
                    <b>{b}</b> · {inr(amt)}
                  </span>
                ))}
              </div>
            </>
          )}
          {(() => {
            const net2 = net;
            const base = /^\d{4}-\d{2}$/.test(month) ? month : new Date().toISOString().slice(0, 7);
            const [by, bm] = [Number(base.slice(0, 4)), Number(base.slice(5, 7))];
            const monthsList = Array.from({ length: 6 }, (_, i) => {
              const d = new Date(by, bm - 1 - (5 - i), 1);
              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            });
            const branches = Array.from(new Set(salaries.filter((s) => monthsList.includes(s.month)).map((s) => s.office || "—")));
            const data = monthsList.map((m) => {
              const row = { label: new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1).toLocaleDateString("en-GB", { month: "short" }) };
              salaries.filter((s) => s.month === m).forEach((s) => { const b = s.office || "—"; row[b] = (row[b] || 0) + net2(s); });
              return row;
            });
            const hasData = data.some((r) => branches.some((b) => (r[b] || 0) > 0));
            const yearly = {};
            salaries.forEach((s) => { const yr = String(s.month || "").slice(0, 4); if (/^\d{4}$/.test(yr)) yearly[yr] = (yearly[yr] || 0) + net2(s); });
            const palette = ["#1d4ed8", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#0ea5e9", "#84cc16"];
            return (
              <div className="mt-4" data-testid="payroll-trend-section">
                <div className="mb-1 text-xs font-semibold text-muted-foreground">Wage cost — last 6 months (up to {base})</div>
                {hasData ? (
                  <div className="h-[190px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                        <Tooltip formatter={(v) => inr(v)} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {branches.map((b, i) => (
                          <Bar key={b} dataKey={b} stackId="pay" fill={BRANCH_BAR_COLORS[b] || palette[i % palette.length]} radius={[3, 3, 0, 0]} maxBarSize={28} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="py-4 text-center text-xs text-muted-foreground" data-testid="payroll-trend-empty">No salaries in the last 6 months.</div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(yearly).sort((a, b) => b[0].localeCompare(a[0])).map(([yr, amt]) => (
                    <span key={yr} className="rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-800" data-testid="payroll-year-chip">
                      Year {yr} Total · {inr(amt)}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}

function MarketHireTrendCard() {
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    api.get("/reports/market-hire-trend").then((r) => setTrend(r.data)).catch(() => setTrend(null));
  }, []);
  const palette = ["#1d4ed8", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#0ea5e9", "#84cc16"];
  const active = (trend?.branches || []).filter((b) => (trend?.rows || []).some((r) => (r[b] || 0) > 0));
  return (
    <div className="mt-6">
      <Card className="border-border" data-testid="market-hire-trend-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-lg">Market Vehicle Hire Trend</CardTitle>
          <div className="text-xs text-muted-foreground">Last 6 months — approved hire spend per branch</div>
        </CardHeader>
        <CardContent className="h-[230px]">
          {!trend || active.length === 0 ? (
            <div className="grid h-full place-items-center text-sm text-muted-foreground" data-testid="hire-trend-empty">No approved market vehicle hires in the last 6 months.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend.rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {active.map((b, i) => (
                  <Bar key={b} dataKey={b} stackId="hire" fill={BRANCH_BAR_COLORS[b] || palette[i % palette.length]} radius={[3, 3, 0, 0]} maxBarSize={28} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MarketMarginTrendCard() {
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    api.get("/reports/market-margin-trend").then((r) => setTrend(r.data)).catch(() => setTrend(null));
  }, []);
  const target = Math.max(0, Number(localStorage.getItem("ntc_mv_margin_pct") || 10));
  const rows = trend?.rows || [];
  const hasData = rows.some((r) => r.trips > 0);
  const latest = [...rows].reverse().find((r) => r.margin_pct != null);
  const prev = latest ? [...rows].reverse().find((r) => r.margin_pct != null && r.month < latest.month) : null;
  const delta = latest && prev ? +(latest.margin_pct - prev.margin_pct).toFixed(1) : null;
  return (
    <div className="mt-6">
      <Card className="border-border" data-testid="market-margin-trend-card">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="font-display text-lg">Market Vehicle Margin % Trend</CardTitle>
              <div className="text-xs text-muted-foreground">Last 6 months — (Freight − Hire) ÷ Freight on approved trips · target {target}% (set on Market Vehicle page)</div>
            </div>
            {latest && (
              <div className="text-right">
                <div className={`text-2xl font-bold tabular-nums ${latest.margin_pct >= target ? "text-emerald-700" : "text-amber-600"}`} data-testid="margin-trend-latest">
                  {latest.margin_pct}%
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {latest.label}
                  {delta != null && (
                    <span className={`ml-1 font-semibold ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="margin-trend-delta">
                      {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}% vs {prev.label}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="h-[230px]">
          {!hasData ? (
            <div className="grid h-full place-items-center text-sm text-muted-foreground" data-testid="margin-trend-empty">No approved market vehicle trips in the last 6 months.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, (dataMax) => Math.max(dataMax || 0, target + 5)]} />
                <Tooltip
                  formatter={(v, name) => (name === "Margin %" ? [`${v}%`, name] : [inr(v), name])}
                  labelFormatter={(l, p) => {
                    const r = p?.[0]?.payload;
                    return r ? `${l} — ${r.trips} trips | Freight ${inr(r.freight)} | Hire ${inr(r.hire)} | Margin ${inr(r.margin)}` : l;
                  }}
                />
                <ReferenceLine
                  y={target}
                  stroke="#f59e0b"
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  label={{ value: `Target ${target}%`, position: "insideTopRight", fill: "#b45309", fontSize: 11, fontWeight: 600 }}
                />
                <Line
                  type="monotone"
                  dataKey="margin_pct"
                  name="Margin %"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  connectNulls
                  dot={(p) => {
                    const { cx, cy, payload, index } = p;
                    if (cx == null || cy == null || payload.margin_pct == null) return <g key={index} />;
                    const below = payload.margin_pct < target;
                    return <circle key={index} cx={cx} cy={cy} r={below ? 5 : 4} fill={below ? "#ef4444" : "#10b981"} stroke="#fff" strokeWidth={1.5} data-testid={below ? "margin-dot-below" : "margin-dot-ok"} />;
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Gstr1Nudge() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const d = new Date();
    if (d.getDate() <= 5) return;
    const pm = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const key = `${pm.getFullYear()}-${String(pm.getMonth() + 1).padStart(2, "0")}`;
    api
      .get("/invoices")
      .then((r) => {
        const bills = (r.data || []).filter((b) => (b.issue_date || "").startsWith(key));
        const missing = bills.filter((b) => !b.gstr1_ack_path).length;
        if (bills.length && missing) setInfo({ label: pm.toLocaleDateString("en-GB", { month: "long", year: "numeric" }), missing, total: bills.length });
      })
      .catch(() => {});
  }, []);
  if (!info) return null;
  return (
    <button
      type="button"
      onClick={() => navigate("/bills")}
      data-testid="gstr1-filing-nudge"
      className="mb-4 flex w-full items-center justify-between rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-left transition-colors hover:bg-orange-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-orange-100 text-orange-600">
          <FileText className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-semibold text-orange-800">GSTR-1 filing pending — {info.label}</div>
          <div className="text-xs text-orange-700">
            Filed copy not uploaded for {info.missing} of {info.total} bill{info.total > 1 ? "s" : ""}. Open Bills → GSTR-1 to upload the acknowledgement.
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-orange-700">Open GSTR-1 →</span>
    </button>
  );
}

function VehicleDocAlerts() {
  const [vehicles, setVehicles] = useState([]);
  const [fleetMgr, setFleetMgr] = useState(null);
  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    api.get("/company-profile").then((r) => setFleetMgr({ name: r.data?.fleet_manager_name || "", phone: r.data?.fleet_manager_whatsapp || "" })).catch(() => {});
  }, []);
  const soonCutoff = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const todayStr = new Date().toISOString().slice(0, 10);
  const alerts = [];
  vehicles.forEach((v) => {
    [["fc_expiry", "FC"], ["insurance_expiry", "Insurance"], ["puc_expiry", "PUC"], ["permit_expiry", "Permit"]].forEach(([k, label]) => {
      const d = v[k];
      if (d && d <= soonCutoff) alerts.push({ vehicle: v.registration_no || v.vehicle_no || "—", label, date: d, expired: d < todayStr });
    });
  });
  if (!alerts.length) return null;
  alerts.sort((a, b) => a.date.localeCompare(b.date));
  const shareWhatsApp = () => {
    const lines = alerts.map((a, i) => `${i + 1}. ${a.vehicle} — ${a.label} ${a.expired ? "EXPIRED" : "due"} ${fmtDate(a.date)}`);
    const msg = `🚚 NTC Vehicle Document Alert (${fmtDate(new Date().toISOString().slice(0, 10))})\n\n${alerts.length} document(s) expired or due within 30 days:\n\n${lines.join("\n")}\n\nPlease arrange renewals at the earliest.\n\n— Nisha Transport Corporation`;
    const digits = String(fleetMgr?.phone || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const hasFleetMgr = Boolean(String(fleetMgr?.phone || "").replace(/\D/g, ""));
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-4" data-testid="vehicle-doc-alerts">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-amber-800">
        <AlertTriangle className="h-4 w-4" /> Vehicle Documents — expired or due within 30 days ({alerts.length})
        <Button variant="outline" size="sm" className="ml-auto h-7 bg-white text-xs text-emerald-600" onClick={shareWhatsApp} data-testid="doc-expiry-whatsapp-btn">
          <MessageCircle className="mr-1 h-3 w-3" /> {hasFleetMgr ? `WhatsApp ${fleetMgr.name || "Fleet Manager"}` : "WhatsApp"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {alerts.map((a, i) => (
          <span key={i} className={`rounded-md border px-2.5 py-1 text-xs font-medium ${a.expired ? "border-red-200 bg-white text-red-700" : "border-amber-200 bg-white text-amber-800"}`} data-testid="vehicle-doc-chip">
            <b>{a.vehicle}</b> · {a.label} {a.expired ? "expired" : "due"} {fmtDate(a.date)}
          </span>
        ))}
      </div>
    </div>
  );
}

function ConsignmentPolicyAlerts() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  useEffect(() => {
    api.get("/shipments").then((r) => setShipments(r.data || [])).catch(() => {});
  }, []);
  const soonCutoff = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const todayStr = new Date().toISOString().slice(0, 10);
  const alerts = shipments
    .filter((s) => s.insurance_expiry && s.insurance_expiry <= soonCutoff && !/deliver|cancel/i.test(s.status || ""))
    .map((s) => ({
      lr: String(s.lr_no || s.consignment_no || "—").replace(/^CN[-\s]?/i, ""),
      route: `${s.origin || "—"} → ${s.destination || "—"}`,
      policy: s.insurance_policy_no || "",
      date: s.insurance_expiry,
      expired: s.insurance_expiry < todayStr,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (!alerts.length) return null;
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50/70 p-4" data-testid="consignment-policy-alerts">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-rose-800">
        <AlertTriangle className="h-4 w-4" /> Consignment Insurance — policy expired or due within 30 days ({alerts.length})
        <Button variant="outline" size="sm" className="ml-auto h-7 bg-white text-xs" onClick={() => navigate("/consignment-entry")} data-testid="policy-alert-open-btn">
          Open Consignments →
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {alerts.map((a, i) => (
          <span key={i} className={`rounded-md border px-2.5 py-1 text-xs font-medium ${a.expired ? "border-red-300 bg-white text-red-700" : "border-amber-200 bg-white text-amber-800"}`} data-testid="consignment-policy-chip">
            <b>LR {a.lr}</b> · {a.route}{a.policy ? ` · Policy ${a.policy}` : ""} · {a.expired ? "EXPIRED" : "due"} {fmtDate(a.date)}
          </span>
        ))}
      </div>
    </div>
  );
}

function useBranchManagers() {
  const [m, setM] = useState({});
  const [c, setC] = useState({});
  useEffect(() => {
    api.get("/branch-managers").then((r) => setM(r.data || {})).catch(() => {});
    api.get("/branch-contacts").then((r) => setC(r.data || {})).catch(() => {});
  }, []);
  return (branch) => {
    const d = String(m[branch] || c[branch] || "").replace(/\D/g, "");
    return d ? (d.length === 10 ? "91" + d : d) : "";
  };
}

function ChargeEditsWeekCard() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/change-requests").then(({ data }) => {
      const cutoff = Date.now() - 7 * 86400000;
      setRows((data || []).filter((h) => h.field === "extra_charge_edit" && Date.parse(h.created_at || "") >= cutoff).sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))));
    }).catch(() => {});
  }, []);
  if (!rows.length) return null;
  const amt = (s) => Number(String(s || "").split("—")[0].replace(/[^\d.]/g, "")) || 0;
  const total = rows.reduce((s, h) => s + Math.max(amt(h.new_value) - amt(h.old_value), 0), 0);
  const share = async () => {
    let mob = "";
    try {
      const r = await api.get("/settings/otp-mobile");
      mob = String(r.data?.mobile || "").replace(/\D/g, "");
    } catch {}
    const target = mob ? (mob.length === 10 ? "91" + mob : mob) : "";
    const msg = [
      "*NTC — EXTRA AMOUNTS ADDED THIS WEEK* ₹",
      `${rows.length} edit${rows.length === 1 ? "" : "s"} · Total added: ${inr(total)}`,
      "",
      ...rows.slice(0, 15).map((h) => `• ${fmtDate(String(h.created_at || "").slice(0, 10))} — Challan ${h.lr_no || "—"} (${h.customer_name || "—"}) — ${String(h.field_label || "").replace(" (Vehicle Payments)", "")}: ${h.old_value || "₹0"} → ${String(h.new_value || "").split("—")[0].trim()} — by ${h.decided_by || "—"}`),
      "",
      "All edits are in the Correction Log Book.",
    ].join("\n");
    window.open(target ? `https://wa.me/${target}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="w-full rounded-lg border border-orange-300 bg-orange-50 px-4 py-3" data-testid="charge-edits-week-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-orange-100 text-orange-600">
            <IndianRupee className="h-5 w-5" />
          </span>
          <div>
            <div className="text-sm font-bold text-orange-900">Extra amounts added this week — {rows.length} edit{rows.length === 1 ? "" : "s"}, {inr(total)} total</div>
            <div className="text-xs text-orange-800">Admin-added charges from the Vehicle Payments dialog (last 7 days), all in the Correction Log Book.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={share} data-testid="charge-edits-wa-btn">
            <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
          </Button>
          <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100" onClick={() => navigate("/hire-challans")} data-testid="charge-edits-open-btn">
            Open Challans
          </Button>
        </div>
      </div>
      <div className="mt-2 space-y-0.5">
        {rows.slice(0, 5).map((h) => (
          <div key={h.id} className="flex flex-wrap items-baseline gap-x-2 text-xs text-orange-900" data-testid="charge-edits-week-row">
            <span className="text-orange-700">{fmtDate(String(h.created_at || "").slice(0, 10))}</span>
            <span className="font-bold">Challan {h.lr_no || "—"}</span>
            <span>{h.customer_name || "—"}</span>
            <span className="font-semibold">{String(h.field_label || "").replace(" (Vehicle Payments)", "")}</span>
            <span>{h.old_value || "₹0"} → <b>{String(h.new_value || "").split("—")[0].trim()}</b></span>
            <span className="text-orange-700">by {h.decided_by || h.requested_by || "—"}</span>
          </div>
        ))}
        {rows.length > 5 && <div className="text-[11px] font-semibold text-orange-700">+{rows.length - 5} more in the Correction Log Book</div>}
      </div>
    </div>
  );
}

function WeeklySecurityDigest() {
  const [data, setData] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const isMonday = new Date().getDay() === 1;
  useEffect(() => {
    if (isMonday) api.get("/security-digest").then((r) => setData(r.data)).catch(() => {});
  }, [isMonday]);
  if (!isMonday || !data) return null;
  const send = async () => {
    let mob = "";
    try {
      const r = await api.get("/settings/otp-mobile");
      mob = String(r.data?.mobile || "").replace(/\D/g, "");
    } catch {}
    const target = mob ? (mob.length === 10 ? "91" + mob : mob) : "";
    const end = new Date();
    const start = new Date(Date.now() - 6 * 86400000);
    const f = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    const msg = [
      `NTC Weekly Security Digest (${f(start)} – ${f(end)} ${end.getFullYear()})`,
      `• Staff Code rejections (7 days): ${data.rejections}`,
      `• Accounts locked right now: ${data.locked.length}${data.locked.length ? " — " + data.locked.slice(0, 5).join(", ") : ""}`,
      `• Stale logins (30+ days): ${data.stale.length}${data.stale.length ? " — " + data.stale.slice(0, 5).join(", ") + (data.stale.length > 5 ? ` +${data.stale.length - 5} more` : "") : ""}`,
      "Review: Staff Logins → Login Activity",
    ].join("\n");
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="flex w-full items-center justify-between rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-3" data-testid="security-digest-card">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-indigo-100 text-indigo-600">
          <ShieldAlert className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-indigo-900">It's Monday — Weekly Security Digest is ready</div>
          <div className="text-xs text-indigo-800" data-testid="security-digest-summary">
            {data.rejections} Staff Code rejection{data.rejections === 1 ? "" : "s"} · {data.locked.length} locked account{data.locked.length === 1 ? "" : "s"} · {data.stale.length} stale login{data.stale.length === 1 ? "" : "s"} (30+ days)
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-100" disabled={emailing} data-testid="security-digest-email-btn"
          onClick={async () => {
            setEmailing(true);
            try {
              const r = await api.post("/security-digest/email");
              if (r.data?.ok) toast.success(`Digest emailed to ${r.data?.to}`);
              else toast.error(r.data?.error || "Email could not be delivered");
            } catch (e) {
              toast.error(formatApiError(e.response?.data?.detail));
            } finally {
              setEmailing(false);
            }
          }}
        >
          {emailing ? "Sending…" : "Email Copy"}
        </Button>
        <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={send} data-testid="security-digest-whatsapp-btn">
          <MessageCircle className="mr-1.5 h-4 w-4" /> Send on WhatsApp
        </Button>
      </div>
    </div>
  );
}

function StaffCodeRejectionNudge() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/staff-code-rejections").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data?.count) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/staff-logins")}
      data-testid="staff-code-rejection-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-red-100 text-red-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-red-900">
            {data.count} Staff Code rejection{data.count > 1 ? "s" : ""} this week — possible misuse
          </div>
          <div className="flex flex-wrap gap-x-3 text-xs text-red-800">
            {(data.rows || []).slice(0, 5).map((r) => (
              <span key={r.id} data-testid="staff-code-rej-chip">
                {r.user_id || r.name || "—"} · {String(r.method || "").replace("Staff Code REJECTED — ", "")}
              </span>
            ))}
            {data.count > 5 && <span>+{data.count - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-red-700">Review in Login Activity →</span>
    </div>
  );
}

function DeductionApprovalNudge() {
  const navigate = useNavigate();
  const [pend, setPend] = useState([]);
  useEffect(() => {
    api.get("/receipts/pending-deductions").then((r) => setPend(r.data || [])).catch(() => {});
  }, []);
  if (!pend.length) return null;
  const totalDed = pend.reduce((s, r) => s + (Number(r.deduction) || 0) + ((r.bills || []).reduce((a, b) => a + (Number(b.deduction) || 0), 0)), 0);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/receipts")}
      data-testid="deduction-approval-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-amber-900">
            {pend.length} MR deduction{pend.length > 1 ? "s" : ""} waiting for your approval · {inr(totalDed)}
          </div>
          <div className="flex flex-wrap gap-x-3 text-xs text-amber-800">
            {pend.slice(0, 5).map((r) => (
              <span key={r.id} data-testid="ded-nudge-chip">
                MR {String(r.mr_no || "").replace(/^MR[-\s]?/i, "") || "—"} · {r.customer_name || "—"} ({r.office || "Chennai"})
              </span>
            ))}
            {pend.length > 5 && <span>+{pend.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-amber-700">Approve in Money Receipts →</span>
    </div>
  );
}

function EwayExpiryNudge() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const managerFor = useBranchManagers();
  useEffect(() => {
    api.get("/shipments").then((r) => {
      const t = new Date().toISOString().slice(0, 10);
      const delivered = (s) => String(s.status || "").toLowerCase() === "delivered" || !!s.pod_path || !!s.delivery_date;
      const out = [];
      (r.data || []).forEach((s) => {
        if (delivered(s)) return;
        const entries = [
          { no: s.eway_bill, valid: s.eway_valid },
          ...(s.extra_eways || []).map((e) => ({ no: e.no, valid: e.valid })),
          ...(s.extra_invoices || []).filter((x) => String(x.eway || "").trim()).map((x) => ({ no: x.eway, valid: x.eway_valid })),
        ];
        entries.forEach((e) => {
          if (String(e.no || "").trim() && (e.valid || "") === t) {
            out.push({ no: e.no, lr: String(s.lr_no || s.consignment_no || "").replace(/^CN[-\s]?/i, ""), route: `${s.origin || ""} → ${s.destination || ""}`, office: s.office || "Chennai" });
          }
        });
      });
      setList(out);
    }).catch(() => {});
  }, []);
  if (!list.length) return null;
  const msg = [
    "*NTC — E-Way Bills Expiring TODAY*",
    "",
    ...list.map((e, i) => `${i + 1}. EWB ${e.no} — LR ${e.lr || "—"} — ${e.route}`),
    "",
    "Please extend these on the GST portal before midnight.",
  ].join("\n");
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/eway-bills")}
      data-testid="eway-expiry-nudge"
      className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-red-100 text-red-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-red-900">
            {list.length} e-way bill{list.length > 1 ? "s" : ""} expiring TODAY — extend on the GST portal before midnight
          </div>
          <div className="flex flex-wrap gap-2 pt-1 text-xs text-red-800">
            {list.slice(0, 5).map((e, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-2 py-0.5" data-testid="eway-expiry-chip">
                <b>{e.no}</b> · LR {e.lr || "—"} · {e.route}
              </span>
            ))}
            {list.length > 5 && <span>+{list.length - 5} more</span>}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
        {[...new Set(list.map((e) => e.office))].filter((b) => managerFor(b)).map((b) => (
          <Button
            key={b}
            size="sm"
            variant="outline"
            className="border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
            onClick={(ev) => {
              ev.stopPropagation();
              const items = list.filter((e) => e.office === b);
              const bm = [
                `*NTC — ${b}: E-Way Bills Expiring TODAY*`,
                "",
                ...items.map((e, i) => `${i + 1}. EWB ${e.no} — LR ${e.lr || "—"} — ${e.route}`),
                "",
                "Please extend these on the GST portal before midnight.",
              ].join("\n");
              window.open(`https://wa.me/${managerFor(b)}?text=${encodeURIComponent(bm)}`, "_blank");
            }}
            data-testid="eway-manager-wa-btn"
          >
            <MessageCircle className="mr-1 h-3.5 w-3.5" /> {b} Manager
          </Button>
        ))}
        <Button
          size="sm"
          className="shrink-0 bg-emerald-600 text-white hover:bg-emerald-700"
          onClick={(ev) => {
            ev.stopPropagation();
            window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
          }}
          data-testid="eway-expiry-wa-btn"
        >
          <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp List
        </Button>
      </div>
    </div>
  );
}

function PendingNotesNudge() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get("/notes/pending").then((r) => setNotes(r.data.notes || [])).catch(() => {});
  }, []);
  if (!notes.length) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/outstanding")}
      data-testid="pending-notes-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-amber-900">
            {notes.length} credit/debit note{notes.length > 1 ? "s" : ""} awaiting your approval — branch-entered, not yet in the ledger
          </div>
          <div className="flex flex-wrap gap-2 pt-1 text-xs text-amber-800">
            {notes.slice(0, 5).map((n) => (
              <span key={n.id} className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2 py-0.5" data-testid="pending-note-chip">
                <b>{n.kind === "credit" ? "CN" : "DN"}</b> {n.party} · {inr(n.amount)}{n.reason ? ` · ${n.reason}` : ""}{n.entered_by ? ` · by ${n.entered_by}` : ""}
              </span>
            ))}
            {notes.length > 5 && <span>+{notes.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-amber-700">Approve in Outstanding →</span>
    </div>
  );
}

function PromisedDateNudge() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/reminders/promised-overdue").then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  if (!rows.length) return null;
  const chase = (e, r) => {
    e.stopPropagation();
    const msg = `Dear ${r.customer_name},\n\nYou had kindly promised payment by ${fmtDate(r.promised_date)}, but we have not yet received it. Outstanding: ${inr(r.outstanding_now)}.\n\nKindly arrange the payment today. If already paid, please share the payment details.\n\n— Nisha Transport Corporation`;
    window.open(wishLink(r.phone, msg), "_blank");
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/outstanding")}
      data-testid="promised-date-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-left transition-colors hover:bg-rose-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-rose-100 text-rose-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-rose-900">
            {rows.length} promised payment{rows.length > 1 ? "s" : ""} overdue — date passed, no receipt
          </div>
          <div className="flex flex-wrap gap-2 pt-1 text-xs text-rose-800">
            {rows.slice(0, 5).map((r) => (
              <span key={r.id} className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-2 py-0.5" data-testid="promised-date-chip">
                {r.customer_name} · promised {fmtDate(r.promised_date)} · {inr(r.outstanding_now)}
                {r.phone && (
                  <button type="button" className="font-bold text-emerald-600 hover:text-emerald-700" title="Chase on WhatsApp" onClick={(e) => chase(e, r)} data-testid="promised-chase-wa-btn">
                    WA
                  </button>
                )}
              </span>
            ))}
            {rows.length > 5 && <span>+{rows.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-rose-700">Open Outstanding →</span>
    </div>
  );
}

function BouncedSalaryNudge() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/salaries/bounced-cheques").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.count) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/salaries")}
      data-testid="bounced-salary-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-red-100 text-red-600">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-red-900">
            {data.count} salary cheque{data.count > 1 ? "s" : ""} BOUNCED · {inr(data.total)} — re-payment needed
          </div>
          <div className="flex flex-wrap gap-x-3 text-xs text-red-800">
            {data.items.slice(0, 5).map((s) => (
              <span key={s.id} data-testid="bounced-salary-chip">
                {s.employee_name || "—"} · Chq {s.cheque_no || "—"} ({s.bank_name || "—"}) · {inr(Number(s.cheque_amount) || Number(s.net_pay) || 0)}
              </span>
            ))}
            {data.count > 5 && <span>+{data.count - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-red-700">Open Staff Salary →</span>
    </div>
  );
}

function HireDueNudge() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/vendors"), api.get("/vendors/stats")])
      .then(([v, s]) => {
        const stats = s.data || {};
        const dues = (v.data || [])
          .map((ven) => ({ name: ven.name, code: ven.vendor_code, due: stats[ven.id]?.hire_due || 0 }))
          .filter((x) => x.due > 0)
          .sort((a, b) => b.due - a.due);
        setData({ dues, total: dues.reduce((a, x) => a + x.due, 0) });
      })
      .catch(() => {});
  }, []);
  if (!data || !data.dues.length) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/vendor-master")}
      data-testid="hire-due-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-orange-300 bg-orange-50 px-4 py-3 text-left transition-colors hover:bg-orange-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-orange-100 text-orange-600">
          <Truck className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-orange-900">
            Broker hire dues: {inr(data.total)} pending across {data.dues.length} vendor{data.dues.length > 1 ? "s" : ""}
          </div>
          <div className="flex flex-wrap gap-x-3 text-xs text-orange-800">
            {data.dues.slice(0, 5).map((d) => (
              <span key={d.name} data-testid="hire-due-chip">
                {d.name}{d.code ? ` (${d.code})` : ""} · {inr(d.due)}
              </span>
            ))}
            {data.dues.length > 5 && <span>+{data.dues.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-orange-700">Open Vendor Master →</span>
    </div>
  );
}

function ChargesTrendCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const now = new Date();
      const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const pd = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prev = `${pd.getFullYear()}-${String(pd.getMonth() + 1).padStart(2, "0")}`;
      const exTot = (c) => (Number(c.unloading_charge) || 0) + (Number(c.challan_charge) || 0) + (Number(c.extra_weight_charge) || 0) + (Number(c.extra_delivery_charge) || 0);
      const sum = { cur: 0, prev: 0, curN: 0, prevN: 0 };
      const byB = {};
      (r.data || []).forEach((c) => {
        const m = String(c.loading_date || c.date || "").slice(0, 7);
        const t = exTot(c);
        if (!t) return;
        const b = c.engaged_office || c.office || "Chennai";
        byB[b] = byB[b] || { name: b, cur: 0, prev: 0 };
        if (m === cur) { sum.cur += t; sum.curN++; byB[b].cur += t; }
        else if (m === prev) { sum.prev += t; sum.prevN++; byB[b].prev += t; }
      });
      sum.branches = Object.values(byB).filter((b) => b.cur || b.prev).sort((a, b) => b.cur - a.cur);
      const d0 = new Date();
      const dow = (d0.getDay() + 6) % 7;
      const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
      const wk = { from: iso(new Date(d0.getFullYear(), d0.getMonth(), d0.getDate() - dow - 7)), to: iso(new Date(d0.getFullYear(), d0.getMonth(), d0.getDate() - dow - 1)), total: 0, count: 0, byB: {} };
      (r.data || []).forEach((c) => {
        const d = String(c.loading_date || c.date || "").slice(0, 10);
        const t = exTot(c);
        if (!t || d < wk.from || d > wk.to) return;
        const b = c.engaged_office || c.office || "Chennai";
        wk.byB[b] = (wk.byB[b] || 0) + t;
        wk.total += t;
        wk.count++;
      });
      sum.weekly = wk;
      setData(sum);
    }).catch(() => {});
  }, []);
  if (!data || (!data.cur && !data.prev)) return null;
  const up = data.cur > data.prev;
  const diff = Math.abs(data.cur - data.prev);
  const pct = data.prev > 0 ? Math.round((diff / data.prev) * 100) : null;
  const fmt = (v) => `₹${Number(v).toLocaleString("en-IN")}`;
  const waWeekly = () => {
    const wk = data.weekly || {};
    const fmtD = (s) => (s ? s.split("-").reverse().join("-") : "");
    const msg = [
      "*NTC — Weekly Extra Charges Summary*",
      `Week: ${fmtD(wk.from)} to ${fmtD(wk.to)}`,
      `Total: ${fmt(wk.total || 0)} across ${wk.count || 0} challan(s)`,
      "",
      ...Object.entries(wk.byB || {}).sort((a, b) => b[1] - a[1]).map(([b, v]) => `• ${b}: ${fmt(v)}`),
      ...(!wk.count ? ["No extra charges last week 🎉"] : []),
      "",
      "Tap every Monday for last week's review — open Charges Report for the detailed sheet.",
    ].join("\n");
    const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
    const num = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/hire-challans")}
      data-testid="charges-trend-card"
      className={`flex w-full cursor-pointer items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors ${up ? "border-amber-300 bg-amber-50 hover:bg-amber-100" : "border-emerald-300 bg-emerald-50 hover:bg-emerald-100"}`}
    >
      <div className="flex items-center gap-3">
        <span className={`grid h-9 w-9 place-items-center rounded-md ${up ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}>
          <IndianRupee className="h-5 w-5" />
        </span>
        <div>
          <div className={`text-sm font-bold ${up ? "text-amber-900" : "text-emerald-900"}`} data-testid="charges-trend-text">
            Extra Charges This Month: {fmt(data.cur)} ({data.curN} challan{data.curN === 1 ? "" : "s"}) — {data.cur === data.prev ? "same as" : `${up ? "▲ up" : "▼ down"} ${fmt(diff)}${pct != null ? ` (${pct}%)` : ""} vs`} last month{data.prev || data.cur === data.prev ? ` (${fmt(data.prev)})` : " (none)"}
          </div>
          <div className={`flex flex-wrap gap-x-3 gap-y-0.5 text-xs ${up ? "text-amber-800" : "text-emerald-800"}`}>
            {(data.branches || []).map((b) => (
              <span key={b.name} className={b.cur > b.prev ? "font-semibold" : ""} data-testid="charges-trend-branch-chip">
                {b.name} {fmt(b.cur)}{b.cur !== b.prev ? ` ${b.cur > b.prev ? "▲" : "▼"}${fmt(Math.abs(b.cur - b.prev))}` : " ="} <span className="opacity-70">(last {fmt(b.prev)})</span>
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <button
          type="button"
          className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
          onClick={(e) => { e.stopPropagation(); waWeekly(); }}
          title="Last week's (Mon–Sun) extra charges per branch — compose to your WhatsApp"
          data-testid="weekly-charges-wa-btn"
        >
          Weekly WhatsApp
        </button>
        <span className={`text-xs font-semibold ${up ? "text-amber-700" : "text-emerald-700"}`}>Open Challans →</span>
      </div>
    </div>
  );
}

function SlowPodVendorCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const m = {};
      (r.data || []).forEach((c) => {
        const v = String(c.vendor_broker_name || c.owner_name || "").trim();
        if (!v) return;
        const k = v.toLowerCase();
        m[k] = m[k] || { name: v, back: 0, out: 0, sumDays: 0 };
        const ld = String(c.loading_date || c.date || "").slice(0, 10);
        if (c.pod_ack_received) {
          m[k].back++;
          if (c.pod_ack_date && /^\d{4}-\d{2}-\d{2}$/.test(ld)) m[k].sumDays += Math.max(Math.floor((new Date(c.pod_ack_date) - new Date(ld)) / 86400000), 0);
        } else {
          m[k].out++;
        }
      });
      const scored = Object.values(m)
        .map((x) => ({ ...x, avg: x.back ? Math.round((x.sumDays / x.back) * 10) / 10 : null }))
        .filter((x) => x.avg != null)
        .sort((a, b) => b.avg - a.avg);
      setData(scored);
    }).catch(() => {});
  }, []);
  if (!data || !data.length) return null;
  const slow = data[0];
  const isSlow = slow.avg > 15;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/hire-challans")}
      data-testid="slow-pod-vendor-card"
      className={`flex w-full cursor-pointer items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors ${isSlow ? "border-red-300 bg-red-50 hover:bg-red-100" : "border-emerald-300 bg-emerald-50 hover:bg-emerald-100"}`}
    >
      <div className="flex items-center gap-3">
        <span className={`grid h-9 w-9 place-items-center rounded-md ${isSlow ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"}`}>
          <Truck className="h-5 w-5" />
        </span>
        <div>
          <div className={`text-sm font-bold ${isSlow ? "text-red-900" : "text-emerald-900"}`} data-testid="slow-pod-vendor-name">
            Slowest POD returner: {slow.name} — avg {slow.avg} day{slow.avg === 1 ? "" : "s"} to return signed PODs{slow.out ? ` · ${slow.out} still out` : ""}
          </div>
          <div className={`flex flex-wrap gap-x-3 text-xs ${isSlow ? "text-red-800" : "text-emerald-800"}`}>
            {data.slice(1, 4).map((v) => (
              <span key={v.name} data-testid="slow-pod-vendor-chip">{v.name} · {v.avg}d avg{v.out ? ` · ${v.out} out` : ""}</span>
            ))}
            {data.length === 1 && <span>Only vendor with returned PODs so far</span>}
          </div>
        </div>
      </div>
      <span className={`text-xs font-semibold ${isSlow ? "text-red-700" : "text-emerald-700"}`}>Open Challans →</span>
    </div>
  );
}

function MonthlyVendorReviewCard() {
  const [data, setData] = useState(null);
  const isFirst = new Date().getDate() === 1;
  useEffect(() => {
    if (!isFirst) return;
    api.get("/hire-challans").then((r) => {
      const m = {};
      (r.data || []).forEach((c) => {
        const v = String(c.vendor_broker_name || c.owner_name || "").trim();
        if (!v) return;
        const k = v.toLowerCase();
        m[k] = m[k] || { name: v, total: 0, back: 0, out: 0, sumDays: 0 };
        m[k].total++;
        const ld = String(c.loading_date || c.date || "").slice(0, 10);
        if (c.pod_ack_received) {
          m[k].back++;
          if (c.pod_ack_date && /^\d{4}-\d{2}-\d{2}$/.test(ld)) m[k].sumDays += Math.max(Math.floor((new Date(c.pod_ack_date) - new Date(ld)) / 86400000), 0);
        } else {
          m[k].out++;
        }
      });
      const scored = Object.values(m)
        .map((x) => ({ ...x, avg: x.back ? Math.round((x.sumDays / x.back) * 10) / 10 : null }))
        .sort((a, b) => (a.avg ?? 1e9) - (b.avg ?? 1e9) || a.out - b.out);
      setData(scored);
    }).catch(() => {});
  }, [isFirst]);
  if (!isFirst || !data || !data.length) return null;
  const slowCnt = data.filter((v) => v.avg != null && v.avg > 15).length;
  const send = () => {
    const monthLabel = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    const msg = [
      `*NTC — Monthly Vendor POD Scorecard (${monthLabel})*`,
      `${data.length} vendor(s) · ${slowCnt} slow (avg over 15 days)`,
      "",
      ...data.slice(0, 15).map((v, i) => `${i + 1}. ${v.name} — ${v.avg != null ? `avg ${v.avg}d` : "no PODs returned"} · ${v.back} back${v.out ? ` · *${v.out} still out*` : ""}`),
      ...(data.length > 15 ? [`…and ${data.length - 15} more`] : []),
      "",
      "Review slow vendors before engaging them this month.",
      "— Nisha Transport Corporation",
    ].join("\n");
    const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="flex w-full items-center justify-between rounded-lg border border-violet-300 bg-violet-50 px-4 py-3" data-testid="monthly-vendor-review-card">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-violet-100 text-violet-600">
          <Truck className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-violet-900">It's the 1st — Monthly Vendor POD Review is ready</div>
          <div className="text-xs text-violet-800" data-testid="monthly-vendor-review-summary">
            {data.length} vendor{data.length === 1 ? "" : "s"} ranked · {slowCnt} slow (15d+ avg) · fastest: {data[0].name}{data[0].avg != null ? ` (${data[0].avg}d)` : ""}
          </div>
        </div>
      </div>
      <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={send} data-testid="monthly-vendor-review-wa-btn">
        <MessageCircle className="mr-1.5 h-4 w-4" /> Send on WhatsApp
      </Button>
    </div>
  );
}

function TopDriverCard() {
  const [top, setTop] = useState(null);
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const m = today().slice(0, 7);
      const g = {};
      (r.data || []).forEach((c) => {
        if (String(c.loading_date || c.date || "").slice(0, 7) !== m) return;
        const name = String(c.driver_name || "").trim();
        const phone = String(c.driver_phone || "").replace(/\D/g, "").slice(-10);
        const key = phone || name.toLowerCase();
        if (!key) return;
        g[key] = g[key] || { name: name || "Driver", phone, trips: 0, routes: new Set() };
        if (name) g[key].name = name;
        g[key].trips += 1;
        const rt = [c.origin, c.destination].filter(Boolean).join(" → ");
        if (rt) g[key].routes.add(rt);
      });
      const best = Object.values(g).sort((a, b) => b.trips - a.trips)[0];
      if (best) setTop({ ...best, routes: [...best.routes] });
    }).catch(() => {});
  }, []);
  if (!top) return null;
  const thank = () => {
    const msg = [
      "🏆 *NTC — Driver of the Month*",
      `Dear ${top.name},`,
      "",
      `Thank you for running ${top.trips} trip(s) with us this month${top.routes.length ? ` (${top.routes.slice(0, 3).join(", ")})` : ""}.`,
      "We value your reliability — keep it up!",
      "— Nisha Transport Corporation",
    ].join("\n");
    const num = top.phone ? `91${top.phone}` : "";
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const certAndWa = () => {
    printDriverCertificate(top);
    if (top.phone) {
      const monthLabel = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
      const msg = [
        "🏆 *NTC — CERTIFICATE OF APPRECIATION*",
        `*Driver of the Month — ${monthLabel}*`,
        "",
        `Proudly presented to *${top.name}*`,
        `in recognition of completing *${top.trips} trip${top.trips === 1 ? "" : "s"}* this month${top.routes.length ? ` on ${top.routes.slice(0, 3).join(", ")}` : ""}, with reliability and dedication that keeps NTC consignments moving on time.`,
        "",
        "A printed copy awaits you at the branch. Congratulations!",
        "— Nisha Transport Corporation",
      ].join("\n");
      window.open(`https://wa.me/91${top.phone}?text=${encodeURIComponent(msg)}`, "_blank");
    }
  };
  return (
    <div className="flex w-full items-center justify-between rounded-lg border border-sky-300 bg-sky-50 px-4 py-3" data-testid="top-driver-card">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-sky-100 text-sky-700">
          <Trophy className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-sky-900">Top Driver This Month — {top.name} · {top.trips} trip{top.trips === 1 ? "" : "s"}</div>
          <div className="text-xs text-sky-800" data-testid="top-driver-summary">
            {top.phone ? `${top.phone} · ` : ""}{top.routes.slice(0, 3).join(" · ") || "Routes not recorded"}
          </div>
        </div>
      </div>
      <span className="inline-flex items-center gap-2">
        <Button size="sm" variant="outline" className="border-sky-300" onClick={certAndWa} data-testid="top-driver-cert-btn">
          <Printer className="mr-1.5 h-4 w-4" /> Certificate{top.phone ? " + WhatsApp" : ""}
        </Button>
        {top.phone ? (
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={thank} data-testid="top-driver-wa-btn">
            <MessageCircle className="mr-1.5 h-4 w-4" /> Send Thanks
          </Button>
        ) : null}
      </span>
    </div>
  );
}

function MonthlyExpiryDigestCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/expiry-digest").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.items?.length) return null;
  const isFirst = new Date().getDate() === 1;
  const send = () => {
    const monthLabel = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    const msg = [
      `*NTC — Document Expiry Digest (${monthLabel})*`,
      `${data.items.length} document(s) / licence(s) due this month:`,
      "",
      ...data.items.slice(0, 25).map((it, i) => `${i + 1}. ${it.name} — ${it.type} expires ${fmtDate(it.date)}${it.who ? ` · ${it.who}` : ""}${it.mobile ? ` (${it.mobile})` : ""}`),
      ...(data.items.length > 25 ? [`…and ${data.items.length - 25} more`] : []),
      "",
      "Arrange renewals before the due dates.",
      "— Nisha Transport Corporation",
    ].join("\n");
    const digits = String(localStorage.getItem("ntc_company_mobile") || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="flex w-full items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="monthly-expiry-digest-card">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-700">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-amber-900">{isFirst ? "It's the 1st — " : ""}Monthly Expiry Digest — {data.items.length} document{data.items.length === 1 ? "" : "s"} due this month</div>
          <div className="text-xs text-amber-800" data-testid="monthly-expiry-digest-summary">
            {data.items.slice(0, 3).map((it) => `${it.name} (${it.type} · ${fmtDate(it.date)})`).join(" · ")}{data.items.length > 3 ? ` · +${data.items.length - 3} more` : ""}
          </div>
        </div>
      </div>
      <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={send} data-testid="monthly-expiry-digest-wa-btn">
        <MessageCircle className="mr-1.5 h-4 w-4" /> Send on WhatsApp
      </Button>
    </div>
  );
}

function AssetRepairNudge() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/assets").then((r) => setItems((r.data || []).filter((a) => a.condition === "Needs Repair" && a.status !== "Disposed"))).catch(() => {});
  }, []);
  if (!items || !items.length) return null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/asset-book")}
      data-testid="asset-repair-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-600">
          <Wrench className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-amber-900">
            {items.length} asset{items.length > 1 ? "s" : ""} need{items.length > 1 ? "" : "s"} repair — schedule maintenance
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-amber-800">
            {items.slice(0, 5).map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5" data-testid="asset-repair-chip">
                {a.name}{a.asset_code ? ` (${a.asset_code})` : ""} · {a.branch || "Chennai"}
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await api.put(`/assets/${a.id}`, { ...a, condition: "Good" });
                      setItems((p) => p.filter((x) => x.id !== a.id));
                      toast.success(`${a.name} marked Repaired (condition: Good)`);
                    } catch {
                      toast.error("Failed to update asset");
                    }
                  }}
                  className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                  title={`Mark ${a.name} as repaired`}
                  data-testid="asset-mark-repaired-btn"
                >
                  ✓ Mark Repaired
                </button>
              </span>
            ))}
            {items.length > 5 && <span>+{items.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-amber-700">Open NTC Asset Book →</span>
    </div>
  );
}

function RateChangeNudge() {
  const [items, setItems] = useState(null);
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    api.get("/rate-change-requests").then((r) => setItems((r.data || []).filter((x) => x.status === "Pending"))).catch(() => {});
    api.get("/staff-master").then((r) => setStaff(r.data || [])).catch(() => {});
  }, []);
  if (!items || !items.length) return null;
  const act = async (e, r, action) => {
    e.stopPropagation();
    try {
      await api.post(`/rate-change-requests/${r.id}/${action}`);
      setItems((p) => p.filter((x) => x.id !== r.id));
      toast.success(action === "approve" ? "Approved — consignment updated" : "Request dismissed");
      const pool = staff.filter((s) => (s.status || "Active") !== "Inactive" && (s.branch || "Chennai") === (r.office || "Chennai") && s.phone);
      const mgr = pool.find((s) => String(s.designation || "").toLowerCase().includes("manager")) || pool.find((s) => (s.name || "").toLowerCase() === String(r.requested_by || "").toLowerCase()) || pool[0];
      const decision = action === "approve" ? "APPROVED ✅ — the consignment has been updated" : "DISMISSED ❌ — values remain unchanged";
      const msg = `*NTC — Rate Change Request ${action === "approve" ? "Approved" : "Dismissed"}*\n\nLR ${String(r.lr_no || "").replace(/^CN[-\s]?/i, "")} (${r.office || "Chennai"})\nRequested by: ${r.requested_by || "—"}\n${diff(r)}\n\nStatus: ${decision}.\n\n— Nisha Transport Corporation`;
      const ph = String(mgr?.phone || "").replace(/\D/g, "");
      window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch {
      toast.error("Action failed");
    }
  };
  const diff = (r) => {
    const parts = [];
    if (r.proposed_freight !== null && r.proposed_freight !== undefined && Number(r.proposed_freight) !== Number(r.current_freight)) parts.push(`Freight ₹${Number(r.current_freight).toLocaleString("en-IN")} → ₹${Number(r.proposed_freight).toLocaleString("en-IN")}`);
    if (r.proposed_basis && r.proposed_basis !== r.current_basis) parts.push(`Basis ${r.current_basis || "—"} → ${r.proposed_basis}`);
    if (r.proposed_lorry_hire !== null && r.proposed_lorry_hire !== undefined && Number(r.proposed_lorry_hire) !== Number(r.current_lorry_hire)) parts.push(`Hire ₹${Number(r.current_lorry_hire).toLocaleString("en-IN")} → ₹${Number(r.proposed_lorry_hire).toLocaleString("en-IN")}`);
    return parts.join(" · ") || "No change proposed";
  };
  return (
    <div className="w-full rounded-lg border border-violet-300 bg-violet-50 px-4 py-3" data-testid="rate-change-nudge">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-violet-900">
        <FileSearch className="h-4 w-4" /> {items.length} rate-change request{items.length > 1 ? "s" : ""} from branches — Freight / Basis / Lorry Hire
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-violet-200 bg-white px-3 py-1.5 text-xs" data-testid="rate-change-row">
            <div className="min-w-0">
              <b>LR {String(r.lr_no || "").replace(/^CN[-\s]?/i, "")}</b> · {r.office} · by {r.requested_by || "—"} — <span className="font-semibold text-violet-800">{diff(r)}</span>
              {r.reason ? <span className="text-muted-foreground"> · “{r.reason}”</span> : null}
            </div>
            <span className="flex shrink-0 items-center gap-1.5">
              <button type="button" onClick={(e) => act(e, r, "approve")} className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700 hover:bg-emerald-100" title="Approve & WhatsApp the branch" data-testid="rate-change-approve-btn">✓ Approve</button>
              <button type="button" onClick={(e) => act(e, r, "dismiss")} className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 font-bold text-red-600 hover:bg-red-100" title="Dismiss & WhatsApp the branch" data-testid="rate-change-dismiss-btn">✕ Dismiss</button>
            </span>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-violet-700">+{items.length - 6} more</div>}
      </div>
    </div>
  );
}

function CbsWhatsAppCard() {
  const [digest, setDigest] = useState(null);
  const [staff, setStaff] = useState([]);
  const [trendOpen, setTrendOpen] = useState(false);
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    api.get("/reports/cbs-digest").then((r) => setDigest(r.data)).catch(() => {});
    api.get("/staff-master").then((r) => setStaff(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (trendOpen && !trend) api.get("/reports/cbs-history?days=30").then((r) => setTrend(r.data)).catch(() => {});
  }, [trendOpen, trend]);
  if (!digest || !digest.rows?.length) return null;
  const sendWa = (e) => {
    e.stopPropagation();
    const lines = digest.rows.map((r) => `${r.branch}: ₹${Number(r.cbs || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
    const msg = `*NTC — Daily CBS (Closing Balance)*\nAs of ${fmtDate(digest.as_of)} (yesterday):\n\n${lines.join("\n")}\n\n*TOTAL: ₹${Number(digest.total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}*\n\n— Nisha Transport Corporation`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const managerFor = (branch) => {
    const pool = staff.filter((s) => (s.status || "Active") !== "Inactive" && (s.branch || "Chennai") === branch && s.phone);
    return pool.find((s) => String(s.designation || "").toLowerCase().includes("manager")) || pool[0];
  };
  const sendBranchWa = (e, r) => {
    e.stopPropagation();
    const mgr = managerFor(r.branch);
    const msg = `*NTC — Daily CBS*\n${mgr ? `Dear ${mgr.name}, ` : ""}${r.branch} closing balance as of ${fmtDate(digest.as_of)} (yesterday): *₹${Number(r.cbs || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}*\n\n— Nisha Transport Corporation`;
    const ph = String(mgr?.phone || "").replace(/\D/g, "");
    window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const chartData = (() => {
    if (!trend?.branches?.length) return [];
    const dates = trend.branches[0].points.map((p) => p.date);
    return dates.map((d, i) => {
      const row = { date: fmtDate(d).slice(0, 6) };
      trend.branches.forEach((b) => { row[b.branch] = b.points[i]?.cbs ?? 0; });
      return row;
    });
  })();
  const colors = ["#0ea5e9", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444"];
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-2.5" data-testid="cbs-whatsapp-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-sky-900">Yesterday&apos;s CBS — {fmtDate(digest.as_of)}:</span>
        {digest.rows.map((r) => (
          <span key={r.branch} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${Number(r.cbs) < 0 ? "border-red-200 bg-white text-red-700" : branchPillClass(r.branch)}`} data-testid="cbs-branch-chip">
            {r.branch} <b>₹{Number(r.cbs || 0).toLocaleString("en-IN")}</b>
            <button type="button" onClick={(e) => sendBranchWa(e, r)} className="inline-flex rounded-full border border-green-300 bg-green-50 px-1.5 py-0.5 text-[10px] font-bold text-green-700 hover:bg-green-100" title={`WhatsApp ${r.branch}'s CBS to its manager`} data-testid="cbs-branch-wa-btn">
              WA
            </button>
          </span>
        ))}
        <span className="inline-flex items-center rounded-full border border-sky-300 bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-900" data-testid="cbs-total-chip">
          Total ₹{Number(digest.total || 0).toLocaleString("en-IN")}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 border-sky-300 text-sky-700 hover:bg-sky-100" onClick={(e) => { e.stopPropagation(); setTrendOpen(true); }} data-testid="cbs-trend-btn">
          <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Trend
        </Button>
        <Button size="sm" className="h-8 bg-[#25D366] text-white hover:bg-[#1ebe5b]" onClick={sendWa} title="Send yesterday's CBS summary on WhatsApp" data-testid="cbs-whatsapp-btn">
          <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp CBS
        </Button>
      </div>
      <Dialog open={trendOpen} onOpenChange={setTrendOpen}>
        <DialogContent className="sm:max-w-[720px]" data-testid="cbs-trend-dialog">
          <DialogHeader><DialogTitle className="font-display">CBS Trend — last 30 days, branch-wise</DialogTitle></DialogHeader>
          {chartData.length ? (
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={4} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} width={80} />
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                  <Legend />
                  {trend.branches.map((b, i) => (
                    <Line key={b.branch} type="monotone" dataKey={b.branch} stroke={colors[i % colors.length]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading trend…</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PendingLogNudge() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/logbook").then((r) => {
      const t = new Date().toISOString().slice(0, 10);
      const list = (r.data || [])
        .filter((l) => String(l.remarks || "").toLowerCase().includes("pending"))
        .map((l) => ({ ...l, _due: l.follow_up_date && String(l.follow_up_date).slice(0, 10) <= t }))
        .sort((a, b) => (b._due ? 1 : 0) - (a._due ? 1 : 0));
      setItems(list);
    }).catch(() => {});
  }, []);
  if (!items || !items.length) return null;
  const dueCount = items.filter((l) => l._due).length;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/log-book")}
      data-testid="pending-log-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-3 text-left transition-colors hover:bg-indigo-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-indigo-100 text-indigo-600">
          <BookOpen className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-indigo-900">
            {items.length} log entr{items.length > 1 ? "ies" : "y"} pending follow-up{dueCount ? <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">{dueCount} DUE</span> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-indigo-800">
            {items.slice(0, 5).map((l) => (
              <span key={l.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${l._due ? "border-red-300 bg-red-50 text-red-700 font-semibold" : "border-indigo-200 bg-white"}`} data-testid="pending-log-chip">
                {l.title} · {l.branch || "Chennai"}{l.follow_up_date ? ` · ${l._due ? "DUE " : "due "}${fmtDate(l.follow_up_date)}` : l.date ? ` · ${fmtDate(l.date)}` : ""}
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await api.put(`/logbook/${l.id}`, { ...l, remarks: "Done" });
                      setItems((p) => p.filter((x) => x.id !== l.id));
                      toast.success(`"${l.title}" marked Done`);
                    } catch {
                      toast.error("Failed to update log entry");
                    }
                  }}
                  className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                  title={`Mark "${l.title}" as Done`}
                  data-testid="log-mark-done-btn"
                >
                  ✓ Mark Done
                </button>
              </span>
            ))}
            {items.length > 5 && <span>+{items.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-indigo-700">Open NTC Log Book →</span>
    </div>
  );
}

function RateExpiryNudge() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [custs, setCusts] = useState([]);
  useEffect(() => {
    api.get("/freight-master").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const list = (r.data || [])
        .filter((f) => f.valid_till && String(f.valid_till).slice(0, 10) < today)
        .sort((a, b) => String(a.valid_till).localeCompare(String(b.valid_till)));
      setItems(list);
    }).catch(() => {});
    api.get("/customers").then((r) => setCusts(r.data || [])).catch(() => {});
  }, []);
  if (!items || !items.length) return null;
  const names = [...new Set(items.map((f) => f.customer_name || "—"))];
  const extend = async (e, c) => {
    e.stopPropagation();
    const fs = items.filter((f) => (f.customer_name || "—") === c);
    try {
      await Promise.all(
        fs.map((f) => {
          const cur = new Date(String(f.valid_till).slice(0, 10));
          const base = new Date(Math.max(isNaN(cur) ? 0 : cur.getTime(), Date.now()));
          base.setFullYear(base.getFullYear() + 1);
          return api.put(`/freight-master/${f.id}`, { ...f, valid_till: base.toISOString().slice(0, 10) });
        })
      );
      setItems((p) => p.filter((f) => (f.customer_name || "—") !== c));
      toast.success(`${c} — ${fs.length} rate${fs.length > 1 ? "s" : ""} extended by 1 year`);
    } catch {
      toast.error("Failed to extend validity");
    }
  };
  const waRateCard = (e, c) => {
    e.stopPropagation();
    const fs = items.filter((f) => (f.customer_name || "—") === c);
    const cust = custs.find((x) => (x.name || "").trim().toLowerCase() === c.trim().toLowerCase());
    const lines = fs.map((r, i) => `${i + 1}. ${r.origin || "—"} → ${r.destination || "—"}: ₹${Number(r.freight_amount || 0).toLocaleString("en-IN")}${r.valid_till ? ` — expired ${fmtDate(r.valid_till)}` : ""}`);
    const msg = `*Nisha Transport Corporation — Agreed Freight Rate Card*\n\nCustomer: ${c}${cust?.customer_code ? ` (${cust.customer_code})` : ""}\n\nThe following agreed rates have crossed their validity — request your confirmation to renew:\n\n${lines.join("\n")}\n\nKindly revert so we can update the rate card. Thank you!\n— Nisha Transport Corporation`;
    const ph = String(cust?.phone || "").replace(/[^\d]/g, "");
    window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/freight-master")}
      data-testid="rate-expiry-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-600">
          <FileSearch className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-amber-900">
            {items.length} agreed freight rate{items.length > 1 ? "s" : ""} past Valid Till — renegotiate
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-amber-800">
            {names.slice(0, 5).map((c) => {
              const fs = items.filter((f) => (f.customer_name || "—") === c);
              return (
                <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-2 py-0.5" data-testid="rate-expiry-chip">
                  {c} · {fs.length} rate{fs.length > 1 ? "s" : ""} · expired {fmtDate(fs[0].valid_till)}
                  <button type="button" onClick={(e) => waRateCard(e, c)} className="inline-flex rounded-full border border-green-300 bg-green-50 px-1.5 py-0.5 text-[10px] font-bold text-green-700 hover:bg-green-100" title={`WhatsApp ${c}'s rate card`} data-testid="rate-wa-btn">
                    WA Rate Card
                  </button>
                  <button type="button" onClick={(e) => extend(e, c)} className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100" title={`Extend ${c}'s expired rates by 1 year`} data-testid="rate-extend-btn">
                    + Extend 1 Year
                  </button>
                </span>
              );
            })}
            {names.length > 5 && <span>+{names.length - 5} more customers</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-amber-700">Open Freight Master →</span>
    </div>
  );
}

function CardRenewalNudge() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/staff-master").then((r) => {
      const t = new Date();
      const soon = new Date(t.getTime() + 30 * 86400000).toISOString().slice(0, 10);
      const today = t.toISOString().slice(0, 10);
      const list = (r.data || [])
        .filter((s) => (s.status || "Active") !== "Inactive" && s.card_valid_upto && String(s.card_valid_upto).slice(0, 10) <= soon)
        .map((s) => ({ ...s, _expired: String(s.card_valid_upto).slice(0, 10) < today }))
        .sort((a, b) => String(a.card_valid_upto).localeCompare(String(b.card_valid_upto)));
      setItems(list);
    }).catch(() => {});
  }, []);
  if (!items || !items.length) return null;
  const expired = items.filter((s) => s._expired).length;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/staff-master")}
      data-testid="card-renewal-nudge"
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-left transition-colors hover:bg-rose-100"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-rose-100 text-rose-600">
          <IdCard className="h-5 w-5" />
        </span>
        <div>
          <div className="text-sm font-bold text-rose-900">
            {items.length} staff ID card{items.length > 1 ? "s" : ""} due for renewal{expired ? <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">{expired} EXPIRED</span> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-rose-800">
            {items.slice(0, 5).map((s) => (
              <span key={s.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${s._expired ? "border-red-300 bg-red-50 font-semibold text-red-700" : "border-rose-200 bg-white"}`} data-testid="card-renewal-chip">
                {s.name} · {s.branch || "Chennai"} · {s._expired ? "expired " : "valid till "}{fmtDate(s.card_valid_upto)}
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const cur = new Date(String(s.card_valid_upto).slice(0, 10));
                    const base = new Date(Math.max(isNaN(cur) ? 0 : cur.getTime(), Date.now()));
                    base.setFullYear(base.getFullYear() + 1);
                    const nd = base.toISOString().slice(0, 10);
                    try {
                      await api.put(`/staff-master/${s.id}`, { ...s, card_valid_upto: nd });
                      setItems((p) => p.filter((x) => x.id !== s.id));
                      toast.success(`${s.name} — card extended to ${fmtDate(nd)}`);
                    } catch {
                      toast.error("Failed to extend card validity");
                    }
                  }}
                  className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                  title={`Extend ${s.name}'s card by 1 year`}
                  data-testid="card-extend-btn"
                >
                  + Extend 1 Year
                </button>
              </span>
            ))}
            {items.length > 5 && <span>+{items.length - 5} more</span>}
          </div>
        </div>
      </div>
      <span className="text-xs font-semibold text-rose-700">Open Staff Master →</span>
    </div>
  );
}

function FestivalReminder() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/calendar-events").then((r) => setEvents(r.data || [])).catch(() => {});
  }, []);
  const todayStr = new Date().toISOString().slice(0, 10);
  const tomorrowStr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.type === "Holiday" && (e.date === todayStr || e.date === tomorrowStr));
  if (!upcoming.length) return null;
  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/70 p-4" data-testid="festival-reminder">
      {upcoming.map((e) => (
        <div key={e.id || e.title} className="flex flex-wrap items-center gap-3" data-testid="festival-reminder-row">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-violet-100 text-violet-600">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-bold text-violet-900">
              {e.date === todayStr ? "Today" : "Tomorrow"} is {e.title} ({fmtDate(e.date)})
            </div>
            <div className="text-xs text-violet-700">Send festival greetings to your customers so wishes go out on time.</div>
          </div>
          <Button size="sm" className="ml-auto bg-violet-600 text-white hover:bg-violet-700" onClick={() => navigate("/customers")} data-testid="festival-reminder-send-btn">
            <MessageCircle className="mr-1.5 h-4 w-4" /> Send Greetings →
          </Button>
        </div>
      ))}
    </div>
  );
}

function TodayCounter() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    const d = new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
    api.get("/reports/today-counts", { params: { start, end } }).then((r) => setCounts(r.data)).catch(() => {});
  }, []);
  if (!counts) return null;
  const items = [
    { label: "LRs", n: counts.consignments, to: "/consignment-entry" },
    { label: "Bills", n: counts.bills, to: "/bills" },
    { label: "MRs", n: counts.receipts, to: "/receipts" },
    { label: "Challans", n: counts.challans, to: "/hire-challans" },
    { label: "Vouchers", n: counts.vouchers, to: "/vouchers" },
    { label: "Expenses", n: counts.expenses, to: "/expenses" },
  ];
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3" data-testid="today-counter">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-emerald-100 text-emerald-600">
        <FilePlus2 className="h-5 w-5" />
      </span>
      <div>
        <div className="text-sm font-semibold text-emerald-800" data-testid="today-counter-total">Created Today: {counts.total}</div>
        <div className="text-xs text-emerald-700">entries added across the system today</div>
      </div>
      <div className="ml-auto flex flex-wrap gap-1.5">
        {items.map((it) => (
          <button
            key={it.label}
            type="button"
            onClick={() => navigate(it.to)}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${it.n ? "border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100" : "border-slate-200 bg-white text-slate-400 hover:bg-slate-50"}`}
            data-testid={`today-count-${it.label.toLowerCase()}`}
          >
            {it.label} {it.n}
          </button>
        ))}
      </div>
    </div>
  );
}

function DeductionApprovalAlert() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/receipts/pending-deductions").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!items.length) return null;
  const dedOf = (r) => (Number(r.deduction) || 0) + (r.bills || []).reduce((s, b) => s + (Number(b.deduction) || 0), 0);
  const decide = async (r, approve) => {
    try {
      await api.put(`/receipts/${r.id}/deduction-approval`, { approve });
      toast.success(`Deduction MR ${String(r.mr_no || "").replace(/^MR[-\s]?/i, "")} ${approve ? "approved" : "rejected"}${approve ? "" : " — bill balance restored"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="deduction-approval-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-amber-800">
          Deduction MR Approvals ({items.length}) — branch receipts with deductions awaiting your approval
        </span>
      </div>
      <div className="mt-2 space-y-1.5">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-700" data-testid="deduction-approval-row">
            <span className="font-bold">MR {String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</span>
            <span>· {r.office || "—"}</span>
            <span>· {r.customer_name || "—"}</span>
            <span>· Received {inr(r.amount)}</span>
            <span className="font-bold">· Deduction {inr(dedOf(r))}</span>
            {r.deduction_reason && <span>· “{r.deduction_reason}”</span>}
            <span className="ml-auto flex gap-1">
              <button
                type="button"
                className="rounded-full bg-emerald-600 px-2.5 py-0.5 font-bold text-white hover:bg-emerald-700"
                onClick={() => decide(r, true)}
                data-testid="deduction-approve-btn"
              >
                Approve
              </button>
              <button
                type="button"
                className="rounded-full bg-rose-600 px-2.5 py-0.5 font-bold text-white hover:bg-rose-700"
                onClick={() => decide(r, false)}
                data-testid="deduction-reject-btn"
              >
                Reject
              </button>
            </span>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-amber-600">+{items.length - 6} more…</div>}
      </div>
    </div>
  );
}

function SubmissionFollowupAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/bill-submissions").then((r) => {
      const t = new Date().toISOString().slice(0, 10);
      setItems(
        (r.data || [])
          .filter((s) => s.expected_date && s.expected_date < t && !["Cleared", "Returned"].includes(s.status))
          .sort((a, b) => (a.expected_date || "").localeCompare(b.expected_date || ""))
      );
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  const total = items.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const days = (d) => Math.floor((new Date(new Date().toISOString().slice(0, 10)) - new Date(d)) / 86400000);
  return (
    <div className="mb-4 rounded-lg border border-sky-300 bg-sky-50/70 px-4 py-3" data-testid="submission-followup-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-sky-600" />
        <span className="text-sm font-bold text-sky-800">
          Payment Follow-ups ({items.length}) — submitted bills past expected payment date · {inr(total)} pending
        </span>
        <button
          type="button"
          onClick={() => navigate("/bill-submission")}
          className="ml-auto text-xs font-semibold text-sky-700 underline-offset-2 hover:underline"
          data-testid="followup-alert-open"
        >
          Open Bill Submission →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 5).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-sky-700" data-testid="followup-alert-row">
            <span className="font-bold">Sub {String(r.submission_no || "—").replace(/^BS[-\s]?/i, "")}</span>
            <span>· {r.customer_name || "—"}</span>
            {r.submitted_to && <span>· {r.submitted_to}</span>}
            <span>· expected {fmtDate(r.expected_date)}</span>
            <span className="ml-auto rounded-full bg-sky-600 px-2 py-0.5 font-bold text-white">
              {inr(r.amount)} · {days(r.expected_date)}d overdue
            </span>
          </div>
        ))}
        {items.length > 5 && <div className="text-xs text-sky-600">+{items.length - 5} more…</div>}
      </div>
    </div>
  );
}

function NextCallReminder() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/bill-submissions").then((r) => {
      const t = new Date().toISOString().slice(0, 10);
      const out = [];
      (r.data || []).forEach((s) => {
        if (["Cleared", "Returned"].includes(s.status)) return;
        const logs = s.call_logs || [];
        if (!logs.length) return;
        const last = logs[logs.length - 1];
        if (last.next_call_date && last.next_call_date <= t) out.push({ ...s, _next: last.next_call_date, _outcome: last.outcome, _lastDate: last.date });
      });
      out.sort((a, b) => (a._next || "").localeCompare(b._next || ""));
      setItems(out);
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  const t = new Date().toISOString().slice(0, 10);
  return (
    <div className="mb-4 rounded-lg border border-indigo-300 bg-indigo-50/70 px-4 py-3" data-testid="next-call-reminder">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-indigo-600" />
        <span className="text-sm font-bold text-indigo-800">
          Follow-up Calls Due ({items.length}) — promised call-backs for today or earlier
        </span>
        <button
          type="button"
          onClick={() => navigate("/bill-submission")}
          className="ml-auto text-xs font-semibold text-indigo-700 underline-offset-2 hover:underline"
          data-testid="next-call-open"
        >
          Open Bill Submission →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-indigo-700" data-testid="next-call-row">
            <span className="font-bold">Sub {String(r.submission_no || "—").replace(/^BS[-\s]?/i, "")}</span>
            <span>· {r.customer_name || "—"}</span>
            <span>· {inr(r.amount)}</span>
            {r._outcome && <span>· last: {r._outcome} ({fmtDate(r._lastDate)})</span>}
            <span className={`ml-auto rounded-full px-2 py-0.5 font-bold text-white ${r._next < t ? "bg-rose-600" : "bg-indigo-600"}`}>
              {r._next < t ? `Call overdue · ${fmtDate(r._next)}` : "Call today"}
            </span>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-indigo-600">+{items.length - 6} more…</div>}
      </div>
    </div>
  );
}

function ReplacementReminderAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/receipts").then((r) => {
      const now = Date.now();
      setItems(
        (r.data || [])
          .filter((x) => x.cheque_status === "Bounced" && x.replacement?.status === "Pending")
          .map((x) => {
            const since = x.replacement.cheque_date || (x.replacement.added_at || "").slice(0, 10);
            return { ...x, _days: since ? Math.floor((now - new Date(since).getTime()) / 86400000) : 0 };
          })
          .filter((x) => x._days > 7)
          .sort((a, b) => b._days - a._days)
      );
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-orange-300 bg-orange-50/70 px-4 py-3" data-testid="replacement-reminder-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <span className="text-sm font-bold text-orange-800">
          Replacement Cheques Stuck ({items.length}) — pending beyond a week, deposit or chase them
        </span>
        <button
          type="button"
          onClick={() => navigate("/receipts")}
          className="ml-auto text-xs font-semibold text-orange-700 underline-offset-2 hover:underline"
          data-testid="replacement-reminder-open"
        >
          Open Money Receipts →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 5).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-orange-700" data-testid="replacement-reminder-row">
            <span className="font-bold">MR {String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</span>
            <span>· {r.customer_name || "—"}</span>
            <span>· Repl cheque {r.replacement.cheque_no}{r.replacement.bank_name ? ` (${r.replacement.bank_name})` : ""}</span>
            <span className="ml-auto rounded-full bg-orange-600 px-2 py-0.5 font-bold text-white">
              {inr(Number(r.replacement.cheque_amount) || 0)} · {r._days}d pending
            </span>
          </div>
        ))}
        {items.length > 5 && <div className="text-xs text-orange-600">+{items.length - 5} more…</div>}
      </div>
    </div>
  );
}

function BouncedChequeAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [issued, setIssued] = useState([]);
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    api.get("/receipts").then((r) => {
      setItems((r.data || []).filter((x) => x.cheque_status === "Bounced" && x.replacement?.status !== "Cleared").sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1)));
    }).catch(() => {});
    api.get("/hire-challans").then((r) => {
      const out = [];
      (r.data || []).forEach((c) => {
        (c.payments || []).forEach((p) => {
          if (p.cheque_status === "Bounced") out.push({ ...p, challan_no: c.challan_no, vendor: c.vendor_broker_name, vehicle_no: c.vehicle_no, challan_id: c.id });
        });
      });
      setIssued(out.sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1)));
    }).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, []);
  if (!items.length && !issued.length) return null;
  const sendBounceWa = (r) => {
    const cust = customers.find((c) => c.name === r.customer_name);
    let phone = String(cust?.phone || "").replace(/\D/g, "");
    if (phone.length === 10) phone = `91${phone}`;
    const amt = Number(r.cheque_amount) || Number(r.amount) || 0;
    const msg = [
      "*Nisha Transport Corporation — Cheque Returned*",
      `Dear ${r.customer_name || "Customer"},`,
      `Your cheque no. ${r.cheque_no || "—"}${r.cheque_date ? ` dated ${fmtDate(r.cheque_date)}` : ""}${r.bank_name ? ` drawn on ${r.bank_name}` : ""} for *${inr(amt)}* against MR ${String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")} has been returned (bounced) by the bank.`,
      ...(Number(r.bounce_charge) > 0 ? [`Bank bounce charges of *${inr(Number(r.bounce_charge))}* are also payable by you.`] : []),
      "Kindly arrange an alternate payment at the earliest. Thank you.",
    ].join("\n");
    window.open(phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-rose-300 bg-rose-50/70 px-4 py-3" data-testid="bounced-cheque-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-rose-600" />
        <span className="text-sm font-bold text-rose-800">
          Bounced Cheques ({items.length} received{issued.length ? ` + ${issued.length} issued` : ""}) — follow up before money slips
        </span>
        <button
          type="button"
          onClick={() => navigate("/receipts")}
          className="ml-auto text-xs font-semibold text-rose-700 underline-offset-2 hover:underline"
          data-testid="bounced-alert-open-receipts"
        >
          Open Money Receipts →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-rose-700" data-testid="bounced-cheque-row">
            <span className="font-bold">MR {String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</span>
            <span>· {r.customer_name || "—"}</span>
            <span>· Cheque {r.cheque_no || "—"}</span>
            {r.bank_name && <span>· {r.bank_name}</span>}
            {r.replacement && (
              <span className="rounded-full border border-sky-300 bg-sky-50 px-2 py-0.5 font-bold text-sky-700" data-testid="replacement-pending-chip">
                Repl {r.replacement.cheque_no} · {r.replacement.status || "Pending"}
              </span>
            )}
            <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 font-bold text-white">
              {inr(Number(r.cheque_amount) || Number(r.amount) || 0)}{r.cheque_date ? ` · ${fmtDate(r.cheque_date)}` : ""}
            </span>
            <button
              type="button"
              onClick={() => sendBounceWa(r)}
              className="flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white hover:bg-emerald-700"
              title="WhatsApp the customer about this bounced cheque"
              data-testid="bounce-wa-btn"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </button>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-rose-600">+{items.length - 6} more…</div>}
      </div>
      {issued.length > 0 && (
        <div className="mt-3 border-t border-rose-200 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-rose-800">Issued cheques bounced — challan payments to vehicle owners</span>
            <button
              type="button"
              onClick={() => navigate("/hire-challans")}
              className="ml-auto text-xs font-semibold text-rose-700 underline-offset-2 hover:underline"
              data-testid="bounced-alert-open-challans"
            >
              Open Vehicle Hire Challan →
            </button>
          </div>
          <div className="mt-1.5 space-y-1">
            {issued.slice(0, 6).map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-x-2 text-xs text-rose-700" data-testid="issued-bounced-row">
                <span className="font-bold">Ch {p.challan_no || "—"}</span>
                <span>· {p.vendor || "—"}</span>
                {p.vehicle_no && <span>· {p.vehicle_no}</span>}
                <span>· Cheque {p.cheque_no || "—"}</span>
                {p.bank_name && <span>· {p.bank_name}</span>}
                <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 font-bold text-white">
                  {inr(Number(p.cheque_amount) || Number(p.amount) || 0)}{p.cheque_date ? ` · ${fmtDate(p.cheque_date)}` : ""}
                </span>
              </div>
            ))}
            {issued.length > 6 && <div className="text-xs text-rose-600">+{issued.length - 6} more…</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function CbsWatchCard() {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    api.get("/reports/cbs-history?days=8").then((r) => setHist(r.data)).catch(() => {});
  }, []);
  if (!hist?.branches?.length) return null;
  const changes = hist.branches.map((b) => ({
    branch: b.branch,
    change: (b.points[b.points.length - 1]?.cbs ?? 0) - (b.points[0]?.cbs ?? 0),
  }));
  const dropped = changes.filter((c) => c.change < 0).sort((a, b) => a.change - b.change);
  if (!dropped.length) return null;
  const worst = dropped[0];
  const rs = (n) => `₹${Math.abs(n).toLocaleString("en-IN")}`;
  return (
    <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-sm" data-testid="cbs-watch-card">
      <div className="flex flex-wrap items-center gap-2 font-semibold text-rose-800">
        <TrendingDown className="h-4 w-4" />
        CBS Drop Watch — <span data-testid="cbs-watch-worst">{worst.branch}</span> CBS fell {rs(worst.change)} in the last 7 days
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs">
        {changes.map((c) => (
          <span
            key={c.branch}
            className={`rounded-full border px-2 py-0.5 font-semibold ${c.change < 0 ? "border-rose-300 bg-white text-rose-700" : "border-emerald-300 bg-white text-emerald-700"}`}
            data-testid={`cbs-watch-${c.branch.toLowerCase()}`}
          >
            {c.branch}: {c.change < 0 ? "−" : "+"}{rs(c.change)}
          </span>
        ))}
      </div>
    </div>
  );
}

function MissingPapersCard() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(null);
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState({});
  useEffect(() => {
    api.get("/bill-enquiry").then((r) => {
      const list = r.data?.rows || [];
      setRows(list);
      const pod = list.filter((x) => !x.pod_path).length;
      const mr = list.filter((x) => !x.mr_copy_path).length;
      const bill = list.filter((x) => !x.bill_copy_path).length;
      const any = list.filter((x) => !x.pod_path || !x.mr_copy_path || !x.bill_copy_path).length;
      setCounts({ pod, mr, bill, any, total: list.length });
    }).catch(() => {});
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
  }, []);
  if (!counts || !counts.any) return null;
  const chips = [
    { label: "Missing POD", n: counts.pod, testid: "missing-papers-pod" },
    { label: "Missing MR Copy", n: counts.mr, testid: "missing-papers-mr" },
    { label: "Missing Bill Copy", n: counts.bill, testid: "missing-papers-bill" },
  ].filter((c) => c.n > 0);
  const chaseGroups = (() => {
    const m = {};
    rows.forEach((r) => {
      const missing = [!r.pod_path && "POD", !r.mr_copy_path && "MR Copy"].filter(Boolean);
      if (!missing.length) return;
      const br = r.office || "Chennai";
      (m[br] = m[br] || []).push({ bill: String(r.invoice_no || "").replace(/^INV[-\s]?/i, ""), customer: r.customer_name, missing });
    });
    return Object.entries(m).map(([branch, bills]) => ({ branch, bills })).sort((a, b) => a.branch.localeCompare(b.branch));
  })();
  const chaseBranch = (g) => {
    const lines = g.bills.map((b, i) => `${i + 1}. Bill ${b.bill} (${b.customer}) — Missing: ${b.missing.join(", ")}`).join("\n");
    const msg = `NISHA TRANSPORT CORPORATION — Morning Papers Chase (${g.branch})\n\nGood morning! The following bills have missing documents. Please arrange and upload them in Bill Enquiry today:\n\n${lines}\n\nTotal: ${g.bills.length} bill(s) pending papers.`;
    const phone = String(contacts[g.branch] || "").replace(/\D/g, "");
    const target = phone ? (phone.length === 10 ? `91${phone}` : phone) : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-violet-300 bg-violet-50/70 px-4 py-3" data-testid="missing-papers-card">
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-md bg-violet-100 text-violet-600">
          <FileSearch className="h-4 w-4" />
        </span>
        <span className="text-sm font-bold text-violet-900" data-testid="missing-papers-count">
          Bill Papers Pending — {counts.any} of {counts.total} bill{counts.total > 1 ? "s" : ""} missing POD / MR / bill copy
        </span>
        <button
          type="button"
          onClick={() => navigate("/bill-enquiry?papers=" + encodeURIComponent("Missing Any"))}
          className="ml-auto text-xs font-semibold text-violet-700 underline-offset-2 hover:underline"
          data-testid="missing-papers-open"
        >
          Open Bill Enquiry →
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => navigate("/bill-enquiry?papers=" + encodeURIComponent(c.label))}
            className="rounded-full border border-violet-300 bg-white px-2.5 py-1 text-xs font-semibold text-violet-800 transition-colors hover:bg-violet-100"
            data-testid={c.testid}
          >
            {c.label} <b>{c.n}</b>
          </button>
        ))}
        {chaseGroups.length > 0 && (
          <span className="ml-1 flex flex-wrap items-center gap-1.5" data-testid="morning-chase-group">
            <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700">Morning chase:</span>
            {chaseGroups.map((g) => (
              <button
                key={g.branch}
                type="button"
                onClick={() => chaseBranch(g)}
                className="flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-bold text-white hover:bg-emerald-700"
                title={`WhatsApp ${g.branch} its ${g.bills.length} bill(s) with missing POD/MR copy`}
                data-testid="morning-chase-wa-btn"
              >
                <MessageCircle className="h-3 w-3" /> {g.branch} ({g.bills.length})
              </button>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

function BrokenPromisesAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/reminder-log"), api.get("/reports/outstanding")]).then(([l, o]) => {
      const bal = {};
      const ph = {};
      (o.data?.rows || []).forEach((r) => {
        const k = (r.customer_name || "").trim().toLowerCase();
        bal[k] = (bal[k] || 0) + (Number(r.net_balance) || 0);
        if (r.customer_phone && !ph[k]) ph[k] = r.customer_phone;
      });
      const today = new Date().toISOString().slice(0, 10);
      const out = (l.data?.rows || [])
        .filter((lg) => ["Promised to pay", "Partly paying"].includes(lg.response || "") && lg.promised_date && lg.promised_date < today)
        .map((lg) => {
          const k = (lg.customer_name || "").trim().toLowerCase();
          return { ...lg, balance: bal[k] || 0, phone: ph[k] || "" };
        })
        .filter((lg) => lg.balance > 0.01)
        .sort((a, b) => a.promised_date.localeCompare(b.promised_date));
      setItems(out);
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  const daysLate = (d) => Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  const chase = (lg) => {
    const msg =
      `*NISHA TRANSPORT CORPORATION*\nDear ${lg.customer_name},\n` +
      `You had promised payment by ${new Date(lg.promised_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} — ` +
      `₹${lg.balance.toLocaleString("en-IN")} is still outstanding (${daysLate(lg.promised_date)} days past promise).\n` +
      `Kindly arrange the payment today.\n— Nisha Transport Corporation`;
    window.open(lg.phone ? wishLink(lg.phone, msg) : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-red-300 bg-red-50/70 px-4 py-3" data-testid="broken-promises-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <span className="text-sm font-bold text-red-800" data-testid="broken-promises-count">
          Broken Promises ({items.length}) — promised payment date passed, balance still unpaid
        </span>
        <button
          type="button"
          onClick={() => navigate("/bill-enquiry")}
          className="ml-auto text-xs font-semibold text-red-700 underline-offset-2 hover:underline"
          data-testid="broken-promises-open"
        >
          Open Reminder Log →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 5).map((lg) => (
          <div key={lg.id} className="flex flex-wrap items-center gap-x-2 text-xs text-red-800" data-testid="broken-promise-row">
            <span className="font-bold">{lg.customer_name}</span>
            <span>· promised {new Date(lg.promised_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
            {lg.response_note ? <span>· “{lg.response_note}”</span> : null}
            <span className="ml-auto rounded-full bg-red-600 px-2 py-0.5 font-bold text-white">
              {daysLate(lg.promised_date)}d late · ₹{lg.balance.toLocaleString("en-IN")} due
            </span>
            <button
              type="button"
              onClick={() => chase(lg)}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-white px-2 py-0.5 font-bold text-emerald-700 hover:bg-emerald-50"
              data-testid="broken-promise-whatsapp"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </button>
          </div>
        ))}
        {items.length > 5 && <div className="text-xs text-red-600">+{items.length - 5} more…</div>}
      </div>
    </div>
  );
}

function TdsMismatchAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/bill-enquiry").then((r) => {
      const out = [];
      (r.data?.rows || []).forEach((x) => {
        const tds = (x.receipts || []).reduce((s, m) => s + (Number(m.tds) || 0), 0);
        if (!(tds > 0)) return;
        const lo = Math.round(x.total * 1) / 100;
        const hi = Math.round(x.total * 2) / 100;
        if (tds < lo - 0.5 || tds > hi + 0.5) out.push({ bill: String(x.invoice_no || "").replace(/^INV[-\s]?/i, ""), customer: x.customer_name, total: x.total, tds, lo, hi });
      });
      setItems(out.sort((a, b) => b.tds - a.tds));
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-fuchsia-300 bg-fuchsia-50/70 px-4 py-3" data-testid="tds-mismatch-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-fuchsia-600" />
        <span className="text-sm font-bold text-fuchsia-900">
          TDS Mismatch ({items.length}) — customer-deducted TDS outside the expected 1–2% band
        </span>
        <button
          type="button"
          onClick={() => navigate("/bill-enquiry")}
          className="ml-auto text-xs font-semibold text-fuchsia-700 underline-offset-2 hover:underline"
          data-testid="tds-mismatch-open"
        >
          Open Bill Enquiry →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 5).map((x) => (
          <div key={x.bill} className="flex flex-wrap items-center gap-x-2 text-xs text-fuchsia-800" data-testid="tds-mismatch-row">
            <span className="font-bold">Bill {x.bill}</span>
            <span>· {x.customer}</span>
            <span>· Bill {inr(x.total)}</span>
            <span className="ml-auto rounded-full bg-fuchsia-600 px-2 py-0.5 font-bold text-white">
              TDS {inr(x.tds)} · expected {inr(x.lo)}–{inr(x.hi)}
            </span>
          </div>
        ))}
        {items.length > 5 && <div className="text-xs text-fuchsia-600">+{items.length - 5} more…</div>}
      </div>
    </div>
  );
}

function PendingChequeReminder() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/receipts").then((r) => {
      const now = new Date();
      const list = (r.data || [])
        .filter((x) => (x.payment_mode === "Cheque" || x.cheque_no) && (x.cheque_status || "Pending") === "Pending")
        .map((x) => {
          const ref = x.deposit_date || x.cheque_date || x.date;
          const days = ref ? Math.floor((now - new Date(ref)) / 86400000) : 0;
          return { ...x, _days: days, _fromDeposit: !!x.deposit_date };
        })
        .filter((x) => x._days >= 7)
        .sort((a, b) => b._days - a._days);
      setItems(list);
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="pending-cheque-reminder">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-amber-800">Cheques Pending Clearance 7+ Days ({items.length}) — deposit or update status</span>
        <button
          type="button"
          onClick={() => navigate("/receipts")}
          className="ml-auto text-xs font-semibold text-amber-700 underline-offset-2 hover:underline"
          data-testid="pending-cheque-open-receipts"
        >
          Open Cheque Register →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-amber-800" data-testid="pending-cheque-row">
            <span className="font-bold">MR {String(r.mr_no || "—").replace(/^MR[-\s]?/i, "")}</span>
            <span>· {r.customer_name || "—"}</span>
            <span>· Cheque {r.cheque_no || "—"}</span>
            {r.bank_name && <span>· {r.bank_name}</span>}
            <span className="ml-auto rounded-full bg-amber-500 px-2 py-0.5 font-bold text-white">
              {inr(Number(r.cheque_amount) || Number(r.amount) || 0)} · Pending {r._days}d{r._fromDeposit ? " since deposit" : ""}
            </span>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-amber-700">+{items.length - 6} more…</div>}
      </div>
    </div>
  );
}

function IssuedBounceAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    api.get("/vouchers").then((r) => {
      setItems((r.data || []).filter((v) => v.payment_mode === "Cheque" && v.cheque_status === "Bounced").sort((a, b) => ((a.date || "") < (b.date || "") ? 1 : -1)));
    }).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  const sendVendorWa = (v) => {
    const vend = vendors.find((x) => x.name === v.party_name);
    let phone = String(vend?.phone || "").replace(/\D/g, "");
    if (phone.length === 10) phone = `91${phone}`;
    const amt = Number(v.cheque_amount) || Number(v.amount) || 0;
    const msg = [
      "*Nisha Transport Corporation — Payment Update*",
      `Dear ${v.party_name || "Partner"},`,
      `Our cheque no. ${v.cheque_no || "—"}${v.cheque_date ? ` dated ${fmtDate(v.cheque_date)}` : ""}${v.bank_name ? ` drawn on ${v.bank_name}` : ""} for *${inr(amt)}* issued to you has unfortunately been returned by the bank.`,
      "We sincerely apologise for the inconvenience. A fresh payment is being arranged and will reach you shortly.",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-orange-300 bg-orange-50/70 px-4 py-3" data-testid="issued-bounce-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <span className="text-sm font-bold text-orange-800">Issued Cheques Bounced ({items.length}) — arrange vendor payment again</span>
        <button
          type="button"
          onClick={() => navigate("/receipts")}
          className="ml-auto text-xs font-semibold text-orange-700 underline-offset-2 hover:underline"
          data-testid="issued-bounce-open-register"
        >
          Open Cheque Register →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((v) => (
          <div key={v.id} className="flex flex-wrap items-center gap-x-2 text-xs text-orange-800" data-testid="issued-bounce-row">
            <span className="font-bold">DV {String(v.voucher_no || "—").replace(/^DV[-\s]?/i, "")}</span>
            <span>· {v.party_name || "—"}</span>
            <span>· Cheque {v.cheque_no || "—"}</span>
            {v.bank_name && <span>· {v.bank_name}</span>}
            <span className="ml-auto rounded-full bg-orange-600 px-2 py-0.5 font-bold text-white">
              {inr(Number(v.cheque_amount) || Number(v.amount) || 0)}{v.cheque_date ? ` · ${fmtDate(v.cheque_date)}` : ""}
            </span>
            <button
              type="button"
              onClick={() => sendVendorWa(v)}
              className="flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white hover:bg-emerald-700"
              title="WhatsApp the vendor an apology with payment update"
              data-testid="vendor-bounce-wa-btn"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </button>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-orange-700">+{items.length - 6} more…</div>}
      </div>
    </div>
  );
}

function BankPositionCard() {
  const [data, setData] = useState(null);
  const [showMonthly, setShowMonthly] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/receipts"), api.get("/vouchers")])
      .then(([r, v]) => {
        const clearedIn = (r.data || []).filter((x) => (x.payment_mode === "Cheque" || x.cheque_no) && x.cheque_status === "Cleared" && !x.reversed);
        const clearedOut = (v.data || []).filter((x) => x.payment_mode === "Cheque" && x.cheque_status === "Cleared");
        const amt = (x) => Number(x.cheque_amount) || Number(x.amount) || 0;
        const mkey = (x) => String(x.deposit_date || x.cheque_date || x.date || "").slice(0, 7);
        const months = {};
        clearedIn.forEach((x) => { const k = mkey(x); if (k) { months[k] = months[k] || { in: 0, out: 0 }; months[k].in += amt(x); } });
        clearedOut.forEach((x) => { const k = mkey(x); if (k) { months[k] = months[k] || { in: 0, out: 0 }; months[k].out += amt(x); } });
        const monthly = Object.entries(months).sort((a, b) => (a[0] < b[0] ? 1 : -1)).slice(0, 6);
        const cashInList = (r.data || []).filter((x) => x.payment_mode === "Cash" && !x.reversed);
        const cashOutList = (v.data || []).filter((x) => x.payment_mode === "Cash");
        const camt = (x) => Number(x.amount) || 0;
        const t = today();
        const days = {};
        cashInList.forEach((x) => { const k = x.date || String(x.created_at || "").slice(0, 10); if (k) { days[k] = days[k] || { in: 0, out: 0 }; days[k].in += camt(x); } });
        cashOutList.forEach((x) => { const k = x.date || String(x.created_at || "").slice(0, 10); if (k) { days[k] = days[k] || { in: 0, out: 0 }; days[k].out += camt(x); } });
        const cashDays = Object.entries(days).sort((a, b) => (a[0] < b[0] ? -1 : 1));
        setData({
          inTotal: clearedIn.reduce((a, x) => a + amt(x), 0),
          outTotal: clearedOut.reduce((a, x) => a + amt(x), 0),
          inCount: clearedIn.length,
          outCount: clearedOut.length,
          monthly,
          cashIn: cashInList.reduce((a, x) => a + camt(x), 0),
          cashOut: cashOutList.reduce((a, x) => a + camt(x), 0),
          cashInCount: cashInList.length,
          cashOutCount: cashOutList.length,
          cashTodayIn: cashInList.filter((x) => x.date === t).reduce((a, x) => a + camt(x), 0),
          cashTodayOut: cashOutList.filter((x) => x.date === t).reduce((a, x) => a + camt(x), 0),
          cashDays,
        });
      })
      .catch(() => {});
  }, []);
  if (!data || (data.inCount === 0 && data.outCount === 0 && data.cashInCount === 0 && data.cashOutCount === 0)) return null;
  const net = data.inTotal - data.outTotal;
  const monthLabel = (k) => {
    const [y, m] = k.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  };
  const fmtNet = (n) => (n < 0 ? `-${inr(Math.abs(n))}` : inr(n));
  const downloadTrendCsv = () => {
    const lines = ["Month,Cheques In,Cheques Out,Net"];
    [...data.monthly].reverse().forEach(([k, m]) => lines.push([monthLabel(k), m.in, m.out, m.in - m.out].join(",")));
    lines.push(["TOTAL", data.inTotal, data.outTotal, data.inTotal - data.outTotal].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bank-trend-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const sharePosition = () => {
    const msg = [
      `*NTC — Money Position (${fmtDate(today())})*`,
      `Bank (cleared cheques): In ${inr(data.inTotal)} · Out ${inr(data.outTotal)} · Net ${fmtNet(data.inTotal - data.outTotal)}`,
      `Cash: In ${inr(data.cashIn)} · Out ${inr(data.cashOut)} · Net ${fmtNet(data.cashIn - data.cashOut)}`,
      `Today's cash: In ${inr(data.cashTodayIn)} · Out ${inr(data.cashTodayOut)}`,
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <>
    {(data.inCount > 0 || data.outCount > 0) && (
    <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3" data-testid="bank-position-card">
      <div className="flex items-center gap-2">
        <Landmark className="h-4 w-4 text-sky-600" />
        <span className="text-sm font-bold text-sky-900">Bank Position — Cleared Cheques</span>
        <button
          type="button"
          onClick={sharePosition}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="Send today's bank & cash position on WhatsApp"
          data-testid="position-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
        <button
          type="button"
          onClick={downloadTrendCsv}
          className="text-xs font-semibold text-sky-700 underline-offset-2 hover:underline"
          data-testid="bank-trend-excel-btn"
        >
          Trend Excel ↓
        </button>
        <button
          type="button"
          onClick={() => setShowMonthly((s) => !s)}
          className="text-xs font-semibold text-sky-700 underline-offset-2 hover:underline"
          data-testid="bank-monthly-toggle"
        >
          {showMonthly ? "Hide Monthly Trend" : "Monthly Trend →"}
        </button>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-md bg-white/70 px-2 py-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Cheques In ({data.inCount})</div>
          <div className="text-sm font-bold tabular-nums text-emerald-700" data-testid="bank-in">{inr(data.inTotal)}</div>
        </div>
        <div className="rounded-md bg-white/70 px-2 py-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Cheques Out ({data.outCount})</div>
          <div className="text-sm font-bold tabular-nums text-red-600" data-testid="bank-out">{inr(data.outTotal)}</div>
        </div>
        <div className="rounded-md bg-white/70 px-2 py-1.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Net Position</div>
          <div className={`text-sm font-extrabold tabular-nums ${net >= 0 ? "text-sky-800" : "text-red-700"}`} data-testid="bank-net">{fmtNet(net)}</div>
        </div>
      </div>
      {showMonthly && data.monthly.length > 0 && (
        <div className="mt-3 overflow-hidden rounded-md border border-sky-200 bg-white/80" data-testid="bank-monthly-table">
          <table className="w-full text-xs">
            <thead className="bg-sky-100/60">
              <tr className="[&>th]:px-3 [&>th]:py-1.5 [&>th]:text-left [&>th]:font-semibold [&>th]:text-sky-800">
                <th>Month</th><th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly.map(([k, m]) => (
                <tr key={k} className="border-t border-sky-100 [&>td]:px-3 [&>td]:py-1.5" data-testid="bank-monthly-row">
                  <td className="font-semibold">{monthLabel(k)}</td>
                  <td className="text-right tabular-nums text-emerald-700">{inr(m.in)}</td>
                  <td className="text-right tabular-nums text-red-600">{inr(m.out)}</td>
                  <td className={`text-right font-bold tabular-nums ${m.in - m.out >= 0 ? "text-sky-800" : "text-red-700"}`}>{fmtNet(m.in - m.out)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    )}
    {(data.cashInCount > 0 || data.cashOutCount > 0) && (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3" data-testid="cash-position-card">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-bold text-emerald-900">Cash Position — Cash Receipts &amp; Vouchers</span>
          <span className="ml-auto text-xs font-semibold text-emerald-700" data-testid="cash-today">
            Today: In {inr(data.cashTodayIn)} · Out {inr(data.cashTodayOut)}
          </span>
          {!(data.inCount > 0 || data.outCount > 0) && (
            <button
              type="button"
              onClick={sharePosition}
              className="flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
              data-testid="position-wa-btn"
            >
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </button>
          )}
          <button
            type="button"
            onClick={() => printCashBook(data.cashDays)}
            className="text-xs font-semibold text-emerald-700 underline-offset-2 hover:underline"
            data-testid="cash-book-print-btn"
          >
            Cash Book 🖶
          </button>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-md bg-white/70 px-2 py-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Cash In ({data.cashInCount})</div>
            <div className="text-sm font-bold tabular-nums text-emerald-700" data-testid="cash-in">{inr(data.cashIn)}</div>
          </div>
          <div className="rounded-md bg-white/70 px-2 py-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Cash Out ({data.cashOutCount})</div>
            <div className="text-sm font-bold tabular-nums text-red-600" data-testid="cash-out">{inr(data.cashOut)}</div>
          </div>
          <div className="rounded-md bg-white/70 px-2 py-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Net Cash</div>
            <div className={`text-sm font-extrabold tabular-nums ${data.cashIn - data.cashOut >= 0 ? "text-emerald-800" : "text-red-700"}`} data-testid="cash-net">{fmtNet(data.cashIn - data.cashOut)}</div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function WeeklyDigestCard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [snap, setSnap] = useState(null);
  const isMonday = new Date().getDay() === 1;
  useEffect(() => {
    if (!isMonday) return;
    Promise.all([api.get("/shipments"), api.get("/booking-digest/latest")])
      .then(([sh, dg]) => {
        const end = new Date(today());
        const start = new Date(end);
        start.setDate(start.getDate() - 7);
        const fro = start.toISOString().slice(0, 10);
        const to = new Date(end.getTime() - 86400000).toISOString().slice(0, 10);
        const week = (sh.data || []).filter((r) => {
          const bd = String(r.lr_date || r.dispatch_date || "").slice(0, 10);
          return bd >= fro && bd <= to;
        });
        const freight = week.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
        const byBranch = {};
        const routes = {};
        week.forEach((r) => {
          const b = r.office || "Chennai";
          byBranch[b] = (byBranch[b] || 0) + 1;
          const k = `${r.origin || "?"} → ${r.destination || "?"}`;
          routes[k] = (routes[k] || 0) + 1;
        });
        setD({ fro, to, n: week.length, freight, byBranch, routes, delivered: week.filter((r) => /delivered/i.test(r.status || "")).length });
        setSnap(dg.data && dg.data.prepared_at ? dg.data : null);
      })
      .catch(() => {});
  }, [isMonday]);
  if (!isMonday || !d) return null;
  const share = () => {
    const msg = [
      `*NTC — Weekly Booking Digest (${fmtDate(d.fro)} to ${fmtDate(d.to)})*`,
      "",
      `*Total Bookings:* ${d.n}`,
      `*Total Freight:* ₹${d.freight.toLocaleString("en-IN")}`,
      `*Delivered:* ${d.delivered} · *Pending:* ${d.n - d.delivered}`,
      `*By Branch:* ${Object.entries(d.byBranch).sort((a, b) => b[1] - a[1]).map(([b, n]) => `${b}: ${n}`).join(" · ") || "—"}`,
      "",
      "*Top Routes:*",
      Object.entries(d.routes).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, n], i) => `${i + 1}. ${k} — ${n} booking(s)`).join("\n") || "—",
      "",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-violet-300 bg-violet-50/70 px-4 py-3" data-testid="weekly-digest-card">
      <div className="flex flex-wrap items-center gap-2">
        <BarChart3 className="h-4 w-4 text-violet-700" />
        <span className="text-sm font-bold text-violet-900">Weekly Booking Digest — {fmtDate(d.fro)} to {fmtDate(d.to)}</span>
        {snap && (
          <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold text-white" title={`Emailed at ${snap.prepared_at || ""}`} data-testid="weekly-digest-badge">
            Emailed 8am ✓
          </span>
        )}
        <button
          type="button"
          className="cursor-pointer text-xs font-semibold text-violet-800 underline-offset-2 hover:underline"
          onClick={() => navigate("/booking-report")}
          data-testid="weekly-digest-open-report"
        >
          {d.n} booking(s) · ₹{d.freight.toLocaleString("en-IN")} · {d.delivered} delivered
        </button>
        <button
          type="button"
          onClick={share}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="WhatsApp last week's digest to head office"
          data-testid="weekly-digest-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
      </div>
    </div>
  );
}

function EveningBookingCard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ping, setPing] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/shipments"), api.get("/booking-ping/today")])
      .then(([sh, bp]) => {
        const t = today();
        const todays = (sh.data || []).filter((r) => String(r.lr_date || r.dispatch_date || "").slice(0, 10) === t);
        const freight = todays.reduce((s, r) => s + (Number(r.freight_amount) || 0), 0);
        const byBranch = {};
        const routes = {};
        todays.forEach((r) => {
          const b = r.office || "Chennai";
          byBranch[b] = (byBranch[b] || 0) + 1;
          const k = `${r.origin || "?"} → ${r.destination || "?"}`;
          routes[k] = (routes[k] || 0) + 1;
        });
        setD({ n: todays.length, freight, byBranch, routes, delivered: todays.filter((r) => /delivered/i.test(r.status || "")).length });
        setPing(bp.data && bp.data.msg ? bp.data : null);
      })
      .catch(() => setD({ n: 0, freight: 0, byBranch: {}, routes: {}, delivered: 0 }));
  }, []);
  if (!d) return null;
  const share = () => {
    const msg = ping?.msg || [
      `*NTC — Daily Booking Ping (${fmtDate(today())})*`,
      "",
      `*Bookings today:* ${d.n}`,
      `*Total Freight:* ₹${d.freight.toLocaleString("en-IN")}`,
      `*Delivered same-day:* ${d.delivered} · *Pending:* ${d.n - d.delivered}`,
      `*By Branch:* ${Object.entries(d.byBranch).sort((a, b) => b[1] - a[1]).map(([b, n]) => `${b}: ${n}`).join(" · ") || "—"}`,
      "",
      "*Top Routes:*",
      Object.entries(d.routes).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, n], i) => `${i + 1}. ${k} — ${n} booking(s)`).join("\n") || "—",
      "",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-teal-300 bg-teal-50/70 px-4 py-3" data-testid="booking-ping-card">
      <div className="flex flex-wrap items-center gap-2">
        <Truck className="h-4 w-4 text-teal-700" />
        <span className="text-sm font-bold text-teal-900">Daily Booking Ping — {fmtDate(today())}</span>
        {ping && (
          <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold text-white" title={`Prepared at ${ping.prepared_at || ""}`} data-testid="booking-ping-prepared-badge">
            Prepared 7pm ✓ ready to send
          </span>
        )}
        <button
          type="button"
          className="cursor-pointer text-xs font-semibold text-teal-800 underline-offset-2 hover:underline"
          onClick={() => navigate("/booking-report")}
          data-testid="booking-ping-open-report"
        >
          {d.n} booking(s) · ₹{d.freight.toLocaleString("en-IN")} freight today
        </button>
        <button
          type="button"
          onClick={share}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="WhatsApp today's booking summary to head office"
          data-testid="booking-ping-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
      </div>
    </div>
  );
}

function MorningBriefingCard() {
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [target, setTarget] = useState(0);
  const [branchTargets, setBranchTargets] = useState({});
  const [prepared, setPrepared] = useState(null);
  const [tgOpen, setTgOpen] = useState(false);
  const [tgForm, setTgForm] = useState({});
  const load = () => {
    Promise.all([
      api.get("/bill-submissions"),
      api.get("/hire-challans"),
      api.get("/shipments"),
      api.get("/receipts"),
      api.get("/settings/collection-target"),
      api.get("/briefings/today"),
    ])
      .then(([s, c, sh, rc, tg, br]) => {
        const t = today();
        const month = t.slice(0, 7);
        const bm = (list, key, val) => {
          const m = {};
          list.forEach((r) => { const k = key(r) || "Chennai"; m[k] = (m[k] || 0) + val(r); });
          return Object.entries(m).sort((x, y) => y[1] - x[1]);
        };
        const overdue = (s.data || []).filter(
          (r) => r.expected_date && String(r.expected_date).slice(0, 10) < t && !/paid|cleared|settled|received/i.test(r.status || "")
        );
        const dues = (c.data || []).filter((r) => (Number(r.balance) || 0) > 0);
        const vendors = new Set(dues.map((r) => (r.vendor_broker_name || r.owner_name || "—").trim().toLowerCase()));
        const byId = {};
        const byCn = {};
        (sh.data || []).forEach((x) => { byId[x.id] = x; if (x.consignment_no) byCn[x.consignment_no] = x; });
        const pods = (c.data || []).filter((r) => {
          if (r.pod_path) return false;
          const ship = byId[r.consignment_id] || byCn[r.cn_no] || byCn[r.consignment_no];
          return ship?.status === "Delivered";
        });
        const todayRc = (rc.data || []).filter((r) => (r.date || "") === t);
        const mtdRc = (rc.data || []).filter((r) => String(r.date || "").startsWith(month));
        const lmDate = new Date(t);
        lmDate.setDate(0);
        const lastMonth = lmDate.toISOString().slice(0, 7);
        const lmC = (rc.data || []).filter((r) => String(r.date || "").startsWith(lastMonth)).reduce((x, r) => x + (Number(r.amount) || 0), 0);
        setTarget(Number(tg.data?.target) || 0);
        setBranchTargets(tg.data?.branches || {});
        setPrepared(br.data?.msg ? br.data : null);
        setB({
          overdueN: overdue.length,
          overdueT: overdue.reduce((x, r) => x + (Number(r.amount) || 0), 0),
          overdueBr: bm(overdue, (r) => r.office, (r) => Number(r.amount) || 0),
          duesN: vendors.size,
          duesT: dues.reduce((x, r) => x + (Number(r.balance) || 0), 0),
          duesBr: bm(dues, (r) => r.branch || r.office, (r) => Number(r.balance) || 0),
          podN: pods.length,
          podBr: bm(pods, (r) => r.branch || r.office, () => 1),
          todayC: todayRc.reduce((x, r) => x + (Number(r.amount) || 0), 0),
          todayBr: bm(todayRc, (r) => r.office, (r) => Number(r.amount) || 0),
          mtdC: mtdRc.reduce((x, r) => x + (Number(r.amount) || 0), 0),
          lmC,
          lmLabel: lmDate.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
          mtdBr: Object.fromEntries(bm(mtdRc, (r) => r.office, (r) => Number(r.amount) || 0)),
        });
      })
      .catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!b || (!b.overdueN && !b.duesN && !b.podN && !b.todayC && !target && !b.mtdC)) return null;
  const share = () => {
    if (prepared?.msg) {
      window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(prepared.msg)}`, "_blank");
      return;
    }
    const pct = target > 0 ? Math.round((b.mtdC / target) * 100) : null;
    const msg = [
      `*NTC — Morning Briefing (${fmtDate(today())})*`,
      "",
      `1. Overdue bill submissions: ${b.overdueN} — ₹${b.overdueT.toLocaleString("en-IN")}`,
      `2. Vendor hire dues: ${b.duesN} vendor(s) — ₹${b.duesT.toLocaleString("en-IN")}`,
      `3. Delivered trips missing POD: ${b.podN}`,
      `4. Collections today: ₹${b.todayC.toLocaleString("en-IN")} · MTD ₹${b.mtdC.toLocaleString("en-IN")}${target > 0 ? ` of ₹${target.toLocaleString("en-IN")} target (${pct}%)` : ""}`,
      "",
      "Please review and action in the NTC app.",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const setTargetPrompt = () => {
    const known = Array.from(new Set([...BRANCHES, ...Object.keys(branchTargets), ...Object.keys(b.mtdBr || {})]));
    setTgForm(Object.fromEntries(known.map((k) => [k, branchTargets[k] || ""])));
    setTgOpen(true);
  };
  const saveTargets = async () => {
    const branches = {};
    Object.entries(tgForm).forEach(([k, v]) => {
      const n = Number(String(v).replace(/[^\d.]/g, "")) || 0;
      if (n > 0) branches[k] = n;
    });
    try {
      const res = await api.put("/settings/collection-target", { target: Object.values(branches).reduce((s, v) => s + v, 0), branches });
      setTarget(Number(res.data?.target) || 0);
      setBranchTargets(res.data?.branches || {});
      setTgOpen(false);
      toast.success("Collection targets saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const BranchRow = ({ list, money = true }) =>
    list.length > 1 ? (
      <span className="mt-1 flex flex-wrap gap-1" data-testid="briefing-branch-split">
        {list.slice(0, 4).map(([k, v]) => (
          <span key={k} className="rounded-full bg-white/70 px-1.5 py-0.5 text-[10px] font-bold">
            {k}: {money ? inr(v) : v}
          </span>
        ))}
        {list.length > 4 && <span className="text-[10px] font-bold opacity-60">+{list.length - 4}</span>}
      </span>
    ) : null;
  const Tile = ({ label, value, sub, onClick, tone, testid, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-w-[170px] flex-1 flex-col items-start rounded-md border px-3 py-2 text-left transition-colors ${tone}`}
      data-testid={testid}
    >
      <span className="text-[11px] font-bold uppercase tracking-wide opacity-70">{label}</span>
      <span className="text-lg font-extrabold">{value}</span>
      <span className="text-xs font-semibold opacity-80">{sub}</span>
      {children}
    </button>
  );
  const pct = target > 0 ? Math.min(100, Math.round((b.mtdC / target) * 100)) : 0;
  return (
    <div className="mb-4 rounded-lg border border-indigo-300 bg-indigo-50/70 px-4 py-3" data-testid="morning-briefing-card">
      <div className="flex items-center gap-2">
        <BellRing className="h-4 w-4 text-indigo-600" />
        <span className="text-sm font-bold text-indigo-900">Morning Briefing — {fmtDate(today())}</span>
        {prepared && (
          <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white" title={`Prepared at ${prepared.prepared_at || ""}`} data-testid="briefing-prepared-badge">
            Prepared 9am ✓ ready to send
          </span>
        )}
        <button
          type="button"
          onClick={share}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="WhatsApp this briefing to head office"
          data-testid="briefing-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Tile
          label="Overdue Bills"
          value={b.overdueN ? inr(b.overdueT) : "None"}
          sub={b.overdueN ? `${b.overdueN} submission(s) past expected date` : "All payments on time"}
          onClick={() => navigate("/bill-submission")}
          tone={b.overdueN ? "border-red-300 bg-red-50 text-red-900 hover:bg-red-100" : "border-emerald-200 bg-emerald-50 text-emerald-800"}
          testid="briefing-overdue-tile"
        >
          <BranchRow list={b.overdueBr} />
        </Tile>
        <Tile
          label="Vendor Dues"
          value={b.duesN ? inr(b.duesT) : "None"}
          sub={b.duesN ? `${b.duesN} vendor(s) with unpaid hire balance` : "No hire balances pending"}
          onClick={() => navigate("/hire-challans")}
          tone={b.duesN ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100" : "border-emerald-200 bg-emerald-50 text-emerald-800"}
          testid="briefing-dues-tile"
        >
          <BranchRow list={b.duesBr} />
        </Tile>
        <Tile
          label="Pending PODs"
          value={b.podN || "None"}
          sub={b.podN ? "delivered trip(s) missing signed POD" : "All delivered trips have PODs"}
          onClick={() => navigate("/hire-challans")}
          tone={b.podN ? "border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100" : "border-emerald-200 bg-emerald-50 text-emerald-800"}
          testid="briefing-pod-tile"
        >
          <BranchRow list={b.podBr} money={false} />
        </Tile>
        <Tile
          label="Collections Today"
          value={inr(b.todayC)}
          sub={target > 0 ? `MTD ${inr(b.mtdC)} of ${inr(target)} target (${pct}%)` : `MTD ${inr(b.mtdC)} · tap to set monthly target`}
          onClick={setTargetPrompt}
          tone="border-sky-300 bg-sky-50 text-sky-900 hover:bg-sky-100"
          testid="briefing-collection-tile"
        >
          {Number(today().slice(8, 10)) <= 7 && (b.lmC > 0 || target > 0) && (
            <span className={`mt-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${target > 0 && b.lmC >= target ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`} data-testid="briefing-last-month-review">
              Last month ({b.lmLabel}): {inr(b.lmC)}{target > 0 ? ` of ${inr(target)} (${Math.round((b.lmC / target) * 100)}%)${b.lmC >= target ? " — target achieved 🎯" : ""}` : ""}
            </span>
          )}
          {target > 0 && (
            <span className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-sky-200" data-testid="briefing-target-bar">
              <span className={`block h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : "bg-sky-600"}`} style={{ width: `${pct}%` }} />
            </span>
          )}
          {Object.keys(branchTargets).length > 0 && (
            <span className="mt-1.5 w-full space-y-1" data-testid="briefing-branch-targets">
              {Object.entries(branchTargets).map(([k, tv]) => {
                const got = Number(b.mtdBr?.[k]) || 0;
                const p = tv > 0 ? Math.min(100, Math.round((got / tv) * 100)) : 0;
                return (
                  <span key={k} className="block">
                    <span className="flex justify-between text-[10px] font-bold">
                      <span>{k}</span>
                      <span>{inr(got)} / {inr(tv)} ({p}%)</span>
                    </span>
                    <span className="block h-1 w-full overflow-hidden rounded-full bg-sky-200">
                      <span className={`block h-full rounded-full ${p >= 100 ? "bg-emerald-500" : "bg-sky-600"}`} style={{ width: `${p}%` }} />
                    </span>
                  </span>
                );
              })}
            </span>
          )}
          <BranchRow list={b.todayBr} />
        </Tile>
      </div>
      <Dialog open={tgOpen} onOpenChange={setTgOpen}>
        <DialogContent className="max-w-sm" data-testid="target-dialog">
          <DialogHeader>
            <DialogTitle>Monthly Collection Targets (₹)</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {Object.keys(tgForm).map((k) => (
              <div key={k} className="flex items-center gap-2">
                <span className="w-24 text-sm font-semibold">{k}</span>
                <input
                  type="number"
                  min="0"
                  className="h-9 flex-1 rounded-md border border-input bg-white px-3 text-sm"
                  placeholder="0"
                  value={tgForm[k]}
                  onChange={(e) => setTgForm((p) => ({ ...p, [k]: e.target.value }))}
                  data-testid={`target-input-${k.toLowerCase()}`}
                />
              </div>
            ))}
            <div className="flex items-center justify-between border-t pt-2 text-sm font-bold">
              <span>Total target</span>
              <span data-testid="target-total">{inr(Object.values(tgForm).reduce((s, v) => s + (Number(String(v).replace(/[^\d.]/g, "")) || 0), 0))}</span>
            </div>
            <button type="button" onClick={saveTargets} className="h-9 w-full rounded-md bg-sky-600 text-sm font-bold text-white hover:bg-sky-700" data-testid="target-save-btn">
              Save Targets
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverdueSubmissionsCard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api
      .get("/bill-submissions")
      .then((r) => {
        const t = today();
        setRows(
          (r.data || []).filter(
            (s) => s.expected_date && String(s.expected_date).slice(0, 10) < t && !/paid|cleared|settled|received/i.test(s.status || "")
          )
        );
      })
      .catch(() => {});
  }, []);
  if (!rows.length) return null;
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const od = (r) => Math.floor((new Date(today()) - new Date(String(r.expected_date).slice(0, 10))) / 86400000);
  const share = () => {
    const msg = [
      "*NTC — Overdue Bill Submissions*",
      "Payment expected date crossed:",
      ...rows.slice(0, 15).map((r, i) => `${i + 1}. Sub ${String(r.submission_no || "—").replace(/^BS[-\s]?/i, "")} — ${r.customer_name || "—"} — Bill ${String(r.invoice_no || "—").replace(/^INV[-\s]?/i, "")} — ₹${(Number(r.amount) || 0).toLocaleString("en-IN")} — expected ${fmtDate(r.expected_date)} (*${od(r)}d overdue*)`),
      ...(rows.length > 15 ? [`…and ${rows.length - 15} more`] : []),
      "",
      `Total overdue: ₹${total.toLocaleString("en-IN")} — ${rows.length} submission(s). Please follow up for payment.`,
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-red-300 bg-red-50/70 px-4 py-3" data-testid="overdue-submissions-card">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <span className="text-sm font-bold text-red-900">
          Overdue Bill Submissions ({rows.length}) — payment expected date crossed · {inr(total)}
        </span>
        <button
          type="button"
          onClick={share}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="WhatsApp this overdue list to head office"
          data-testid="overdue-submissions-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
        <button
          type="button"
          onClick={() => navigate("/bill-submission")}
          className="text-xs font-semibold text-red-700 underline-offset-2 hover:underline"
          data-testid="overdue-submissions-open"
        >
          Open Bill Submission →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {rows.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-red-900" data-testid="overdue-submission-row">
            <span className="font-bold">Sub {String(r.submission_no || "—").replace(/^BS[-\s]?/i, "")}</span>
            <span>· {r.customer_name || "—"}</span>
            <span>· Bill {String(r.invoice_no || "—").replace(/^INV[-\s]?/i, "")}</span>
            <span>· expected {fmtDate(r.expected_date)}</span>
            <span className="rounded-full bg-red-600 px-2 py-0.5 font-bold text-white">{od(r)}d overdue</span>
            <span className="ml-auto font-bold">{inr(r.amount)}</span>
          </div>
        ))}
        {rows.length > 6 && <div className="text-xs text-red-700">+{rows.length - 6} more…</div>}
      </div>
    </div>
  );
}

function VendorPaymentReminderCard() {
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    api.get("/vendor-reminders/latest").then((r) => setSnap(r.data || null)).catch(() => {});
  }, []);
  const rows = snap?.rows || [];
  if (!rows.length) return null;
  if (!snap.date || (new Date(today()) - new Date(snap.date)) / 86400000 > 7) return null;
  const share = () => {
    const msg = [
      "*NTC — Weekly Vendor Payment Reminder*",
      `Week of ${fmtDate(snap.date)}`,
      "",
      "Vendors with unpaid hire balances:",
      ...rows.slice(0, 15).map((x, i) => `${i + 1}. ${x.vendor}${x.code ? ` (${x.code})` : ""} — ${x.challans} challan(s) — *₹${(Number(x.balance) || 0).toLocaleString("en-IN")}*`),
      ...(rows.length > 15 ? [`…and ${rows.length - 15} more`] : []),
      "",
      `*Total Balance Due: ₹${(Number(snap.total) || 0).toLocaleString("en-IN")}*`,
      "",
      "Please arrange the vendor payments.",
    ].join("\n");
    window.open(`https://wa.me/${HEAD_OFFICE.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="vendor-reminder-card">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-amber-900">
          Weekly Vendor Payment Reminder ({rows.length}) — unpaid balances {inr(snap.total)} · week of {fmtDate(snap.date)}
        </span>
        <button
          type="button"
          onClick={share}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="WhatsApp this vendor balance list to head office"
          data-testid="vendor-reminder-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
        <button
          type="button"
          onClick={() => navigate("/vendor-ledger")}
          className="text-xs font-semibold text-amber-700 underline-offset-2 hover:underline"
          data-testid="vendor-reminder-open-ledger"
        >
          Open Vendor Ledger →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {rows.slice(0, 6).map((x) => (
          <div key={x.vendor} className="flex flex-wrap items-center gap-x-2 text-xs text-amber-900" data-testid="vendor-reminder-row">
            <span className="font-bold">{x.vendor}</span>
            {x.code ? <span>· {x.code}</span> : null}
            <span>· {x.challans} challan(s)</span>
            <span className="ml-auto rounded-full bg-amber-600 px-2 py-0.5 font-bold text-white">{inr(x.balance)}</span>
          </div>
        ))}
        {rows.length > 6 && <div className="text-xs text-amber-700">+{rows.length - 6} more…</div>}
      </div>
    </div>
  );
}

function VehicleBalanceDueAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState({});
  useEffect(() => {
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
    Promise.all([api.get("/hire-challans"), api.get("/shipments")])
      .then(([c, s]) => {
        const byId = {};
        const byCn = {};
        (s.data || []).forEach((x) => {
          byId[x.id] = x;
          if (x.consignment_no) byCn[x.consignment_no] = x;
        });
        const t = today();
        const flagged = (c.data || []).filter((r) => {
          if (!(Number(r.balance) > 0)) return false;
          if ((r.payments || []).some((p) => p.type === "Vehicle Balance Payment")) return false;
          const ship = byId[r.consignment_id] || byCn[r.cn_no] || byCn[r.consignment_no];
          const delivered = ship?.status === "Delivered";
          const d = String(r.loading_date || r.date || "");
          const aged = d && (new Date(t) - new Date(d)) / 86400000 >= 7;
          return delivered || aged;
        });
        flagged.sort((a, b) => (String(a.loading_date || "") < String(b.loading_date || "") ? -1 : 1));
        setItems(flagged);
      })
      .catch(() => {});
  }, []);
  if (!items.length) return null;
  const days = (r) => Math.floor((new Date(today()) - new Date(String(r.loading_date || r.date || today()))) / 86400000);
  const shareBalanceDue = () => {
    const msg = [
      "*NTC — Vehicle Balance Due Reminder*",
      "Owner balance payments pending:",
      ...items.slice(0, 15).map((r) => `• Challan ${String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")} — ${r.vehicle_no || "—"} — ${r.owner_name || r.vendor_broker_name || "—"} — ${r.origin || "—"} → ${r.destination || "—"} — Balance ${inr(r.balance)} (${days(r)}d)`,),
      ...(items.length > 15 ? [`…and ${items.length - 15} more`] : []),
      "Please arrange the pending owner payments.",
    ].join("\n");
    const branches = [...new Set(items.map((r) => r.engaged_office || r.office).filter(Boolean))];
    let num = branches.length === 1 ? String(contacts[branches[0]] || "").replace(/\D/g, "") : "";
    if (num.length === 10) num = `91${num}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  return (
    <div className="mb-4 rounded-lg border border-violet-300 bg-violet-50/70 px-4 py-3" data-testid="vehicle-balance-due-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-violet-600" />
        <span className="text-sm font-bold text-violet-900">Vehicle Balance Due ({items.length}) — owner payments pending after trip</span>
        <button
          type="button"
          onClick={shareBalanceDue}
          className="ml-auto flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          title="Send the pending owner balance list to the branch on WhatsApp"
          data-testid="balance-due-wa-btn"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp Branch
        </button>
        <button
          type="button"
          onClick={() => navigate("/hire-challans")}
          className="text-xs font-semibold text-violet-700 underline-offset-2 hover:underline"
          data-testid="balance-due-open-challans"
        >
          Open Hire Challans →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-x-2 text-xs text-violet-900" data-testid="vehicle-balance-due-row">
            <span className="font-bold">Challan {String(r.challan_no || "—").replace(/^VHC[-\s]?/i, "")}</span>
            <span>· {r.vehicle_no || "—"}</span>
            <span>· {r.owner_name || r.vendor_broker_name || "—"}</span>
            <span>· {r.origin || "—"} → {r.destination || "—"}</span>
            <span className="ml-auto rounded-full bg-violet-600 px-2 py-0.5 font-bold text-white">
              Balance {inr(r.balance)} · {days(r)}d
            </span>
          </div>
        ))}
        {items.length > 6 && <div className="text-xs text-violet-700">+{items.length - 6} more…</div>}
      </div>
    </div>
  );
}

function CircularNotice() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/circulars").then((r) => {
      const list = (r.data || [])
        .filter((c) => !c.to_branch || c.to_branch === "All Branches" || c.to_branch === user?.office || user?.role === "admin")
        .sort((a, b) => (String(a.date || a.created_at || "") < String(b.date || b.created_at || "") ? 1 : -1))
        .slice(0, 5);
      setItems(list);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!items.length) return null;
  const isRead = (c) => (c.acks || []).some((a) => a.user_id === user?.id);
  const markRead = async (c) => {
    try {
      const res = await api.post(`/circulars/${c.id}/ack`);
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, acks: [...(x.acks || []), res.data.ack || { user_id: user?.id }] } : x)));
      toast.success("Circular marked as read");
    } catch (e) {
      toast.error("Could not mark as read");
    }
  };
  const unread = items.filter((c) => !isRead(c)).length;
  return (
    <div className="mb-4 rounded-lg border border-indigo-300 bg-indigo-50/70 px-4 py-3" data-testid="circular-notice">
      <div className="flex items-center gap-2">
        <BellRing className="h-4 w-4 text-indigo-600" />
        <span className="text-sm font-bold text-indigo-900">
          NTC Circulars — Latest Updates{unread > 0 ? ` (${unread} unread)` : ""}
        </span>
        <button
          type="button"
          onClick={() => navigate("/circulars")}
          className="ml-auto text-xs font-semibold text-indigo-700 underline-offset-2 hover:underline"
          data-testid="circular-notice-open"
        >
          View All Circulars →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-x-2 text-xs text-indigo-900" data-testid="circular-notice-row">
            <span className="font-bold">{String(c.circular_no || "—").replace(/^CIR[-\s]?/i, "CIR-")}</span>
            <span>· {c.date ? fmtDate(c.date) : "—"}</span>
            <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700">{c.category || "General"}</span>
            <span className="font-semibold">{c.subject || "—"}</span>
            {c.attachment_path && (
              <a
                href={`${API}/files/${c.attachment_path}?auth=${localStorage.getItem("ntc_token")}`}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-indigo-700 underline underline-offset-2"
                data-testid="circular-notice-attachment"
              >
                📎 {c.attachment_filename || "Attachment"}
              </a>
            )}
            {isRead(c) ? (
              <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid="circular-read-badge">Read ✓</span>
            ) : (
              <button
                type="button"
                onClick={() => markRead(c)}
                className="ml-auto rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-indigo-700"
                data-testid="circular-mark-read-btn"
              >
                Mark as Read
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LockedAccountsAlert() {
  const [locked, setLocked] = useState([]);
  const managerFor = useBranchManagers();
  const load = () => {
    api.get("/users").then((r) => {
      const now = new Date();
      setLocked((r.data || []).filter((u) => {
        if (!u.locked_until) return false;
        const d = new Date(u.locked_until);
        return !isNaN(d) && d > now;
      }));
    }).catch(() => {});
  };
  useEffect(load, []);
  const unlock = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.user_id || u.email} unlocked — they can log in now`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  if (!locked.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50/80 px-4 py-3" data-testid="locked-accounts-alert">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-red-600" />
        <span className="text-sm font-bold text-red-800">Locked Accounts ({locked.length}) — too many wrong passwords / Staff Codes</span>
      </div>
      <div className="mt-2 space-y-1">
        {locked.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-x-2 text-xs text-red-700" data-testid="locked-account-row">
            <span className="font-bold">{u.user_id || u.email}</span>
            <span>· {u.office || (u.role === "admin" ? "All Branches" : "—")}</span>
            <span>· locked until {new Date(u.locked_until).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
            <button
              type="button"
              className="ml-auto cursor-pointer rounded-full bg-red-600 px-2.5 py-0.5 font-bold text-white hover:bg-red-700"
              onClick={() => unlock(u)}
              data-testid="unlock-account-btn"
            >
              Unlock Now
            </button>
            <button
              type="button"
              className="cursor-pointer rounded-full border border-emerald-300 bg-white px-2.5 py-0.5 font-bold text-emerald-700 hover:bg-emerald-50"
              title={managerFor(u.office || "Chennai") ? `WhatsApp the ${u.office || "Chennai"} branch manager` : "No manager mobile saved — opens WhatsApp chooser"}
              onClick={() => {
                const branch = u.office || "Chennai";
                const until = u.locked_until ? new Date(u.locked_until).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";
                const msg = `NTC Alert — ${branch} branch\nStaff account *${u.user_id || u.email}* is LOCKED${until ? ` until ${until}` : ""} after repeated wrong sign-in attempts.\nPlease check with the staff member. Admin can unlock instantly from Dashboard → Locked Accounts.`;
                window.open(`https://wa.me/${managerFor(branch)}?text=${encodeURIComponent(msg)}`, "_blank");
              }}
              data-testid="locked-manager-wa-btn"
            >
              WA Manager
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VendorDuesAlert() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [pay, setPay] = useState(null);
  const [payForm, setPayForm] = useState({ date: "", amount: "", payment_mode: "Cash", ref_no: "" });
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/reports/vendor-ledger").then((r) => {
      const list = (r.data?.rows || [])
        .filter((x) => Number(x.balance) > 0)
        .sort((a, b) => Number(b.balance) - Number(a.balance));
      setRows(list);
    }).catch(() => {});
  };
  useEffect(load, []);
  const openPay = (x) => {
    setPay(x);
    setPayForm({ date: today(), amount: String(x.balance || ""), payment_mode: "Cash", ref_no: "" });
  };
  const savePay = async () => {
    if (!Number(payForm.amount)) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.post("/vendor-payments", { vendor_name: pay.vendor, date: payForm.date, amount: Number(payForm.amount), payment_mode: payForm.payment_mode, ref_no: payForm.ref_no, narration: "Settled from dashboard Top Dues" });
      toast.success(`Payment of ${inr(payForm.amount)} recorded for ${pay.vendor}`);
      try {
        const vres = await api.get("/vendors");
        const vend = (vres.data || []).find((v) => String(v.name || "").trim().toLowerCase() === String(pay.vendor || "").trim().toLowerCase());
        const ph = String(vend?.phone || "").replace(/\D/g, "");
        if (ph) {
          const remaining = Math.max(0, (Number(pay.balance) || 0) - Number(payForm.amount));
          const msg = [
            "*NTC — Payment Confirmation*",
            `Dear ${vend.contact_person || pay.vendor},`,
            "",
            `We have made a payment of ${inr(payForm.amount)} on ${fmtDate(payForm.date)} via ${payForm.payment_mode}${payForm.ref_no ? ` (Ref: ${payForm.ref_no})` : ""}.`,
            ...(vend.bank_name || vend.account_holder || vend.account_no ? [
              "",
              "*Paid to your bank on record:*",
              ...(vend.bank_name ? [`Bank: ${vend.bank_name}`] : []),
              ...(vend.account_holder ? [`Account Holder: ${vend.account_holder}`] : []),
              ...(vend.account_no ? [`A/c No.: ${vend.account_no}`] : []),
              ...(vend.ifsc ? [`IFSC: ${vend.ifsc}`] : []),
              "",
            ] : []),
            `Remaining balance: ${inr(remaining)}`,
            "",
            "— Nisha Transport Corporation",
          ].join("\n");
          window.open(`https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}`, "_blank");
        } else {
          toast.info(`No mobile saved for ${pay.vendor} in Vendor Master — WhatsApp receipt skipped`);
        }
      } catch { /* wa receipt is best-effort */ }
      setPay(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  if (!rows.length) return null;
  const total = rows.reduce((s, x) => s + (Number(x.balance) || 0), 0);
  return (
    <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50/70 px-4 py-3" data-testid="vendor-dues-alert">
      <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
        <DialogContent className="sm:max-w-[380px]" data-testid="dues-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Record Payment — {pay?.vendor}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Date</span>
              <Input type="date" value={payForm.date} onChange={(e) => setPayForm((f) => ({ ...f, date: e.target.value }))} data-testid="dues-pay-date" />
            </div>
            <div className="grid gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Amount (₹)</span>
              <Input type="number" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} data-testid="dues-pay-amount" />
            </div>
            <div className="grid gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Mode</span>
              <Select value={payForm.payment_mode} onValueChange={(v) => setPayForm((f) => ({ ...f, payment_mode: v }))}>
                <SelectTrigger data-testid="dues-pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Cash", "NEFT/RTGS", "Cheque", "DD", "UPI"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <span className="text-xs font-semibold text-muted-foreground">Ref / UTR No.</span>
              <Input value={payForm.ref_no} onChange={(e) => setPayForm((f) => ({ ...f, ref_no: e.target.value }))} placeholder="Optional" data-testid="dues-pay-ref" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setPay(null)}>Cancel</Button>
            <Button size="sm" onClick={savePay} disabled={saving} data-testid="dues-pay-save">
              {saving ? "Saving…" : "Record Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex items-center gap-2">
        <IndianRupee className="h-4 w-4 text-orange-600" />
        <span className="text-sm font-bold text-orange-800">Top Vendor Dues ({rows.length} · {inr(total)} payable)</span>
        <button
          type="button"
          onClick={async () => {
            try {
              const r = await api.get("/reports/dues-digest");
              const d = r.data || {};
              const line = (x, i) => `${i + 1}. ${x.name}: ₹${Number(x.due).toLocaleString("en-IN")}`;
              const msg = [
                "*NTC — Weekly Dues Digest*",
                `_As on ${new Date().toLocaleDateString("en-IN")}_`,
                "",
                `*Top Customer Dues* (₹${Number(d.customer_total || 0).toLocaleString("en-IN")} across ${d.customer_count || 0})`,
                ...(d.customers || []).map(line),
                "",
                `*Top Vendor Dues* (₹${Number(d.vendor_total || 0).toLocaleString("en-IN")} across ${d.vendor_count || 0})`,
                ...(d.vendors || []).map(line),
                "",
                "— Nisha Transport Corporation",
              ].join("\n");
              window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
            } catch {
              toast.error("Could not build dues digest");
            }
          }}
          className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-bold text-white hover:bg-emerald-700"
          data-testid="weekly-digest-whatsapp-btn"
        >
          WhatsApp Digest
        </button>
        <button
          type="button"
          onClick={() => navigate("/vendor-ledger")}
          className="ml-auto text-xs font-semibold text-orange-700 underline-offset-2 hover:underline"
          data-testid="vendor-dues-open-ledger"
        >
          Open Vendor Ledger →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {rows.slice(0, 5).map((x) => (
          <div key={x.vendor} className="flex flex-wrap items-center gap-x-2 text-xs text-orange-700" data-testid="vendor-dues-row">
            <button
              type="button"
              className="cursor-pointer font-bold hover:underline"
              onClick={() => navigate(`/vendor-ledger?vendor=${encodeURIComponent(x.vendor)}`)}
              data-testid="vendor-dues-name"
            >
              {x.vendor}
            </button>
            <span>· {x.challans} challan{x.challans === 1 ? "" : "s"}</span>
            <span className="ml-auto rounded-full bg-orange-600 px-2 py-0.5 font-bold text-white">{inr(x.balance)} due</span>
            <button
              type="button"
              className="cursor-pointer rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white hover:bg-emerald-700"
              title={`Record a payment to ${x.vendor}`}
              onClick={() => openPay(x)}
              data-testid="dues-pay-btn"
            >
              Pay
            </button>
          </div>
        ))}
        {rows.length > 5 && <div className="text-xs text-orange-600">+{rows.length - 5} more…</div>}
      </div>
    </div>
  );
}

function WeeklyPodScorecard() {
  const managerFor = useBranchManagers();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/shipments").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  if (!rows) return null;
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
  const recent = rows.filter((r) => r.pod_path && String(r.pod_uploaded_at || "") >= weekAgo);
  if (!recent.length) return null;
  const stats = {};
  recent.forEach((r) => {
    const b = r.office || r.office_name || "—";
    stats[b] = stats[b] || { n: 0, days: 0 };
    stats[b].n += 1;
    const lr = r.lr_date || r.dispatch_date;
    if (lr) stats[b].days += Math.max((new Date(String(r.pod_uploaded_at).slice(0, 10)) - new Date(lr)) / 86400000, 0);
  });
  const pending = {};
  rows.filter((r) => !r.pod_path).forEach((r) => {
    const b = r.office || r.office_name || "—";
    pending[b] = (pending[b] || 0) + 1;
  });
  const list = Object.entries(stats)
    .map(([b, s]) => ({ b, n: s.n, avg: s.n ? s.days / s.n : 0, pend: pending[b] || 0 }))
    .sort((a, x) => x.avg - a.avg);
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="weekly-pod-scorecard">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-800">
        Weekly POD Scorecard <span className="text-xs font-medium text-muted-foreground">— upload speed last 7 days (slowest first)</span>
        {new Date().getDay() === 1 && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="scorecard-monday-badge">Monday — send to managers now</span>}
      </div>
      <div className="mt-2 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
        {list.map((s) => (
          <div key={s.b} className={`rounded-md border px-3 py-2 text-xs ${s.avg > 3 ? "border-red-200 bg-red-50/60" : "border-emerald-200 bg-emerald-50/50"}`} data-testid={`pod-score-${String(s.b).toLowerCase()}`}>
            <div className="flex items-center font-bold text-slate-800">
              {s.b}
              <button
                type="button"
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50"
                title="WhatsApp this branch's weekly scorecard to its manager"
                onClick={() => {
                  const msg = [
                    `*NTC — Weekly POD Scorecard (${s.b})*`,
                    `Last 7 days: ${s.n} POD${s.n > 1 ? "s" : ""} uploaded · avg ${s.avg.toFixed(1)} day(s) after LR.`,
                    `Pending PODs: ${s.pend}`,
                    "",
                    s.avg > 3 ? "⚠ Above the 3-day target — please upload signed PODs faster this week." : "✅ Within the 3-day target — keep it up!",
                    "— NTC Admin",
                  ].join("\n");
                  window.open(`https://wa.me/${managerFor(s.b)}?text=${encodeURIComponent(msg)}`, "_blank");
                }}
                data-testid={`pod-score-wa-${String(s.b).toLowerCase()}`}
              >
                <MessageCircle className="h-3 w-3" /> WA
              </button>
            </div>
            <div className="mt-0.5 text-slate-600">{s.n} POD{s.n > 1 ? "s" : ""} uploaded · avg <b className={s.avg > 3 ? "text-red-600" : "text-emerald-700"}>{s.avg.toFixed(1)} day{s.avg.toFixed(1) === "1.0" ? "" : "s"}</b> after LR</div>
            {s.pend > 0 && <div className="mt-0.5 font-semibold text-amber-700">{s.pend} still pending</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function MorningOpeningNudge() {
  const managerFor = useBranchManagers();
  const [missing, setMissing] = useState([]);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  const yday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  useEffect(() => {
    api.get("/daybook", { params: { date: yday } }).then((r) => {
      const have = new Set((r.data?.entries || []).map((e) => e.office).filter(Boolean));
      setMissing(OFFICES.filter((b) => !have.has(b)));
    }).catch(() => {});
  }, [yday]);
  if (now.getHours() < 10 || now.getHours() >= 14 || !missing.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50/70 px-4 py-3" data-testid="morning-opening-nudge">
      <div className="flex flex-wrap items-center gap-2">
        <BookOpen className="h-4 w-4 text-sky-700" />
        <span className="text-sm font-bold text-sky-800">10am check — yesterday's Day Book was never closed in {missing.length} branch{missing.length > 1 ? "es" : ""}</span>
        <span className="text-xs text-sky-700">remind them before today's entries pile up</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {missing.map((b) => (
          <Button
            key={b}
            size="sm"
            variant="outline"
            className="h-7 border-sky-300 bg-white px-2.5 text-xs font-bold text-sky-700 hover:bg-sky-100"
            onClick={() => {
              const msg = [
                `*NTC — Day Book Opening Reminder (${b})*`,
                `Dear Manager, yesterday's (${yday}) Day Book was never closed — it has NO entries.`,
                "Please enter yesterday's receipts and payments first, then start today's book.",
                "— NTC Admin",
              ].join("\n");
              window.open(`https://wa.me/${managerFor(b)}?text=${encodeURIComponent(msg)}`, "_blank");
            }}
            data-testid={`morning-nudge-${b.toLowerCase()}`}
          >
            <MessageCircle className="mr-1 h-3.5 w-3.5" /> {b}
          </Button>
        ))}
      </div>
    </div>
  );
}

function EveningDayBookNudge() {
  const managerFor = useBranchManagers();
  const [missing, setMissing] = useState([]);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get("/daybook", { params: { date: today } }).then((r) => {
      const have = new Set((r.data?.entries || []).map((e) => e.office).filter(Boolean));
      setMissing(OFFICES.filter((b) => !have.has(b)));
    }).catch(() => {});
  }, []);
  if (now.getHours() < 19 || !missing.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50/70 px-4 py-3" data-testid="evening-daybook-nudge">
      <div className="flex flex-wrap items-center gap-2">
        <BookOpen className="h-4 w-4 text-orange-700" />
        <span className="text-sm font-bold text-orange-800">7pm check — {missing.length} branch{missing.length > 1 ? "es have" : " has"} no Day Book entries today</span>
        <span className="text-xs text-orange-700">nudge them on WhatsApp before close of day</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {missing.map((b) => (
          <Button
            key={b}
            size="sm"
            variant="outline"
            className="h-7 border-orange-300 bg-white px-2.5 text-xs font-bold text-orange-700 hover:bg-orange-100"
            onClick={() => {
              const msg = [
                `*NTC — Day Book Reminder (${b})*`,
                `Dear Manager, today's (${today}) Day Book has NO entries yet.`,
                "Please enter the day's receipts and payments and close the book before leaving.",
                "— NTC Admin",
              ].join("\n");
              window.open(`https://wa.me/${managerFor(b)}?text=${encodeURIComponent(msg)}`, "_blank");
            }}
            data-testid={`evening-nudge-${b.toLowerCase()}`}
          >
            <MessageCircle className="mr-1 h-3.5 w-3.5" /> {b}
          </Button>
        ))}
      </div>
    </div>
  );
}

function DaySettlementCard() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      setList((r.data || []).filter((c) => c.settled && String(c.settled_at || "").startsWith(today)));
    }).catch(() => {});
  }, []);
  if (!list.length) return null;
  const amt = (c) => (c.payments || []).filter((p) => p.settlement).reduce((a, p) => a + (Number(p.amount) || 0), 0) || Number(c.balance) || 0;
  const total = list.reduce((a, c) => a + amt(c), 0);
  return (
    <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3" data-testid="day-settlement-card">
      <div className="flex items-center gap-2">
        <IndianRupee className="h-4 w-4 text-emerald-700" />
        <span className="text-sm font-bold text-emerald-800">Settled Today — {inr(total)} cash ({list.length} challan{list.length > 1 ? "s" : ""})</span>
        <span className="text-xs text-emerald-700">match this with the evening cash count</span>
        <button
          type="button"
          onClick={() => navigate("/hire-challans")}
          className="ml-auto text-xs font-semibold text-emerald-700 underline-offset-2 hover:underline"
          data-testid="day-settlement-open-btn"
        >
          Open Hire Challans →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {list.slice(0, 6).map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-x-2 text-xs text-emerald-800" data-testid="day-settlement-row">
            <span className="font-bold">{String(c.challan_no || "").replace(/^VHC[-\s]?/i, "")}</span>
            <span>· {c.vehicle_no || "—"}</span>
            <span>· {c.vendor_broker_name || c.owner_name || "—"}</span>
            <span>· by {c.settled_by || "—"}</span>
            <span className="ml-auto rounded-full bg-emerald-600 px-2 py-0.5 font-bold text-white">{inr(amt(c))}</span>
          </div>
        ))}
        {list.length > 6 && <div className="text-xs text-emerald-700">+{list.length - 6} more…</div>}
      </div>
    </div>
  );
}

function InsuranceAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/hire-challans").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const byVeh = {};
      (r.data || []).forEach((c) => {
        const d = c.ins_valid_upto;
        if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return;
        const k = c.vehicle_no || c.id;
        if (!byVeh[k] || d > byVeh[k].ins_valid_upto) byVeh[k] = c;
      });
      const list = Object.values(byVeh)
        .filter((c) => (new Date(c.ins_valid_upto) - new Date(today)) / 86400000 <= 30)
        .sort((a, b) => (a.ins_valid_upto < b.ins_valid_upto ? -1 : 1));
      setItems(list);
    }).catch(() => {});
  }, []);
  if (!items.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50/70 px-4 py-3" data-testid="insurance-dashboard-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <span className="text-sm font-bold text-red-800">Vehicle Insurance Alerts ({items.length})</span>
        <button
          type="button"
          onClick={() => navigate("/hire-challans")}
          className="ml-auto text-xs font-semibold text-red-700 underline-offset-2 hover:underline"
          data-testid="insurance-alert-open-challans"
        >
          Open Hire Challans →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((c) => {
          const days = Math.round((new Date(c.ins_valid_upto) - new Date(today)) / 86400000);
          return (
            <div key={c.id} className="flex flex-wrap items-center gap-x-2 text-xs text-red-700" data-testid="insurance-alert-row">
              <span className="font-bold">{c.vehicle_no || "—"}</span>
              <span>· Policy {c.policy_no || "—"}</span>
              <span>· {c.engaged_office || c.office || "Chennai"}</span>
              <span className={`ml-auto rounded-full px-2 py-0.5 font-bold ${days < 0 ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                {days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? "Expires today" : `Expires in ${days}d`} · {fmtDate(c.ins_valid_upto)}
              </span>
            </div>
          );
        })}
        {items.length > 6 && <div className="text-xs text-red-600">+{items.length - 6} more…</div>}
      </div>
    </div>
  );
}

function ExpiringRatesAlert() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [custs, setCusts] = useState([]);
  const [renewTarget, setRenewTarget] = useState(null);
  const [hike, setHike] = useState("");
  useEffect(() => {
    api.get("/freight-master").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const list = (r.data || [])
        .filter((f) => f.valid_till && (new Date(f.valid_till) - new Date(today)) / 86400000 <= 15 && (new Date(f.valid_till) - new Date(today)) / 86400000 >= -30)
        .sort((a, b) => (a.valid_till < b.valid_till ? -1 : 1));
      setItems(list);
    }).catch(() => {});
    api.get("/customers").then((r) => setCusts(r.data || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const renew = async (f, pct = 0) => {
    try {
      const d = new Date(today);
      d.setFullYear(d.getFullYear() + 1);
      const till = d.toISOString().slice(0, 10);
      const oldAmt = Number(f.freight_amount) || 0;
      const newAmt = Math.round(oldAmt * (1 + (Number(pct) || 0) / 100));
      await api.post("/freight-master", {
        customer_name: f.customer_name,
        branch: f.branch || "",
        origin: f.origin || "",
        destination: f.destination || "",
        freight_amount: newAmt,
        loading_charge: f.loading_charge || 0,
        unloading_charge: f.unloading_charge || 0,
        unit: f.unit || "",
        valid_from: today,
        valid_till: till,
        remarks: f.remarks || "",
      });
      setItems((prev) => prev.filter((x) => x.id !== f.id));
      toast.success(`Rate renewed at ₹${newAmt.toLocaleString("en-IN")}${Number(pct) ? ` (+${pct}%)` : ""} till ${fmtDate(till)}.`);
      const cust = custs.find((c) => c.name === f.customer_name);
      const msg = [
        "*Nisha Transport Corporation — Freight Rate Renewed*",
        "",
        `Customer: ${f.customer_name}${cust?.customer_code ? ` (${cust.customer_code})` : ""}`,
        `Route: ${f.origin || "—"} → ${f.destination || "—"}`,
        `Freight Charge: ₹${newAmt.toLocaleString("en-IN")}${f.unit ? ` (${f.unit})` : ""}${Number(pct) ? ` — revised +${pct}% from ₹${oldAmt.toLocaleString("en-IN")}` : ""}`,
        `Valid: ${fmtDate(today)} → ${fmtDate(till)}`,
        "",
        "Thank you for your continued business!",
      ].join("\n");
      const ph = String(cust?.phone || "").replace(/\D/g, "");
      window.open(ph ? `https://wa.me/91${ph.slice(-10)}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch {
      toast.error("Could not renew rate");
    }
  };
  return (
    <>
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50/70 px-4 py-3" data-testid="expiring-rates-alert">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-bold text-amber-800">Freight Rates Expiring Soon ({items.length})</span>
        <button
          type="button"
          onClick={() => navigate("/freight-master")}
          className="ml-auto text-xs font-semibold text-amber-700 underline-offset-2 hover:underline"
          data-testid="expiring-rates-open-btn"
        >
          Open Freight Master →
        </button>
      </div>
      <div className="mt-2 space-y-1">
        {items.slice(0, 6).map((f) => {
          const days = Math.round((new Date(f.valid_till) - new Date(today)) / 86400000);
          return (
            <div key={f.id} className="flex flex-wrap items-center gap-x-2 text-xs text-amber-800" data-testid="expiring-rate-row">
              <span className="font-bold">{f.customer_name || "—"}</span>
              <span>· {f.origin || "—"} → {f.destination || "—"}</span>
              <span>· {inr(f.freight_amount || 0)}{f.unit ? ` (${f.unit})` : ""}</span>
              <span className={`ml-auto rounded-full px-2 py-0.5 font-bold text-white ${days < 0 ? "bg-red-600" : "bg-amber-500"}`}>
                {days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? "Expires today" : `Expires in ${days}d`} · {fmtDate(f.valid_till)}
              </span>
              <button
                type="button"
                onClick={() => {
                  setHike("");
                  setRenewTarget(f);
                }}
                className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-white px-2 py-0.5 font-bold text-amber-700 hover:bg-amber-100"
                title="Copy this rate with fresh 1-year validity from today"
                data-testid="expiring-rate-renew-btn"
              >
                <RotateCcw className="h-3 w-3" /> Renew
              </button>
            </div>
          );
        })}
        {items.length > 6 && <div className="text-xs text-amber-700">+{items.length - 6} more…</div>}
      </div>
    </div>
    <Dialog open={!!renewTarget} onOpenChange={(o) => !o && setRenewTarget(null)}>
      <DialogContent className="sm:max-w-[420px]" data-testid="dash-renew-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Renew Freight Rate</DialogTitle>
        </DialogHeader>
        {renewTarget && (
          <div className="grid gap-3 py-1">
            <div className="rounded-md border border-border bg-slate-50 p-3 text-sm">
              <div className="font-bold">{renewTarget.customer_name}</div>
              <div className="text-xs text-muted-foreground">
                {renewTarget.origin || "—"} → {renewTarget.destination || "—"}
                {renewTarget.unit ? ` · ${renewTarget.unit}` : ""}
              </div>
              <div className="mt-1 text-xs">Current charge: <b>{inr(renewTarget.freight_amount || 0)}</b></div>
            </div>
            <div className="grid gap-1.5">
              <span className="text-sm font-medium">Rate hike % (optional)</span>
              <Input type="number" placeholder="e.g. 5" value={hike} onChange={(e) => setHike(e.target.value)} data-testid="dash-renew-hike-input" />
              <p className="text-xs text-muted-foreground">
                New charge: <b data-testid="dash-renew-new-charge">{inr(Math.round((Number(renewTarget.freight_amount) || 0) * (1 + (Number(hike) || 0) / 100)))}</b>
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setRenewTarget(null)}>Cancel</Button>
              <Button
                size="sm"
                onClick={async () => {
                  const t = renewTarget;
                  setRenewTarget(null);
                  await renew(t, hike);
                }}
                data-testid="dash-renew-confirm-btn"
              >
                Renew &amp; WhatsApp
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

function BreakdownAlerts() {
  const [rows, setRows] = useState([]);
  const [fleetMgr, setFleetMgr] = useState(null);
  const [contacts, setContacts] = useState({});
  useEffect(() => {
    api.get("/shipments").then((r) => setRows((r.data || []).filter((s) => s.status === "Vehicle Break down"))).catch(() => {});
    api.get("/company-profile").then((r) => setFleetMgr({ name: r.data?.fleet_manager_name || "", phone: r.data?.fleet_manager_whatsapp || "" })).catch(() => {});
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
  }, []);
  if (!rows.length) return null;
  const branchTarget = () => {
    const branches = [...new Set(rows.map((s) => s.branch || s.office).filter(Boolean))];
    return branches.length === 1 ? contacts[branches[0]] || "" : "";
  };
  const shareWhatsApp = () => {
    const lines = rows.map((s, i) => {
      const lr = String(s.lr_no || s.consignment_no || "—").replace(/^CN-/, "");
      return `${i + 1}. LR ${lr}${s.vehicle_no ? ` — ${s.vehicle_no}` : ""} (${s.origin || "—"} → ${s.destination || "—"})${s.driver_mobile ? ` — Driver: ${s.driver_mobile}` : ""}`;
    });
    const msg = `🔴 NTC VEHICLE BREAK DOWN ALERT (${fmtDate(new Date().toISOString().slice(0, 10))})\n\n${rows.length} consignment(s) affected:\n\n${lines.join("\n")}\n\nPlease arrange recovery/replacement vehicle at the earliest.\n\n— Nisha Transport Corporation`;
    const digits = String(fleetMgr?.phone || "").replace(/\D/g, "");
    const target = digits ? (digits.length === 10 ? "91" + digits : digits) : branchTarget();
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const hasFleetMgr = Boolean(String(fleetMgr?.phone || "").replace(/\D/g, ""));
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-4" data-testid="breakdown-alerts">
      <div className="mb-2 flex items-center gap-2 text-sm font-bold text-red-700">
        <AlertTriangle className="h-4 w-4" /> Vehicle Break down — {rows.length} consignment{rows.length > 1 ? "s" : ""} affected
        <span className="ml-1 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1.5 text-[11px] font-bold text-white" data-testid="breakdown-count">{rows.length}</span>
        <Button variant="outline" size="sm" className="ml-auto h-7 bg-white text-xs text-emerald-600" onClick={shareWhatsApp} data-testid="breakdown-whatsapp-btn">
          <MessageCircle className="mr-1 h-3 w-3" /> {hasFleetMgr ? `WhatsApp ${fleetMgr.name || "Fleet Manager"}` : "WhatsApp"}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.map((s) => (
          <span key={s.id} className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700" data-testid="breakdown-chip">
            <b>LR {String(s.lr_no || s.consignment_no || "—").replace(/^CN-/, "")}</b>
            {s.vehicle_no ? ` · ${s.vehicle_no}` : ""} · {s.origin || "—"} → {s.destination || "—"}
            {s.driver_mobile ? ` · Driver ${s.driver_mobile}` : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

function LostPadAlert() {
  const [lost, setLost] = useState([]);
  const [contacts, setContacts] = useState({});
  useEffect(() => {
    Promise.all([api.get("/app-settings"), api.get("/pad-issues"), api.get("/branch-contacts")])
      .then(([s, p, c]) => {
        setContacts(c.data || {});
        const days = Number(s.data?.lost_pad_days) || 7;
        const cutoff = Date.now() - days * 86400000;
        setLost(
          (p.data || []).filter(
            (r) => !r.voided && r.serial_count > 0 && r.used_count === 0 && r.created_at && new Date(r.created_at).getTime() < cutoff
          ).map((r) => ({ ...r, days }))
        );
      })
      .catch(() => {});
  }, []);
  if (!lost.length) return null;
  const shareWhatsApp = () => {
    const lines = [
      `⚠ *NTC — Possible Lost Pads (${lost.length})*`,
      `Blank serial ranges unused for over ${lost[0].days} days:`,
      "",
      ...lost.slice(0, 15).map((r) => `• ${r.doc_type} ${r.start_no}${r.end_no && r.end_no !== r.start_no ? `–${r.end_no}` : ""} (${r.count} nos) — issued to ${r.issued_by || "—"}, ${r.office || "—"} on ${fmtDate(r.created_at)}`),
      lost.length > 15 ? `…and ${lost.length - 15} more` : "",
      "",
      "Please confirm these pads or report them lost.",
    ].filter((l) => l !== "");
    const branches = [...new Set(lost.map((r) => r.office).filter(Boolean))];
    const num = branches.length === 1 ? contacts[branches[0]] : "";
    window.open(`https://wa.me/${num || ""}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-4" data-testid="lost-pad-alert">
      <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-red-800">
        <AlertTriangle className="h-4 w-4" /> Possible lost pads — {lost.length} blank serial range{lost.length > 1 ? "s" : ""} unused for over {lost[0].days} days
        <span className="ml-1 grid h-5 min-w-5 place-items-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white" data-testid="lost-pad-count">{lost.length}</span>
        <button onClick={shareWhatsApp} className="ml-auto flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700" data-testid="lost-pad-whatsapp-btn">
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Branch
        </button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-xs font-medium text-red-700">
        {lost.slice(0, 6).map((r) => (
          <span key={r.id} className="rounded-md border border-red-200 bg-white px-2.5 py-1" data-testid="lost-pad-item">
            {r.doc_type} {r.start_no}{r.end_no && r.end_no !== r.start_no ? `–${r.end_no}` : ""} · {r.issued_by || "—"} ({r.office || "—"}, {fmtDate(r.created_at)})
          </span>
        ))}
        {lost.length > 6 && <span className="px-1 py-1">…and {lost.length - 6} more</span>}
      </div>
    </div>
  );
}

function ProofReminder() {
  const [counts, setCounts] = useState(null);
  const [contacts, setContacts] = useState({});
  useEffect(() => {
    Promise.all([api.get("/app-settings"), api.get("/vouchers"), api.get("/expenses")])
      .then(([s, v, e]) => {
        const limit = Number(s.data?.proof_alert_limit) || 5000;
        const miss = (list) => (list || []).filter((r) => !r.proof_path && Number(r.amount) >= limit);
        setCounts({ limit, vList: miss(v.data), eList: miss(e.data) });
      })
      .catch(() => {});
    api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {});
  }, []);
  if (!counts || counts.vList.length + counts.eList.length === 0) return null;
  const total = counts.vList.length + counts.eList.length;
  const shareWhatsApp = () => {
    const amt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
    const lines = [
      `⚠ *NTC — Bill Proof Missing (${total})*`,
      `Records of ${amt(counts.limit)}+ without a bill photo:`,
      "",
      ...counts.vList.slice(0, 10).map((r) => `• Voucher ${String(r.voucher_no || "").replace(/^DV[-\s]?/i, "")} — ${r.party_name || "—"} — ${amt(r.amount)} (${r.branch || r.office || "Chennai"})`),
      ...counts.eList.slice(0, 10).map((r) => `• Expense ${r.category || ""} — ${r.description || "—"} — ${amt(r.amount)} (${r.office || "Chennai"})`),
      total > 20 ? `…and ${total - 20} more` : "",
      "",
      "Please attach the bill photos.",
    ].filter((l) => l !== "");
    const branches = [...new Set([...counts.vList.map((r) => r.branch || r.office), ...counts.eList.map((r) => r.office)].filter(Boolean))];
    const num = branches.length === 1 ? contacts[branches[0]] || "" : "";
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="proof-reminder">
      <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-amber-800">
        <AlertTriangle className="h-4 w-4" /> Bill proof missing — {total} record{total > 1 ? "s" : ""} of ₹{counts.limit.toLocaleString("en-IN")}+ without a bill photo
        <span className="ml-1 grid h-5 min-w-5 place-items-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white" data-testid="proof-reminder-count">{total}</span>
        <span className="ml-auto flex gap-2 text-xs font-medium">
          <button onClick={shareWhatsApp} className="flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-white hover:bg-emerald-700" data-testid="proof-reminder-whatsapp-btn">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </button>
          {counts.vList.length > 0 && (
            <Link to="/vouchers" className="rounded-md border border-amber-200 bg-white px-2.5 py-1 text-amber-800 hover:underline" data-testid="proof-reminder-vouchers-link">
              {counts.vList.length} voucher{counts.vList.length > 1 ? "s" : ""}
            </Link>
          )}
          {counts.eList.length > 0 && (
            <Link to="/expenses" className="rounded-md border border-amber-200 bg-white px-2.5 py-1 text-amber-800 hover:underline" data-testid="proof-reminder-expenses-link">
              {counts.eList.length} expense{counts.eList.length > 1 ? "s" : ""}
            </Link>
          )}
        </span>
      </div>
    </div>
  );
}

const KYC_REQ = ["PAN Card", "GST Certificate", "Cancelled Cheque"];

function VendorNudges() {
  const [vendors, setVendors] = useState(null);
  const [vstats, setVstats] = useState({});
  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    api.get("/vendors/stats").then((r) => setVstats(r.data || {})).catch(() => {});
  }, []);
  if (!vendors || !vendors.length) return null;
  const waOpen = (phone, msg) => {
    const d = String(phone || "").replace(/\D/g, "");
    window.open(d ? `https://wa.me/${d.length === 10 ? "91" + d : d}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const gaps = vendors
    .map((v) => ({ v, missing: KYC_REQ.filter((l) => !(v.vendor_docs || []).some((d) => d.label === l)) }))
    .filter((x) => x.missing.length > 0);
  const dues = vendors
    .map((v) => ({ v, s: vstats[v.id] }))
    .filter((x) => x.s && (x.s.hire_due || 0) > 0)
    .sort((a, b) => (b.s.hire_due || 0) - (a.s.hire_due || 0));
  const isMonday = new Date().getDay() === 1;
  return (
    <>
      {gaps.length > 0 && (
        <Card className="mb-4 border-amber-300 bg-amber-50/60" data-testid="kyc-gap-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-700" />
              <span className="font-semibold text-amber-900">Vendor KYC gaps</span>
              <span className="text-xs text-amber-700">{gaps.length} vendor{gaps.length > 1 ? "s" : ""} missing documents</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {gaps.slice(0, 8).map(({ v, missing }) => (
                <span key={v.id} className="flex items-center gap-1.5 rounded-md border border-amber-200 bg-white px-2 py-1 text-xs" data-testid="kyc-gap-row">
                  <b>{v.name}</b>
                  <span className="text-slate-500">missing {missing.join(", ")}</span>
                  <button
                    type="button"
                    className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700"
                    onClick={() => waOpen(v.phone, [
                      `Dear ${v.contact_person || v.name},`,
                      "",
                      "To complete your vendor KYC with Nisha Transport Corporation, please WhatsApp us the following document(s):",
                      ...missing.map((m, i) => `${i + 1}. ${m}`),
                      "",
                      "This helps us process your payments without delay. Thank you!",
                      "— Nisha Transport Corporation",
                    ].join("\n"))}
                    data-testid="kyc-gap-wa-btn"
                  >
                    WhatsApp
                  </button>
                </span>
              ))}
              {gaps.length > 8 && <span className="self-center text-xs text-amber-700">+{gaps.length - 8} more in Vendor Master</span>}
            </div>
          </CardContent>
        </Card>
      )}
      {dues.length > 0 && (
        <Card className={`mb-4 ${isMonday ? "border-emerald-400 bg-emerald-50/70" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="hire-due-reminder-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-emerald-700" />
              <span className="font-semibold text-emerald-900">Hire due reminders</span>
              {isMonday && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="monday-badge">It's Monday — send this week's reminders</span>}
              <span className="text-xs text-emerald-700">{dues.length} vendor{dues.length > 1 ? "s" : ""} with pending hire balance</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {dues.slice(0, 8).map(({ v, s }) => (
                <span key={v.id} className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-white px-2 py-1 text-xs" data-testid="hire-due-row">
                  <b>{v.name}</b>
                  <span className="font-semibold text-red-600">{inr(s.hire_due)} due</span>
                  <button
                    type="button"
                    className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700"
                    onClick={() => waOpen(v.phone, [
                      "*NTC — Weekly Hire Balance Reminder*",
                      "",
                      `Dear ${v.contact_person || v.name}${v.vendor_code ? ` (${v.vendor_code})` : ""},`,
                      `Your pending vehicle hire balance with Nisha Transport Corporation is *${inr(s.hire_due)}*`,
                      `(Total hire ${inr(s.hire_total)} − Paid ${inr(s.payments_total)}).`,
                      "",
                      "Please share any pending bills / PODs so settlement can be processed this week.",
                      "— Nisha Transport Corporation",
                    ].join("\n"))}
                    data-testid="hire-due-wa-btn"
                  >
                    WhatsApp
                  </button>
                </span>
              ))}
              {dues.length > 8 && <span className="self-center text-xs text-emerald-700">+{dues.length - 8} more in Vendor Master</span>}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function ChangeRequestsCard() {
  const [reqs, setReqs] = useState(null);
  const branchPhone = useBranchManagers();
  useEffect(() => {
    api.get("/change-requests").then((r) => setReqs((r.data || []).filter((x) => x.status === "Pending"))).catch(() => {});
  }, []);
  if (!reqs || !reqs.length) return null;
  const decide = async (cr, approve) => {
    try {
      await api.post(`/change-requests/${cr.id}/decide`, { approve });
      setReqs((list) => list.filter((x) => x.id !== cr.id));
      toast.success(approve ? `Approved — ${cr.field_label} on LR ${cr.lr_no} updated to ${cr.new_value}` : "Request rejected");
      const ph = branchPhone(cr.office);
      const msg = [
        `*NTC — Correction Request ${approve ? "APPROVED ✔" : "REJECTED ✖"}*`,
        "",
        `LR ${cr.lr_no} — ${cr.customer_name || ""}`,
        `${cr.field_label}: ${cr.old_value || "—"} → ${cr.new_value}`,
        cr.reason ? `Reason given: ${cr.reason}` : "",
        `Requested by: ${cr.requested_by} (${cr.office})`,
        "",
        approve ? "The consignment has been updated accordingly." : "The consignment remains unchanged. Please contact admin for details.",
        "— NTC Admin",
      ].filter((l) => l !== "").join("\n");
      window.open(ph ? `https://wa.me/${ph}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <Card className="mb-4 border-blue-300 bg-blue-50/60" data-testid="change-requests-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <Pencil className="h-5 w-5 text-blue-700" />
          <span className="font-semibold text-blue-900">Correction requests from branches</span>
          <span className="text-xs text-blue-700">{reqs.length} pending approval</span>
        </div>
        <div className="mt-3 space-y-2">
          {reqs.slice(0, 6).map((cr) => (
            <div key={cr.id} className="flex flex-wrap items-center gap-2 rounded-md border border-blue-200 bg-white px-2.5 py-1.5 text-xs" data-testid="change-request-row">
              <b>LR {cr.lr_no}</b>
              <span className="text-slate-500">{cr.customer_name}</span>
              <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800">{cr.field_label}</span>
              <span className="text-slate-600">{cr.old_value || "—"} → <b className="text-slate-900">{cr.new_value}</b></span>
              {cr.reason && <span className="italic text-slate-500">"{cr.reason}"</span>}
              <span className="text-slate-400">by {cr.requested_by} · {cr.office}</span>
              <span className="ml-auto flex gap-1.5">
                <button type="button" className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700" onClick={() => decide(cr, true)} data-testid="cr-approve-btn">Approve</button>
                <button type="button" className="rounded-full bg-red-600 px-2.5 py-0.5 text-[10px] font-semibold text-white hover:bg-red-700" onClick={() => decide(cr, false)} data-testid="cr-reject-btn">Reject</button>
              </span>
            </div>
          ))}
          {reqs.length > 6 && <p className="text-xs text-blue-700">+{reqs.length - 6} more pending</p>}
        </div>
      </CardContent>
    </Card>
  );
}

const CLAIM_TONE = {
  "Not Filed": "bg-slate-100 text-slate-700 border-slate-300",
  Filed: "bg-amber-100 text-amber-800 border-amber-300",
  Approved: "bg-sky-100 text-sky-800 border-sky-300",
  Settled: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

function ClaimSummaryCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/coa-claims-summary").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.total) return null;
  const c = data.counts || {};
  const staleDays = 30;
  const cutoff = new Date(Date.now() - staleDays * 86400000).toISOString().slice(0, 10);
  const stale = (data.pending || []).filter((p) => p.status === "Filed" && p.filed_on && p.filed_on <= cutoff);
  const daysOf = (p) => Math.floor((Date.now() - new Date(p.filed_on)) / 86400000);
  const followupMsg = [
    "*NTC — Insurance Claim Follow-up*",
    "",
    "The following filed claims are pending approval for over 30 days:",
    ...stale.map((p, i) => `${i + 1}. ${p.coa_no} — LR ${p.lr_no || "—"} — ${p.customer_name || p.office} — damage ₹${(p.damage_value || 0).toLocaleString("en-IN")} — filed ${daysOf(p)} days ago`),
    "",
    "Kindly follow up with the insurer for approval status.",
  ].join("\n");
  return (
    <Card className="mb-4 border-violet-300 bg-violet-50/60" data-testid="claim-summary-card">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-violet-700" />
            <span className="font-semibold text-violet-900">Insurance Claims (COA)</span>
            <span className="text-xs text-violet-700">{data.total} COA issued</span>
          </div>
          <Button size="sm" variant="outline" className="h-7 border-violet-300 text-violet-800" onClick={() => navigate("/consignment-entry")} data-testid="claim-summary-open-btn">
            Open Consignments
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {["Not Filed", "Filed", "Approved", "Settled"].map((st) => (
            <span key={st} className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${CLAIM_TONE[st]}`} data-testid={`claim-count-${st.toLowerCase().replace(" ", "-")}`}>
              {st}: {c[st] || 0}
            </span>
          ))}
          <span className="ml-auto rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white" data-testid="claim-recovered-amount">
            Recovered {inr(data.recovered)} of {inr(data.damage_total)} damage
          </span>
        </div>
        {stale.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2" data-testid="claim-followup-nudge">
            <div className="flex items-center gap-2 text-sm text-red-900">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
              <span>
                <b>{stale.length} filed claim{stale.length > 1 ? "s" : ""}</b> pending approval for over 30 days —{" "}
                {stale.slice(0, 3).map((p) => `${p.coa_no} (${daysOf(p)}d)`).join(", ")}
                {stale.length > 3 ? ` +${stale.length - 3} more` : ""}
              </span>
            </div>
            <Button
              size="sm"
              className="h-7 bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(followupMsg)}`, "_blank")}
              data-testid="claim-followup-wa-btn"
            >
              <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp Follow-up
            </Button>
          </div>
        )}
        {(data.pending || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="claim-pending-list">
            {data.pending.map((p) => (
              <span key={p.coa_no} className="rounded-md border border-violet-200 bg-white px-2 py-1 text-xs text-slate-700">
                <b>{p.coa_no}</b> · LR {p.lr_no || "—"} · {p.customer_name || p.office} ·{" "}
                <span className={`rounded-full border px-1.5 ${CLAIM_TONE[p.status]}`}>{p.status}</span>
                {p.damage_value > 0 && <span className="ml-1 text-slate-500">{inr(p.damage_value)}</span>}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OverdueSection() {
  const [data, setData] = useState(null);
  const [agingOpen, setAgingOpen] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/invoices"), api.get("/receipts"), api.get("/customers")])
      .then(([inv, rec, cust]) => setData({ invoices: inv.data || [], receipts: rec.data || [], customers: cust.data || [] }))
      .catch(() => setData({ invoices: [], receipts: [], customers: [] }));
  }, []);
  if (!data) return null;
  const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const byCust = {};
  data.invoices.forEach((i) => {
    const key = i.customer_name || i.customer_id;
    if (!key) return;
    const a = (byCust[key] = byCust[key] || { name: i.customer_name || "—", customer_id: i.customer_id, billed: 0, received: 0, oldest: "" });
    a.billed += Number(i.total) || 0;
    const d = i.issue_date || String(i.created_at || "").slice(0, 10);
    if ((i.status || "") !== "Paid" && d && (!a.oldest || d < a.oldest)) a.oldest = d;
  });
  data.receipts.forEach((r) => {
    const key = r.customer_name || r.customer_id;
    if (byCust[key]) byCust[key].received += Number(r.amount) || 0;
  });
  const phoneOf = (a) => {
    const c = data.customers.find((x) => x.id === a.customer_id || x.name === a.name);
    return String(c?.phone || "").replace(/\D/g, "");
  };
  const overdue = Object.values(byCust)
    .map((a) => ({ ...a, balance: a.billed - a.received, days: a.oldest ? Math.floor((Date.now() - new Date(a.oldest)) / 86400000) : 0 }))
    .filter((a) => a.balance > 0 && a.oldest && a.oldest <= cutoff)
    .sort((a, b) => b.days - a.days);
  const agingMap = {};
  data.invoices.forEach((i) => {
    if ((i.status || "") === "Paid") return;
    const key = i.customer_name || i.customer_id;
    const d = i.issue_date || String(i.created_at || "").slice(0, 10);
    if (!key || !d) return;
    const days = Math.floor((Date.now() - new Date(d)) / 86400000);
    const amt = Number(i.total) || 0;
    const a = (agingMap[key] = agingMap[key] || { name: i.customer_name || "—", b0: 0, b31: 0, b61: 0, b90: 0, total: 0 });
    if (days <= 30) a.b0 += amt;
    else if (days <= 60) a.b31 += amt;
    else if (days <= 90) a.b61 += amt;
    else a.b90 += amt;
    a.total += amt;
  });
  const aging = Object.values(agingMap).sort((a, b) => b.total - a.total);
  if (!overdue.length && !aging.length) return null;
  const remind = (a) => {
    const digits = phoneOf(a);
    if (!digits) {
      toast.error("No phone saved for this customer");
      return;
    }
    const msg = `Dear ${a.name},\n\nThis is a gentle reminder from Nisha Transport Corporation. An amount of *${inr(a.balance)}* is pending on your account for over ${a.days} days (oldest bill dated ${fmtDate(a.oldest)}).\n\nKindly arrange the payment at the earliest.\n\n— Nisha Transport Corporation`;
    window.open(`https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const agingExcel = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = ["Customer,0-30 Days,31-60 Days,61-90 Days,90+ Days,Total"];
    aging.forEach((r) => lines.push([r.name, r.b0, r.b31, r.b61, r.b90, r.total].map(cell).join(",")));
    lines.push(["TOTAL", aging.reduce((s, r) => s + r.b0, 0), aging.reduce((s, r) => s + r.b31, 0), aging.reduce((s, r) => s + r.b61, 0), aging.reduce((s, r) => s + r.b90, 0), aging.reduce((s, r) => s + r.total, 0)].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ntc-aging-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  const amt = (v) => (v ? inr(v) : "—");
  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 p-4" data-testid="overdue-reminders-card">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm font-bold text-red-700">
          <AlertTriangle className="h-4 w-4" /> Overdue Follow-ups — balance pending over 30 days ({overdue.length})
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 bg-white text-xs" onClick={() => setAgingOpen(true)} data-testid="aging-report-btn">
            Aging Buckets
          </Button>
          <Button variant="outline" size="sm" className="h-7 bg-white text-xs" disabled={!overdue.length} onClick={() => printOverdueSheet(overdue.map((a) => ({ ...a, phone: phoneOf(a) })))} data-testid="print-overdue-btn">
            <Printer className="mr-1 h-3 w-3" /> Print Sheet
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {overdue.map((a) => (
          <div key={a.name} className="flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs" data-testid="overdue-customer-chip">
            <span className="font-semibold">{a.name}</span>
            <span className="font-bold text-red-600">{inr(a.balance)}</span>
            <span className="text-slate-500">{a.days} days · since {fmtDate(a.oldest)}</span>
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-emerald-600 hover:bg-emerald-50"
              title={`WhatsApp payment reminder to ${a.name}`}
              onClick={() => remind(a)}
              data-testid="overdue-whatsapp-btn"
            >
              <MessageCircle className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {!overdue.length && <span className="text-xs text-slate-500">No balances pending beyond 30 days.</span>}
      </div>
      <Dialog open={agingOpen} onOpenChange={setAgingOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[680px]">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="font-display">Outstanding Aging — 30/60/90 Buckets</DialogTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={!aging.length} onClick={agingExcel} data-testid="aging-excel-btn">
                  <FileDown className="mr-1.5 h-3.5 w-3.5" /> Excel
                </Button>
                <Button variant="outline" size="sm" disabled={!aging.length} onClick={() => printAgingReport(aging)} data-testid="print-aging-btn">
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                </Button>
              </div>
            </div>
          </DialogHeader>
          {aging.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No unpaid bills.</p>
          ) : (
            <table className="w-full text-sm" data-testid="aging-table">
              <thead>
                <tr className="border-b text-left text-xs uppercase text-slate-500">
                  <th className="py-1.5">Customer</th>
                  <th className="py-1.5 text-right">0–30</th>
                  <th className="py-1.5 text-right">31–60</th>
                  <th className="py-1.5 text-right">61–90</th>
                  <th className="py-1.5 text-right">90+</th>
                  <th className="py-1.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {aging.map((r) => (
                  <tr key={r.name} className="border-b border-slate-100" data-testid="aging-row">
                    <td className="py-1.5 font-medium">{r.name}</td>
                    <td className="py-1.5 text-right tabular-nums">{amt(r.b0)}</td>
                    <td className="py-1.5 text-right tabular-nums">{amt(r.b31)}</td>
                    <td className="py-1.5 text-right tabular-nums text-amber-700">{amt(r.b61)}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-red-600">{amt(r.b90)}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums">{inr(r.total)}</td>
                  </tr>
                ))}
                <tr className="font-bold">
                  <td className="py-2">TOTAL</td>
                  <td className="py-2 text-right tabular-nums">{inr(aging.reduce((s, r) => s + r.b0, 0))}</td>
                  <td className="py-2 text-right tabular-nums">{inr(aging.reduce((s, r) => s + r.b31, 0))}</td>
                  <td className="py-2 text-right tabular-nums">{inr(aging.reduce((s, r) => s + r.b61, 0))}</td>
                  <td className="py-2 text-right tabular-nums text-red-600">{inr(aging.reduce((s, r) => s + r.b90, 0))}</td>
                  <td className="py-2 text-right tabular-nums">{inr(aging.reduce((s, r) => s + r.total, 0))}</td>
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CollectionSection() {
  const [stats, setStats] = useState(null);
  const [bankRows, setBankRows] = useState([]);
  const [limits, setLimits] = useState({});
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitForm, setLimitForm] = useState({});
  const [sheetDate, setSheetDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sheetMonth, setSheetMonth] = useState(() => new Date().toISOString().slice(0, 7));
  useEffect(() => {
    api.get("/collection-stats").then((r) => { setStats(r.data); if (r.data?.today_date) setSheetDate(r.data.today_date); }).catch(() => {});
    api.get("/bank-statements").then((r) => setBankRows(r.data || [])).catch(() => {});
    api.get("/bank-limits").then((r) => setLimits(r.data.limits || {})).catch(() => {});
  }, []);
  const saveLimits = async () => {
    try {
      const l = Object.fromEntries(TARGET_BRANCHES.map((b) => [b, Number(limitForm[b]) || 0]));
      await api.put("/bank-limits", { limits: l });
      setLimits(l);
      setLimitOpen(false);
      toast.success("Low balance limits saved");
    } catch {
      toast.error("Could not save limits");
    }
  };
  const bankLatest = TARGET_BRANCHES.map((b) => {
    const list = bankRows.filter((r) => (r.branch || "Chennai") === b && r.transaction_date);
    if (!list.length) return null;
    const latest = list.reduce((a, r) => (String(r.transaction_date) > String(a.transaction_date) ? r : a));
    return { b, balance: Number(latest.balance) || 0, date: latest.transaction_date };
  }).filter(Boolean);
  const today = stats?.today || {};
  const todayTotal = Object.values(today).reduce((a, b) => a + (Number(b) || 0), 0);
  const trend = (stats?.trend || []).map((t) => ({ ...t, label: new Date(`${t.month}-01`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }) }));
  const activeBranches = TARGET_BRANCHES.filter((b) => trend.some((t) => Number(t[b]) > 0));
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="today-collection-strip">
        <span className="text-sm font-bold text-emerald-800">Today's Collection: {inr(todayTotal)}</span>
        {TARGET_BRANCHES.map((b) =>
          today[b] ? (
            <span key={b} className="text-xs text-emerald-700" data-testid={`today-collection-${b.toLowerCase()}`}>
              {b}: <b>{inr(today[b])}</b>
            </span>
          ) : null
        )}
        {todayTotal === 0 && <span className="text-xs text-emerald-700">No money receipts entered yet today</span>}
        <div className="ml-auto flex items-center gap-1.5">
          <input
            type="date"
            value={sheetDate}
            max={stats?.today_date || undefined}
            onChange={(e) => setSheetDate(e.target.value)}
            className="h-7 rounded-md border border-emerald-300 bg-white px-2 text-xs text-emerald-800"
            data-testid="collection-sheet-date"
          />
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await api.get("/receipts");
                printDailyCollection(sheetDate || stats?.today_date || new Date().toISOString().slice(0, 10), r.data || []);
              } catch {
                toast.error("Could not load receipts");
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            data-testid="print-daily-collection-btn"
          >
            <Printer className="h-3.5 w-3.5" /> Print Collection Sheet
          </button>
          <input
            type="month"
            value={sheetMonth}
            onChange={(e) => setSheetMonth(e.target.value)}
            className="h-7 rounded-md border border-emerald-300 bg-white px-2 text-xs text-emerald-800"
            data-testid="collection-sheet-month"
          />
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await api.get("/receipts");
                printMonthlyCollection(sheetMonth || new Date().toISOString().slice(0, 7), r.data || []);
              } catch {
                toast.error("Could not load receipts");
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            data-testid="print-monthly-collection-btn"
          >
            <Printer className="h-3.5 w-3.5" /> Monthly Register
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await api.get("/receipts");
                const m = sheetMonth || new Date().toISOString().slice(0, 7);
                const list = (r.data || []).filter((x) => String(x.date || "").slice(0, 7) === m).sort((a, b) => `${a.office || "Chennai"}${a.date}`.localeCompare(`${b.office || "Chennai"}${b.date}`));
                const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
                const lines = [["Sl No.", "Branch", "Date", "MR No.", "Customer", "Bill No.", "Mode", "Amount"].join(",")];
                list.forEach((x, i) => lines.push([i + 1, x.office || "Chennai", x.date || "", x.mr_no || "", x.customer_name || "", x.against_invoice_no || "", x.payment_mode || "", x.amount || 0].map(cell).join(",")));
                lines.push(["", "", "", "", "", "", "GRAND TOTAL", list.reduce((a, x) => a + (Number(x.amount) || 0), 0)].join(","));
                const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `ntc-collection-${m}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch {
                toast.error("Could not load receipts");
              }
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
            data-testid="collection-excel-btn"
          >
            <FileDown className="h-3.5 w-3.5" /> Excel
          </button>
        </div>
      </div>
      {bankLatest.length > 0 && (() => {
        const low = bankLatest.filter(({ b, balance }) => Number(limits[b]) > 0 && balance < Number(limits[b]));
        const isLow = low.length > 0;
        return (
          <div
            className={`mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border px-4 py-3 ${isLow ? "border-red-300 bg-red-50" : "border-sky-200 bg-sky-50"}`}
            data-testid="bank-balance-strip"
          >
            {isLow && <AlertTriangle className="h-4 w-4 text-red-600" data-testid="low-balance-icon" />}
            <span className={`text-sm font-bold ${isLow ? "text-red-800" : "text-sky-800"}`}>
              {isLow ? `LOW BALANCE — ${low.map((x) => x.b).join(", ")} below limit!` : "Bank Balance:"}
            </span>
            {bankLatest.map(({ b, balance, date }) => {
              const bad = Number(limits[b]) > 0 && balance < Number(limits[b]);
              return (
                <span key={b} className={`text-xs ${bad ? "font-semibold text-red-700" : isLow ? "text-red-600" : "text-sky-700"}`} data-testid={`bank-balance-${b.toLowerCase()}`}>
                  {b}: <b>{inr(balance)}</b> <span className={bad ? "text-red-400" : isLow ? "text-red-400" : "text-sky-500"}>(as on {fmtDate(date)}{bad ? ` · limit ${inr(limits[b])}` : ""})</span>
                </span>
              );
            })}
            <button
              type="button"
              onClick={() => { setLimitForm(Object.fromEntries(TARGET_BRANCHES.map((b) => [b, limits[b] || ""]))); setLimitOpen(true); }}
              className={`ml-auto rounded-md border bg-white px-2.5 py-1 text-xs font-semibold ${isLow ? "border-red-300 text-red-700 hover:bg-red-100" : "border-sky-300 text-sky-700 hover:bg-sky-100"}`}
              data-testid="set-bank-limits-btn"
            >
              Set Limits
            </button>
          </div>
        );
      })()}
      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle className="font-display">Low Balance Limits (₹ per branch)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {TARGET_BRANCHES.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <span className="w-24 text-sm font-medium">{b}</span>
                <Input type="number" placeholder="Alert below ₹" value={limitForm[b] ?? ""} onChange={(e) => setLimitForm((f) => ({ ...f, [b]: e.target.value }))} data-testid={`bank-limit-input-${b.toLowerCase()}`} />
              </div>
            ))}
            <Button className="w-full" onClick={saveLimits} data-testid="save-bank-limits-btn">Save Limits</Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollectionTargetCard />
        <Card data-testid="collection-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-display text-lg">Collection Trend</CardTitle>
            <div className="text-xs text-muted-foreground">Last 6 months — money receipts per branch</div>
          </CardHeader>
          <CardContent className="h-[230px]">
            {activeBranches.length === 0 ? (
              <div className="grid h-full place-items-center text-sm text-muted-foreground" data-testid="trend-empty-msg">No collections in the last 6 months.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {activeBranches.map((b) => (
                    <Bar key={b} dataKey={b} fill={BRANCH_BAR_COLORS[b]} radius={[3, 3, 0, 0]} maxBarSize={28} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function CollectionTargetCard() {
  const month = new Date().toISOString().slice(0, 7);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const load = () => api.get(`/collection-targets/${month}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put(`/collection-targets/${month}`, { targets: Object.fromEntries(TARGET_BRANCHES.map((b) => [b, Number(form[b]) || 0])) });
      toast.success("Collection targets saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save targets");
    }
  };
  const monthLabel = new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const targets = data?.targets || {};
  const collected = data?.collected || {};
  const rows = TARGET_BRANCHES.map((b) => ({ b, t: Number(targets[b]) || 0, c: Number(collected[b]) || 0 })).filter((r) => r.t > 0 || r.c > 0);
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const elapsedPct = Math.round((now.getDate() / daysInMonth) * 100);
  const behind = rows.filter(({ t, c }) => t > 0 && elapsedPct >= 60 && (c / t) * 100 < elapsedPct - 25);
  return (
    <Card data-testid="collection-target-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="font-display text-lg">Monthly Collection Target</CardTitle>
          <div className="text-xs text-muted-foreground">{monthLabel} — money receipts vs target, branch-wise</div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setForm(Object.fromEntries(TARGET_BRANCHES.map((b) => [b, targets[b] || ""]))); setOpen(true); }}
          data-testid="set-targets-btn"
        >
          Set Targets
        </Button>
      </CardHeader>
      <CardContent>
        {behind.length > 0 && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5" data-testid="target-behind-alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            <div className="text-xs text-red-700">
              <span className="font-semibold">{elapsedPct}% of the month is over</span> — {behind.map(({ b, t, c }) => `${b} is at only ${Math.round((c / t) * 100)}% of target`).join(", ")}. Push collections before month end!
            </div>
          </div>
        )}
        {rows.length === 0 ? (
          <div className="py-4 text-sm text-muted-foreground" data-testid="no-targets-msg">No targets set for this month yet — click "Set Targets" to begin.</div>
        ) : (
          <div className="space-y-3">
            {rows.map(({ b, t, c }) => {
              const pct = t > 0 ? Math.min(100, Math.round((c / t) * 100)) : 0;
              return (
                <div key={b} data-testid="target-branch-row">
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-semibold">{b}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {inr(c)} / {t > 0 ? inr(t) : "—"} {t > 0 && <span className={pct >= 100 ? "font-bold text-emerald-600" : "font-semibold text-slate-600"}>({pct}%)</span>}
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full ${pct >= 100 ? "bg-emerald-500" : pct >= 60 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${t > 0 ? pct : 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle className="font-display">Set Collection Targets — {monthLabel}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {TARGET_BRANCHES.map((b) => (
              <div key={b} className="flex items-center gap-3">
                <span className="w-24 text-sm font-medium">{b}</span>
                <Input type="number" placeholder="Target ₹" value={form[b] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [b]: e.target.value }))} data-testid={`target-input-${b.toLowerCase()}`} />
              </div>
            ))}
            <Button className="w-full" onClick={save} data-testid="save-targets-btn">Save Targets</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MarketVehicleAlert() {
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  useEffect(() => {
    api.get("/market-vehicles").then((r) => setPending((r.data || []).filter((m) => (m.status || "Pending") === "Pending").length)).catch(() => {});
  }, []);
  if (!pending) return null;
  return (
    <button
      type="button"
      onClick={() => navigate("/market-vehicles")}
      data-testid="dashboard-market-vehicle-alert"
      className="mb-4 flex w-full items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
    >
      <span className="flex items-center gap-2 text-sm font-medium text-amber-800">
        <Truck className="h-4 w-4" />
        {pending} market vehicle request{pending > 1 ? "s" : ""} pending approval
      </span>
      <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Review →</span>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [bookingTrend, setBookingTrend] = useState([]);
  const [branchTrend, setBranchTrend] = useState([]);
  const [branchMetric, setBranchMetric] = useState("bookings");
  const [ewayAlert, setEwayAlert] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [custBirthdays, setCustBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [office, setOffice] = useState("All");

  useEffect(() => {
    api
      .get("/reports/expiring-eway")
      .then((r) => setEwayAlert(r.data))
      .catch(() => {});
    if (isAdmin) {
      api
        .get("/calendar-events")
        .then((r) => {
          const d = new Date();
          const pad = (n) => String(n).padStart(2, "0");
          const fmt = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
          const todayIso = fmt(d);
          const tomorrow = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
          const tomorrowIso = fmt(tomorrow);
          setUpcomingEvents(
            (r.data || [])
              .filter((ev) => ev.date === todayIso || ev.date === tomorrowIso)
              .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")))
              .map((ev) => ({ ...ev, isToday: ev.date === todayIso }))
          );
        })
        .catch(() => {});
      api
        .get("/customers")
        .then((r) => {
          const d = new Date();
          const pad = (n) => String(n).padStart(2, "0");
          const todayMD = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const tm = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
          const tomorrowMD = `${pad(tm.getMonth() + 1)}-${pad(tm.getDate())}`;
          setCustBirthdays(
            (r.data || [])
              .filter((c) => c.birthday)
              .map((c) => ({ ...c, md: String(c.birthday).slice(5, 10) }))
              .filter((c) => c.md === todayMD || c.md === tomorrowMD)
              .map((c) => ({ ...c, isToday: c.md === todayMD }))
          );
        })
        .catch(() => {});
      api
        .get("/staff-master")
        .then((r) => {
          const d = new Date();
          const pad = (n) => String(n).padStart(2, "0");
          const todayMD = `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
          const tm = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
          const tomorrowMD = `${pad(tm.getMonth() + 1)}-${pad(tm.getDate())}`;
          setBirthdays(
            (r.data || [])
              .filter((s) => s.birthday && (s.status || "Active") !== "Inactive")
              .map((s) => ({ ...s, md: String(s.birthday).slice(5, 10) }))
              .filter((s) => s.md === todayMD || s.md === tomorrowMD)
              .map((s) => ({ ...s, isToday: s.md === todayMD }))
          );
          setAnniversaries(
            (r.data || [])
              .filter((s) => s.joining_date && (s.status || "Active") !== "Inactive")
              .map((s) => ({ ...s, md: String(s.joining_date).slice(5, 10), years: d.getFullYear() - Number(String(s.joining_date).slice(0, 4)) }))
              .filter((s) => (s.md === todayMD || s.md === tomorrowMD) && s.years > 0)
              .map((s) => ({ ...s, isToday: s.md === todayMD }))
          );
          const msDay = 86400000;
          setExpiringLicenses(
            (r.data || [])
              .filter((s) => s.license_expiry && (s.status || "Active") !== "Inactive")
              .map((s) => ({ ...s, daysLeft: Math.ceil((new Date(s.license_expiry) - d) / msDay) }))
              .filter((s) => s.daysLeft <= 30)
              .sort((a, b) => a.daysLeft - b.daysLeft)
          );
        })
        .catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    setStats(null);
    api
      .get("/dashboard/stats", { params: office !== "All" ? { office } : {} })
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    Promise.all([api.get("/shipments"), api.get("/invoices"), api.get("/receipts")])
      .then(([r, ri, rr]) => {
        const bd = (s) => s.lr_date || s.dispatch_date || (s.created_at ? String(s.created_at).slice(0, 10) : "");
        const months = [];
        const d = new Date();
        for (let i = 5; i >= 0; i--) {
          const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
          months.push({ key: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`, label: dt.toLocaleDateString("en-GB", { month: "short" }), bookings: 0, revenue: 0 });
        }
        (r.data || []).forEach((s) => {
          if (office !== "All" && (s.office || "Chennai") !== office) return;
          const k = (bd(s) || "").slice(0, 7);
          const row = months.find((mo) => mo.key === k);
          if (row) { row.bookings += 1; row.revenue += Number(s.freight_amount) || 0; }
        });
        setBookingTrend(months);

        const branchOf = (o) => (OFFICES.includes(o) ? o : "Chennai");
        const bm = months.map((mo) => ({
          key: mo.key,
          label: mo.label,
          ...Object.fromEntries(OFFICES.flatMap((o) => [[`${o}_bookings`, 0], [`${o}_freight`, 0], [`${o}_collections`, 0], [`${o}_outstanding`, 0]])),
        }));
        (r.data || []).forEach((s) => {
          const k = (bd(s) || "").slice(0, 7);
          const row = bm.find((mo) => mo.key === k);
          if (!row) return;
          const o = branchOf(s.office);
          row[`${o}_bookings`] += 1;
          row[`${o}_freight`] += Number(s.freight_amount) || 0;
        });
        const receipts = rr.data || [];
        const invoices = ri.data || [];
        receipts.forEach((rc) => {
          const k = ((rc.date || String(rc.created_at || "").slice(0, 10)) || "").slice(0, 7);
          const row = bm.find((mo) => mo.key === k);
          if (row) row[`${branchOf(rc.office)}_collections`] += Number(rc.amount) || 0;
        });
        const shipById = {};
        (r.data || []).forEach((s) => { shipById[s.id] = s; });
        const normBill = (v) => String(v || "").replace(/\D/g, "");
        const idByBillNo = {};
        invoices.forEach((i) => { if (i.invoice_no) idByBillNo[normBill(i.invoice_no)] = i.id; });
        const paid = {};
        receipts.forEach((rc) => {
          if (rc.against_invoice_id) paid[rc.against_invoice_id] = (paid[rc.against_invoice_id] || 0) + (Number(rc.amount) || 0);
          (rc.bills || []).forEach((b) => {
            const iid = idByBillNo[normBill(b.bill_no)];
            const net = (Number(b.amount) || 0) - (Number(b.tds) || 0) - (Number(b.deduction) || 0);
            if (iid) paid[iid] = (paid[iid] || 0) + net;
          });
        });
        invoices.forEach((i) => {
          const bal = /paid/i.test(i.status || "") && !/partly/i.test(i.status || "") ? 0 : Math.max((Number(i.total) || 0) - (paid[i.id] || 0), 0);
          if (!bal) return;
          const k = ((i.issue_date || String(i.created_at || "").slice(0, 10)) || "").slice(0, 7);
          const row = bm.find((mo) => mo.key === k);
          if (!row) return;
          const sid = i.shipment_id || (Array.isArray(i.shipment_ids) ? i.shipment_ids[0] : "");
          const o = branchOf((shipById[sid] || {}).office ?? i.office);
          row[`${o}_outstanding`] += bal;
        });
        setBranchTrend(bm);
      })
      .catch(() => {});
  }, [office]);

  const officeAction = isAdmin ? (
    <OfficeSwitcher value={office} onChange={setOffice} />
  ) : (
    <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700" data-testid="dashboard-branch-badge">
      <Building2 className="h-4 w-4" /> {user?.office || "Unassigned"} Branch
    </div>
  );
  const subtitle = (
    <span data-testid="dashboard-user-info">
      Welcome, <b className="text-foreground">{user?.name || "—"}</b> · Login Branch:{" "}
      <b className="text-foreground">{user?.role === "admin" ? `${user?.office || "Chennai"} (All Branches Admin)` : user?.office || "Unassigned"}</b>
      {user?.last_login && (
        <>
          {" · "}Last logged in:{" "}
          <b className="text-foreground" data-testid="dashboard-last-login">
            {new Date(user.last_login).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </b>
        </>
      )}
      {" — "}
      {isAdmin ? "Financial overview of your logistics operations." : "Bookings, revenue & alerts for your office."}
    </span>
  );

  if (!stats) {
    return (
      <div>
        <PageHeader title="Dashboard" subtitle={subtitle} action={officeAction} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-80 w-full rounded-lg lg:col-span-2" />
          <Skeleton className="h-80 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const profitPositive = stats.net_profit >= 0;

  const ewayCount = ewayAlert?.count || 0;
  const ewayExpired = (ewayAlert?.items || []).filter((e) => e.expired).length;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={subtitle} action={officeAction} />

      <QuickLinks />

      {isAdmin && <Gstr1Nudge />}

      {isAdmin && <MarketVehicleAlert />}

      {ewayCount > 0 && (
        <button
          type="button"
          onClick={() => navigate("/eway-bills")}
          data-testid="dashboard-eway-alert"
          className="mb-4 flex w-full items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-left transition-colors hover:bg-rose-100"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-rose-100 text-rose-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-rose-800">
                {ewayCount} e-way bill{ewayCount > 1 ? "s" : ""} need attention
              </div>
              <div className="text-xs text-rose-600">
                {ewayExpired > 0 ? `${ewayExpired} expired — extend on the GST portal. ` : ""}Undelivered bills expiring within 1 day.
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-rose-700">View E-Way Bills →</span>
        </button>
      )}

      {isAdmin && upcomingEvents.length > 0 && (
        <button
          type="button"
          onClick={() => navigate("/ntc-calendar")}
          data-testid="dashboard-calendar-alert"
          className="mb-4 flex w-full items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left transition-colors hover:bg-amber-100"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-600">
              <CalendarDays className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-amber-800">
                {upcomingEvents.length} upcoming NTC Calendar event{upcomingEvents.length > 1 ? "s" : ""}
              </div>
              <div className="text-xs text-amber-700">
                {upcomingEvents.slice(0, 3).map((ev) => `${ev.isToday ? "Today" : "Tomorrow"}${ev.time ? " " + ev.time : ""}: ${ev.title} (${ev.type || "Event"})`).join(" • ")}
                {upcomingEvents.length > 3 ? ` • +${upcomingEvents.length - 3} more` : ""}
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-amber-700">View NTC Calendar →</span>
        </button>
      )}

      {isAdmin && birthdays.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate("/staff-master")}
          data-testid="dashboard-birthday-alert"
          className="mb-4 flex w-full cursor-pointer items-center justify-between rounded-lg border border-pink-200 bg-pink-50 px-4 py-3 text-left transition-colors hover:bg-pink-100"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-pink-100 text-pink-600">
              <Cake className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-pink-800">
                {birthdays.length} staff birthday{birthdays.length > 1 ? "s" : ""} coming up — don't forget to wish!
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-pink-700">
                {birthdays.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1">
                    {s.isToday ? "Today" : "Tomorrow"}: {s.name} ({s.branch || "Chennai"})
                    {s.phone && (
                      <>
                        <a
                          href={wishLink(s.phone, `🎂 Happy Birthday, ${s.name}! Wishing you great health, happiness and success in the year ahead. Warm wishes from all of us at Nisha Transport Corporation.\n\n— Admin, Nisha Transport Corporation`)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-5 items-center gap-0.5 rounded-full border border-emerald-300 bg-white px-1.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                          title={`Send birthday wish to ${s.name} from Admin`}
                          data-testid="birthday-wish-btn"
                        >
                          <MessageCircle className="h-3 w-3" /> Admin
                        </a>
                        <a
                          href={wishLink(s.phone, `🎂 Dear ${s.name}, Happy Birthday! Your dedication means a lot to our company. Wishing you great health, happiness and continued success in the year ahead.\n\n— Managing Director, Nisha Transport Corporation`)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-5 items-center gap-0.5 rounded-full border border-indigo-300 bg-white px-1.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-100"
                          title={`Send birthday wish to ${s.name} from Company MD`}
                          data-testid="birthday-wish-md-btn"
                        >
                          <MessageCircle className="h-3 w-3" /> MD
                        </a>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); printStaffBirthdayCard(s); }}
                      className="inline-flex h-5 w-5 items-center justify-center rounded text-pink-600 hover:bg-pink-200"
                      title={`Birthday greeting card for ${s.name}`}
                      data-testid="birthday-card-btn"
                    >
                      <Gift className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-pink-700">View Staff Master →</span>
        </div>
      )}

      {isAdmin && custBirthdays.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate("/customers")}
          data-testid="dashboard-customer-birthday-alert"
          className="mb-4 flex w-full cursor-pointer items-center justify-between rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-4 py-3 text-left transition-colors hover:bg-fuchsia-100"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-fuchsia-100 text-fuchsia-600">
              <Cake className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-fuchsia-800">
                {custBirthdays.length} customer birthday{custBirthdays.length > 1 ? "s" : ""} — a wish goes a long way!
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fuchsia-700">
                {custBirthdays.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1">
                    {c.isToday ? "Today" : "Tomorrow"}: {c.name} ({c.branch || "—"})
                    {c.phone && (
                      <>
                        <a
                          href={wishLink(c.phone, `🎂 Dear ${c.contact_person || c.name}, Happy Birthday! Thank you for your continued trust and partnership with us. Wishing you great health, happiness and success.\n\n— Admin, Nisha Transport Corporation`)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-5 items-center gap-0.5 rounded-full border border-emerald-300 bg-white px-1.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                          title={`Send birthday wish to ${c.name} from Admin`}
                          data-testid="customer-birthday-wish-btn"
                        >
                          <MessageCircle className="h-3 w-3" /> Admin
                        </a>
                        <a
                          href={wishLink(c.phone, `🎂 Dear ${c.contact_person || c.name}, Happy Birthday! Your partnership means a lot to our company. Wishing you great health, happiness and continued success.\n\n— Managing Director, Nisha Transport Corporation`)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-5 items-center gap-0.5 rounded-full border border-indigo-300 bg-white px-1.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-100"
                          title={`Send birthday wish to ${c.name} from Company MD`}
                          data-testid="customer-birthday-wish-md-btn"
                        >
                          <MessageCircle className="h-3 w-3" /> MD
                        </a>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); printStaffBirthdayCard({ name: c.contact_person || c.name }); }}
                      className="inline-flex h-5 w-5 items-center justify-center rounded text-fuchsia-600 hover:bg-fuchsia-200"
                      title={`Birthday greeting card for ${c.name}`}
                      data-testid="customer-birthday-card-btn"
                    >
                      <Gift className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-fuchsia-700">View Customer Master →</span>
        </div>
      )}

      {isAdmin && anniversaries.length > 0 && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate("/staff-master")}
          data-testid="dashboard-anniversary-alert"
          className="mb-4 flex w-full cursor-pointer items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-left transition-colors hover:bg-teal-100"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-teal-100 text-teal-600">
              <Award className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-teal-800">
                {anniversaries.length} work anniversar{anniversaries.length > 1 ? "ies" : "y"} — congratulate your staff!
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-teal-700">
                {anniversaries.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1">
                    {s.isToday ? "Today" : "Tomorrow"}: {s.name} completes {s.years} year{s.years > 1 ? "s" : ""} ({s.branch || "Chennai"})
                    {s.phone && (
                      <a
                        href={wishLink(s.phone, `🎉 Congratulations, ${s.name}, on completing ${s.years} wonderful year${s.years > 1 ? "s" : ""} with Nisha Transport Corporation! Thank you for your dedication and hard work. Here's to many more milestones together.`)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex h-5 w-5 items-center justify-center rounded text-emerald-600 hover:bg-emerald-100"
                        title={`Send anniversary wish to ${s.name}`}
                        data-testid="anniversary-wish-btn"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); printStaffAnniversaryCard(s); }}
                      className="inline-flex h-5 w-5 items-center justify-center rounded text-teal-600 hover:bg-teal-200"
                      title={`Anniversary greeting card for ${s.name}`}
                      data-testid="anniversary-card-btn"
                    >
                      <Gift className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-teal-700">View Staff Master →</span>
        </div>
      )}

      {isAdmin && expiringLicenses.length > 0 && (
        <button
          type="button"
          onClick={() => navigate("/staff-master")}
          data-testid="dashboard-license-alert"
          className="mb-4 flex w-full items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-red-100 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-semibold text-red-800">
                {expiringLicenses.length} driving license{expiringLicenses.length > 1 ? "s" : ""} expiring or expired
              </div>
              <div className="text-xs text-red-700">
                {expiringLicenses.slice(0, 3).map((s) => `${s.name}: ${s.daysLeft < 0 ? `expired ${Math.abs(s.daysLeft)}d ago` : s.daysLeft === 0 ? "expires today" : `${s.daysLeft}d left`}`).join(" • ")}
                {expiringLicenses.length > 3 ? ` • +${expiringLicenses.length - 3} more` : ""}
              </div>
            </div>
          </div>
          <span className="text-xs font-semibold text-red-700">View Staff Master →</span>
        </button>
      )}

      <TodayCounter />
      <CircularNotice />
      {isAdmin && <MorningOpeningNudge />}
      {isAdmin && <EveningDayBookNudge />}
      {isAdmin && <DaySettlementCard />}
      {isAdmin && <WeeklyPodScorecard />}
      {isAdmin && <InsuranceAlert />}
      {isAdmin && <LockedAccountsAlert />}
      {isAdmin && <VendorDuesAlert />}
      {isAdmin && <ExpiringRatesAlert />}
      {isAdmin && <BouncedChequeAlert />}
      {isAdmin && <ReplacementReminderAlert />}
      {isAdmin && <IssuedBounceAlert />}
      {isAdmin && <PendingChequeReminder />}
      {isAdmin && <TdsMismatchAlert />}
      {isAdmin && <MorningBriefingCard />}
      {isAdmin && <WeeklyDigestCard />}
      {isAdmin && <EveningBookingCard />}
      {isAdmin && <OverdueSubmissionsCard />}
      {isAdmin && <VendorPaymentReminderCard />}
      {isAdmin && <VehicleBalanceDueAlert />}
      {isAdmin && <CbsWatchCard />}
      {isAdmin && <DeductionApprovalAlert />}
      <MissingPapersCard />
      <BrokenPromisesAlert />
      <SubmissionFollowupAlert />
      <NextCallReminder />
      {isAdmin && <BankPositionCard />}
      <BreakdownAlerts />
      <ProofReminder />
      <LostPadAlert />
      {isAdmin && <VehicleDocAlerts />}
      {isAdmin && <ConsignmentPolicyAlerts />}
      {isAdmin && <WeeklySecurityDigest />}
      {isAdmin && <ChargeEditsWeekCard />}
      {isAdmin && <DeductionApprovalNudge />}
      {isAdmin && <StaffCodeRejectionNudge />}
      {isAdmin && <EwayExpiryNudge />}
      {isAdmin && <PendingNotesNudge />}
      {isAdmin && <PromisedDateNudge />}
      {isAdmin && <BouncedSalaryNudge />}
      {isAdmin && <HireDueNudge />}
      {isAdmin && <SlowPodVendorCard />}
      {isAdmin && <ChargesTrendCard />}
      {isAdmin && <MonthlyVendorReviewCard />}
      {isAdmin && <MonthlyExpiryDigestCard />}
      {isAdmin && <TopDriverCard />}
      {isAdmin && <AssetRepairNudge />}
      {isAdmin && <PendingLogNudge />}
      {isAdmin && <CardRenewalNudge />}
      {isAdmin && <RateExpiryNudge />}
      {isAdmin && <RateChangeNudge />}
      {isAdmin && <CbsWhatsAppCard />}
      <FestivalReminder />

      {isAdmin && <ChangeRequestsCard />}
      {isAdmin && <ClaimSummaryCard />}
      {isAdmin && <VendorNudges />}
      {isAdmin && <OverdueSection />}

      {isAdmin && <CollectionSection />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={IndianRupee}
          label="Total Revenue"
          value={inr(stats.total_revenue)}
          accent="bg-blue-50 text-blue-600"
          testid="kpi-revenue"
        />
        <KpiCard
          icon={Wallet}
          label="Total Expenses"
          value={inr(stats.total_expenses)}
          accent="bg-amber-50 text-amber-600"
          testid="kpi-expenses"
        />
        <KpiCard
          icon={profitPositive ? TrendingUp : TrendingDown}
          label="Net Profit"
          value={inr(stats.net_profit)}
          accent={profitPositive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}
          testid="kpi-profit"
        />
        <KpiCard
          icon={Package}
          label="Active Shipments"
          value={stats.active_shipments}
          accent="bg-indigo-50 text-indigo-600"
          testid="kpi-active-shipments"
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Outstanding</div>
          <div className="mt-1 font-display text-lg font-bold text-amber-600">{inr(stats.outstanding)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Collected</div>
          <div className="mt-1 font-display text-lg font-bold text-emerald-600">{inr(stats.paid_revenue)}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Total Shipments</div>
          <div className="mt-1 font-display text-lg font-bold">{stats.total_shipments}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            <FileText className="h-3 w-3" /> Invoices
          </div>
          <div className="mt-1 font-display text-lg font-bold">{stats.total_invoices}</div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-border lg:col-span-2">
          <CardContent className="p-6">
            <h3 className="font-display text-lg font-bold">Revenue vs Expenses</h3>
            <p className="mb-4 text-xs text-muted-foreground">Last 6 months</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthly_series} barGap={6}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#64748B" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`}
                  />
                  <Tooltip
                    formatter={(v) => inr(v)}
                    contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#2563EB" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Expense" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-display text-lg font-bold">Expense Breakdown</h3>
            <p className="mb-4 text-xs text-muted-foreground">By category</p>
            <div className="h-72">
              {stats.expense_by_category.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  No expenses recorded yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.expense_by_category}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="45%"
                      outerRadius={80}
                      innerRadius={45}
                      paddingAngle={2}
                    >
                      {stats.expense_by_category.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="font-display text-lg font-bold">Monthly Bookings & Revenue Trend</h3>
            <p className="mb-4 text-xs text-muted-foreground">Consignments booked per month (last 6 months)</p>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={bookingTrend}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} formatter={(v, n) => (n === "Revenue" ? inr(v) : v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="left" type="monotone" dataKey="bookings" name="Bookings" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {isAdmin && <BranchSnapshot />}
      {isAdmin && <BranchRankCard />}
      {isAdmin && <MarketHireTrendCard />}
      {isAdmin && <PayrollSummaryCard />}
      {isAdmin && <MarketMarginTrendCard />}

      {isAdmin && (() => {
        const metric = METRICS.find((m) => m.key === branchMetric) || METRICS[0];
        const fmtVal = (v) => (metric.money ? inr(v) : v);
        const rowTotal = (row) => OFFICES.reduce((s, o) => s + (row[`${o}_${metric.key}`] || 0), 0);
        const colTotal = (o) => branchTrend.reduce((s, row) => s + (row[`${o}_${metric.key}`] || 0), 0);
        const grand = branchTrend.reduce((s, row) => s + rowTotal(row), 0);
        return (
        <div className="mt-6">
          <Card className="border-border" data-testid="dashboard-branch-comparison">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold">Monthly Branch Comparison</h3>
                  <p className="mb-4 text-xs text-muted-foreground">
                    {metric.label} per branch — last 6 months (all branches)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex overflow-hidden rounded-md border border-border">
                    {METRICS.map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        data-testid={`branch-metric-${m.key}`}
                        onClick={() => setBranchMetric(m.key)}
                        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${branchMetric === m.key ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-slate-50"}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => printBranchSummary(branchTrend, { metricKey: metric.key, metricLabel: metric.label, money: metric.money, offices: OFFICES })} data-testid="branch-summary-print-btn">
                    <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
                  </Button>
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={branchTrend} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#64748B" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      tickFormatter={(v) => (metric.money ? `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}` : v)}
                    />
                    <Tooltip
                      formatter={(v) => (metric.money ? inr(v) : v)}
                      contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {OFFICES.map((o, i) => (
                      <Bar key={o} dataKey={`${o}_${branchMetric}`} name={o} fill={BRANCH_COLORS[i % BRANCH_COLORS.length]} radius={[4, 4, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-5 overflow-x-auto" data-testid="branch-summary-table">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                      <th className="border border-border px-3 py-2 text-left">Month</th>
                      {OFFICES.map((o) => (
                        <th key={o} className="border border-border px-3 py-2 text-right">{o}</th>
                      ))}
                      <th className="border border-border px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branchTrend.map((row) => (
                      <tr key={row.key}>
                        <td className="border border-border px-3 py-2 font-medium">{row.label}</td>
                        {OFFICES.map((o) => (
                          <td key={o} className="border border-border px-3 py-2 text-right tabular-nums">{fmtVal(row[`${o}_${metric.key}`] || 0)}</td>
                        ))}
                        <td className="border border-border px-3 py-2 text-right font-semibold tabular-nums">{fmtVal(rowTotal(row))}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-100 font-bold">
                      <td className="border border-border px-3 py-2">TOTAL</td>
                      {OFFICES.map((o) => (
                        <td key={o} className="border border-border px-3 py-2 text-right tabular-nums">{fmtVal(colTotal(o))}</td>
                      ))}
                      <td className="border border-border px-3 py-2 text-right tabular-nums text-primary">{fmtVal(grand)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
        );
      })()}
    </div>
  );
}
